import { Element as SVGElement, G as SVGG, Rect as SVGRect, Circle as SVGCircle, Polygon as SVGPolygon, Path as SVGPath, StrokeData, Svg, Symbol as SVGSymbol, FillData, Gradient as SVGGradient, Box as SVGBox, Text as SVGText } from "@svgdotjs/svg.js";
import { Grid } from "honeycomb-grid";
import type { Hex } from "honeycomb-grid";
import { GridPoints, IPoint, type Poly, IPolyPolygon, resolveSquareBoardPoint, type SquarePoint, isTileCornerPoint, expandSquareGrid } from "../grids/index.js";
import { AnnotationBasic, AnnotationSowing, APRenderRep, AreaButtonBar, AreaCompassRose, AreaKey, AreaPieces, AreaReserves, AreaScrollBar, AreaTrack, AreaVolcanoStash, BoardReference, ButtonBarButton, Colourfuncs, ColourResolvable, Glyph, Gradient, MarkerFence, MarkerFences, PatternName, type Polymatrix } from "../schemas/schema.js";
import {
    buildLocalStashRows,
    LOCAL_STASH_PIECE_BASE_Y_FRAC,
    LOCAL_STASH_STACK_LAYER_OFFSET,
    localStashColumnProfile,
    localStashRowMaxSteps,
    localStashRowStackSpanPx,
    localStashSilhouettePieceCenterY,
    localStashSilhouetteRowSharedBaseY,
    localStashSilhouetteTier,
    localStashStepsAboveBottom,
    localStashVerticalStepPx,
} from "../common/localStashArea.js";
import { sheets } from "../sheets/index.js";
import { applyCellInsetToMovePoints, shouldApplyMoveCellInset } from "../common/annotationInset.js";
import { projectPoint, scale, rotate, usePieceAt, calcPyramidOffset, calcLazoOffset, projectPointEllipse, rotatePoint, calcBearing, smallestDegreeDiff, shortenLine, roundPolygon } from "../common/plotting.js";
import { areaTitleBand, measureAreaTitle, placeAreaTitleInBand, placeTextInkTop } from "../common/areaLabelText.js";
import { dominoClickPayload, composeDominoTile, buildPiecesAreaRows, isDominoTileRef, piecesAreaCaption, piecesAreaCaptionTextYFromSlotTop, piecesAreaDominoTileTopFromSlotTop, piecesAreaHorizontalGap, piecesAreaLegendKey, piecesAreaPieceCenterYFromSlotTop, piecesAreaSlotHeight, piecesAreaSlotWidth, piecesAreaVerticalGap, shouldRotateAreaPieces } from "../common/dominoHand.js";
import { glyph2uid, x2uid } from "../common/glyph2uid.js";
import {
    applyFillSlotFillChannelOpacity,
    applySlotDefaults,
    applySlotPaint,
    applyTextGlyphFill,
    collectPatternsFromGlyphPaint,
    isCustomGlyph,
    normalizeGlyphPaint,
    normalizeTextGlyphPaint,
    TEXT_GLYPH_BASE_FONT_SIZE,
    paintFingerprint,
    paintColourValue,
    paintMapAfterProceduralShading,
    paintSlotOpacity,
    playerIndexFromSheetGlyph,
    resolvedPaintBaseHex,
    symbolHasSlotBindings,
    type GlyphPaintApplier,
    type TextGlyphFillContext,
} from "../glyphs/paint.js";
import {
    knownPaintSlotNames,
    proceduralShadingProfile,
    resolveColour2SlotForGlyph,
} from "../sheets/registry/glyphRegistry.js";
import { getGlyphDefinitionMeta } from "../sheets/registry/defineGlyph.js";
import { inferSlotsFromSymbol } from "../sheets/registry/inferSlots.js";
import { buildCustomGlyphSymbol } from "../glyphs/loadCustomGlyphSymbol.js";
import { applyProceduralShadedPaint, isOrbShadingProfile } from "../glyphs/procedural/orbShading.js";
import { applyProceduralMarbledPaint, isMarbledShadingProfile } from "../glyphs/procedural/marbledShading.js";
import tinycolor from "tinycolor2";
import { unionPolys } from "../common/polys.js";
import { hex2rgb, rgb2hex, afterOpacity, lighten } from "../common/colours.js";
import { labelDisplayText } from "../common/renderLabel.js";
import {
    assertButtonBarButton,
    buttonBarClickValue,
    buttonBarGlyphAllowance,
    buttonBarGlyphCenterX,
    buttonBarGlyphPosition,
    buttonBarLabelCenterX,
    buttonBarTotalWidth,
    placeButtonBarGlyph,
} from "../common/buttonBar.js";
import { attachMarkerPulse } from "../common/markerPulse.js";
import { haloIsFullCircle, resolveHaloSegmentSpans, HaloSegmentDegreesError } from "../markers/haloSpans.js";
import { CompassDirection, edges2corners, getBoardFill, BoardReturn } from "../boards/index.js";
import { cairoCatalan, cairoCollinear, cobweb, conhex, conicalHex, dvgc, eleven, fracturedFlat, hexOfCir, hexOfHex, hexOfTri, hexOfTriF, hexSlanted, moon, onyx, bricka, pentagonal, bentTri, star, pyramidHex, rectOfHex, rectOfTri, snubSquare, snubSquareCells, sowing, squares, squaresDiamonds, squaresStacked, stackingTriangles, vertex, wheel } from "../boards/index.js";
import {
    glyphForLayoutRotation,
    glyphKeepsUpright,
    isoFaceGlyphDrawSize,
    isoFaceGlyphPlacement,
    resolveGlyphFlipAxes,
    resolveGlyphRotationDegrees,
} from "./isometric/faceGlyphFit.js";
import {
    applyGlyphLocalNudge,
    applyPieceGlyphNudge,
    resolveGlyphNudgeRelativeTo,
    rotatePieceNudgeVector,
} from "../glyphs/nudge.js";
import {
    computeAnnulusPlacement,
    computeSidebarPlacement,
    computePlayfieldMetrics,
    playfieldExtentFromPolys,
    defaultAnnulusAnchor,
    defaultSidebarAnchor,
    ensureTableau,
    getRegistryReferenceArt,
    referenceInnerMarkup,
    referenceSourceString,
    referenceStyleSelectors,
    registryReferenceDefId,
    resolveReferenceSides,
    resolveSourceForSide,
    sidebarLayoutMetricsInParent,
    unionBoardReferenceBox,
    type ReferenceSide,
} from "../references/helpers.js";
// import { customAlphabet } from 'nanoid'
// const nanoid = customAlphabet('abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789', 10);

/**
 * This interface provides the renderer with preferences for specific element colouring.
 * The intention is to make things like hosting SVGs in "dark mode" easier to accomplish.
 */
export interface IColourContext {
    background: string;
    strokes: string;
    borders: string;
    labels: string;
    annotations: string;
    fill: string;
    board?: string;
}
// "background"|"strokes"|"labels"|"annotations"|"fill"|"board"

/**
 * Defines the options recognized by the rendering engine.
 * @beta
 */
export interface IRendererOptionsIn {
    /**
     * A list of glyph sheets to include.
     * Defaults to the full list, starting with `core`.
     *
     */
    sheets?: string[];
    /**
     * Let's you set desired default colours so the image more immediately fits
     * into a specific context, like dark mode.
     * The type is a generic string-to-string object, but the precheck function
     * turns it into an IColourContext object.
     */
    colourContext?: {[k: string]: string};
    /**
     * I want to know whether the custom context being passed is a global preference or
     * a game-specific one. The idea being that the colourFunc `custom` should prefer
     * the developer defaults over global customizations, but defer to game-specific ones.
     */
    contextGlobal: boolean;
    /**
     * Per-player palette entries: hex colour strings or pattern names (`dots`, `chevrons`, etc.).
     * Use `null` to keep the default for that slot (hex from the built-in palette, or a pattern
     * from `patternList` when `patterns` is true).
     *
     */
    colours?: (string|null)[];
    /**
     * I want to know whether the custom colours being passed are a global preference or
     * a game-specific one. The idea being that the colourFunc `custom` should prefer
     * the developer defaults over global customizations, but defer to game-specific ones.
     */
    coloursGlobal: boolean;
    /**
     * When true, default player slots use `patternList` instead of the built-in hex palette.
     * Does not prevent mixing hex colours and patterns in a custom `colours` array.
     *
     */
    patterns?: boolean;
    /**
     * Pattern IDs used for default player slots when `patterns` is true, and for `null`
     * entries in a custom `colours` array while `patterns` is true.
     *
     */
    patternList?: string[];
    /**
     * Signals whether you want to use the built-in set of four colour-blind-friendly colours.
     *
     */
    colourBlind?: boolean;
    /**
     * Indicates the number of degrees by which you want the entire render rotated.
     * Defaults to 0. The default renderer only supports rotation by 180 degrees. Other renderers may ignore it entirely.
     *
     */
    rotate?: number;
    /**
     * Signals whether you want move annotations shown.
     *
     */
    showAnnotations?: boolean;
    /**
     * When false, move annotations use cell centers (legacy). When true or omitted on supported boards, move arrows inset to mid-edge along travel.
     */
    moveAnnotationCellInset?: boolean;
    /**
     * A string of unique characters that will be used to create column labels. Defaults to the lowercase English alphabet.
     */
    columnLabels?: string;
    /**
     * A list of tuples indicating "requested glyph" and "replacement glyph."
     * This is a way of a user swapping out one glyph for another at render time.
     *
     */
    glyphmap?: ([string,string]|[string,string,number])[];
    /**
     * A callback attached to boards and pieces that is called whenever the user clicks on them.
     *
     * @param row - The row number that was clicked on
     * @param col - The column number that was clicked on
     * @param piece - A string representation of the piece that was clicked on, if any
     */
    boardClick?: (row: number, col: number, piece: string) => void;
    /**
     * A callback attached to the entire drawing that is called as the user moves their mouse over it.
     *
     * @param row - The row number that was clicked on
     * @param col - The column number that was clicked on
     * @param piece - A string representation of the piece that was clicked on, if any
     */
    boardHover?: (row: number, col: number, piece: string) => void;
    /**
     * When true, emit serialized-safe animations (e.g. CSS keyframes for marker pulse).
     */
    staticAnimations?: boolean;
    /**
     * When `live`, incompatible board markers are skipped instead of throwing.
     */
    sanitizeMode?: "live" | "strict";
}

/**
 * Defines the options used by the renderer after all prechecks and validation.
 *
 */
export interface IRendererOptionsOut {
    sheets: string[];
    colourContext: IColourContext;
    contextGlobal: boolean;
    colours: string[];
    coloursGlobal: boolean;
    playerPalette: PlayerFill[];
    useDefaults: number[];
    patterns: boolean;
    patternList: string[];
    colourBlind: boolean;
    rotate: number;
    columnLabels: string;
    showAnnotations: boolean;
    moveAnnotationCellInset: boolean;
    glyphmap: [string,string,number][];
    boardClick?: (row: number, col: number, piece: string) => void;
    boardHover?: (row: number, col: number, piece: string) => void;
    staticAnimations: boolean;
    sanitizeMode: "live" | "strict";
}

export const paletteDefault = ["#e31a1c", "#1f78b4", "#33a02c", "#ffff99", "#6a3d9a", "#ff7f00", "#b15928", "#fb9a99", "#a6cee3", "#b2df8a", "#fdbf6f", "#cab2d6"];
export const paletteBlind = ["#9f0162", "#8400cd", "#a40122", "#009f81", "#008df9", "#e20134", "#ff5aaf", "#00c2f9", "#ff6e3a", "#00fccf", "#ffb2fd", "#ffc33b"];

export const PATTERN_NAMES = [
    "microbial",
    "chevrons",
    "honeycomb",
    "triangles",
    "wavy",
    "slant",
    "dots",
    "starsWhite",
    "cross",
    "houndstooth",
] as const;

export function isPatternName(s: string): s is PatternName {
    return (PATTERN_NAMES as readonly string[]).includes(s);
}

export type PlayerFill =
    | { kind: "colour"; value: string }
    | { kind: "pattern"; value: PatternName };

export type ResolvedFill = string | SVGGradient | SVGElement;

type ColourOperand = { kind: "colour"; value: string } | { kind: "pattern"; element: SVGElement };

export interface IMarkBoardOptions {
    svgGroup: SVGG;
    preGridLines: boolean;
    grid: GridPoints;
    gridExpanded?: GridPoints;
    hexGrid?: Grid<Hex>;
    hexWidth?: number;
    hexHeight?: number;
    polys?: Poly[][];
    boardEdges?: Map<CompassDirection, IPolyPolygon>;
    cairoStart?: "H"|"V";
}

interface ISegment {
    colour: string|number;
    opacity?: number;
    style?: "solid"|"dashed";
}

/**
 * Internal interface when placing markers and annotations
 *
 */
interface ITarget {
    row: number;
    col: number;
}

/**
 * An infinite generator for creating column labels from an initial string of characters.
 * With the English alphabet, you would get a-z, then aa-az-ba-zz, then aaa etc.
 *
 * @param labels - A string of characters to use as column labels
 * @returns The next label in the sequence.
 */
export function* generateColumnLabel(labels: string): IterableIterator<string> {
    let n = 0
    let len = 1;
    const chars = labels.split("");
    while (true) {
        let label = "";
        let mask = n.toString(chars.length);
        while (mask.length < len) {
            mask = "0" + mask;
        }
        for (const char of mask) {
            const val = parseInt(char, chars.length);
            label += chars[val];
        }
        yield label;
        n++;
        const threshold = Math.pow(chars.length, len);
        if (n === threshold) {
            n = 0;
            len++;
        }
    }
}

/**
 * The abstract base class from which all renderers inherit. Contains all the code shared by most renderers.
 *
 */
export abstract class RendererBase {
    /**
     * Every renderer must have a unique name, referenced by the `renderer` property of the schema.
     *
     */
    public static readonly rendererName: string;
    /**
     * The default cell size. It's simply a convenient constant. It has no bearing at all on the final output.
     *
     */
    public cellsize = 50;
    /**
     * The list of received, processed, and validated options. This is available to all class methods.
     *
     */
    public options: IRendererOptionsOut
    public json?: APRenderRep;
    public rootSvg?: Svg;
    /** Main-board polygon grid from the last top-level `dispatchBoardRender` call. */
    protected mainBoardPolys?: Poly[][];
    /** Don't rebuild the same glyphs */
    private readonly glyphCache = new Map<string, SVGSymbol>();

    /**
     * Creates an instance of RendererBase. A name must be provided. Also sets the default options.
     * @param name - The unique name of the renderer
     */
    constructor() {
        this.options = {
            sheets: ["core", "dice", "dominoes", "looney", "piecepack", "chess", "streetcar", "nato", "decktet", "arimaa", "gnostica", "experimental"],
            colourContext: {
                background: "#fff",
                fill: "#000",
                strokes: "#000",
                borders: "#000",
                annotations: "#000",
                labels: "#000",
                board: "#fff",
            },
            contextGlobal: true,
            colourBlind: false,
            colours: [],
            coloursGlobal: true,
            playerPalette: paletteDefault.map(value => ({ kind: "colour" as const, value })),
            useDefaults: [],
            patterns: false,
            patternList: [...PATTERN_NAMES],
            showAnnotations: true,
            moveAnnotationCellInset: true,
            columnLabels: "abcdefghijklmnopqrstuvwxyz",
            rotate: 0,
            glyphmap: [],
            staticAnimations: false,
            sanitizeMode: "strict",
        };
    }

    /**
     * This is the entry function for creating the rendered image.
     *
     * @param json - The parsed JSON to render
     * @param draw - The canvas upon which to render the image
     * @param opts - The renderer options
     */
    public abstract render(json: APRenderRep, draw: Svg, opts: IRendererOptionsIn): void;

    /**
     * Run on all JSON received before it is processed.
     *
     * @param json - The parsed JSON to render
     * @returns A fully validated {@link APRenderRep} object
     */
    protected jsonPrechecks(json: APRenderRep): void {
        // Check for missing renderer
        if (json.renderer === undefined) {
            json.renderer = "default";
        }

        // Make sure the JSON is intended for you
        if (json.renderer !== (this.constructor as typeof RendererBase).rendererName) {
            throw new Error(`Renderer mismatch. The JSON data you provided is intended for the "${json.renderer}" renderer, but the "${(this.constructor as typeof RendererBase).rendererName}" renderer received it.`);
        }

        this.json = json;
    }

    /**
     * Processes received options ({@link IRendererOptionsIn}) and translates them into valid {@link IRendererOptionsOut} options.
     *
     * @param opts - Renderer options passed by the user
     */
    protected optionsPrecheck(opts: IRendererOptionsIn): void {
        this.glyphCache.clear();

        // Check colour blindness
        if (opts.colourBlind !== undefined) {
            this.options.colourBlind = opts.colourBlind;
        } else {
            this.options.colourBlind = false;
        }
        if (this.options.colourBlind) {
            this.options.colours = [...paletteBlind];
        } else {
            this.options.colours = [...paletteDefault];
        }

        // Validate sheet list
        if ( (opts.sheets !== undefined) && (opts.sheets.length > 0) ) {
            for (const name of opts.sheets) {
                if (! sheets.has(name)) {
                    throw new Error(`A glyph sheet you requested could not be found: ${ name }`);
                }
            }
            this.options.sheets = opts.sheets;
        }

        // Validate patterns settings
        this.options.patterns = false;
        if (opts.patterns) {
            this.options.patterns = true;
            if ( (opts.patternList !== undefined) && (opts.patternList.length > 0) ) {
                for (const name of opts.patternList) {
                    if (! isPatternName(name)) {
                        throw new Error(`A pattern you requested could not be found: ${ name }`);
                    }
                }
                this.options.patternList = opts.patternList;
            }
        }

        // Validate colour context
        if (opts.colourContext !== undefined && opts.colourContext !== null) {
            for (const label of ["strokes", "labels", "annotations", "fill", "background", "borders", "board"] as const) {
                if ( (label in opts.colourContext) && (opts.colourContext[label] !== undefined) ) {
                    const color = tinycolor(opts.colourContext[label]);
                    if (! color.isValid()) {
                        throw new Error(`The context colour for '${label}' is malformed: ${ opts.colourContext[label] }`);
                    }
                    this.options.colourContext[label] = color.toHexString();
                } else if (label === "board" && opts.colourContext[label] === undefined) {
                    this.options.colourContext.board = undefined;
                }
            }
            this.options.contextGlobal = opts.contextGlobal ?? true;
        }

        // Build per-player palette (hex and/or patterns)
        this.buildPlayerPalette(opts);

        // Check for annotation screening
        if (opts.showAnnotations !== undefined) {
            this.options.showAnnotations = opts.showAnnotations;
        }
        if (opts.moveAnnotationCellInset !== undefined) {
            this.options.moveAnnotationCellInset = opts.moveAnnotationCellInset;
        }

        // Check for different column labels
        if (opts.columnLabels !== undefined) {
            this.options.columnLabels = opts.columnLabels;
        }

        // Validate rotation
        if ( (opts.rotate !== undefined) && (opts.rotate !== 0) ) {
            let normalized = opts.rotate;
            if (typeof normalized === "string") {
                normalized = parseInt(normalized, 10);
            }
            while (normalized < 0) {
                normalized += 360;
            }
            while (normalized > 0) {
                normalized -= 360;
            }
            this.options.rotate = normalized;
        } else {
            this.options.rotate = 0;
        }

        // move any glyphmap option over
        if (opts.glyphmap !== undefined) {
            this.options.glyphmap = [];
            for (const [from, to, scale] of opts.glyphmap) {
                this.options.glyphmap.push([from, to, scale ?? 1]);
            }
        }

        if (opts.boardClick !== undefined) {
            this.options.boardClick = opts.boardClick;
        }
        if (opts.boardHover !== undefined) {
            this.options.boardHover = opts.boardHover;
        }
        this.options.staticAnimations = opts.staticAnimations ?? false;
        this.options.sanitizeMode = opts.sanitizeMode ?? "strict";
    }

    protected buildPlayerPalette(opts: IRendererOptionsIn): void {
        const hexPalette = this.options.colourBlind ? paletteBlind : paletteDefault;
        const patternList = this.options.patternList;
        const usePatternsDefault = this.options.patterns;

        const defaultSlot = (i: number): PlayerFill => {
            if (usePatternsDefault) {
                const name = patternList[i] ?? patternList[patternList.length - 1];
                return { kind: "pattern", value: name as PatternName };
            }
            return { kind: "colour", value: hexPalette[i] ?? hexPalette[hexPalette.length - 1] };
        };

        this.options.useDefaults = [];
        let playerPalette: PlayerFill[];

        if ( (opts.colours !== undefined) && (opts.colours.length > 0) ) {
            playerPalette = [];
            for (let i = 0; i < opts.colours.length; i++) {
                const c = opts.colours[i];
                if (c === null) {
                    this.options.useDefaults.push(i + 1);
                    playerPalette.push(defaultSlot(i));
                } else if (isPatternName(c)) {
                    playerPalette.push({ kind: "pattern", value: c });
                } else {
                    const color = tinycolor(c);
                    if (! color.isValid()) {
                        throw new Error(`One of the colours you requested is malformed: ${ c }`);
                    }
                    playerPalette.push({ kind: "colour", value: color.toHexString() });
                }
            }
            this.options.coloursGlobal = opts.coloursGlobal ?? true;
        } else if (usePatternsDefault) {
            playerPalette = patternList.map(name => ({ kind: "pattern" as const, value: name as PatternName }));
        } else {
            playerPalette = hexPalette.map(value => ({ kind: "colour" as const, value }));
        }

        this.options.playerPalette = playerPalette;
        this.syncColoursFromPlayerPalette(hexPalette);
    }

    protected syncColoursFromPlayerPalette(hexPalette: string[]): void {
        this.options.colours = this.options.playerPalette.map((fill, i) => (
            fill.kind === "colour"
                ? fill.value
                : (hexPalette[i] ?? hexPalette[hexPalette.length - 1])
        ));
    }

    protected isPatternSVGElement(fill: ResolvedFill): fill is SVGElement {
        return typeof fill === "object" && "type" in fill && (fill as SVGElement).type === "pattern";
    }

    protected coerceFillToHex(fill: ResolvedFill, def?: string): string {
        if (typeof fill === "string") {
            return fill;
        }
        if (this.isPatternSVGElement(fill)) {
            return this.options.colourContext.strokes;
        }
        return def ?? this.options.colourContext.strokes;
    }

    protected scaledPatternId(name: string, useSize: number): string {
        const normalized = Number(useSize);
        const scaleKey = normalized.toString().replace(/[^a-zA-Z0-9_-]/g, "_");
        return `${name}-${scaleKey}`;
    }

    protected getScaledPattern(name: string, useSize: number): SVGElement {
        if (this.rootSvg === undefined) {
            throw new Error("Object in an invalid state!");
        }
        this.loadPattern(name);
        const patternId = this.scaledPatternId(name, useSize);
        let fill = this.rootSvg.findOne("#" + patternId) as SVGElement;
        if (fill === null) {
            fill = this.rootSvg.findOne("#" + name) as SVGElement;
            fill = fill.clone().id(patternId).scale(useSize / 150);
            this.rootSvg.defs().add(fill);
        }
        return fill;
    }

    protected collectPatternsFromColourValue(val: number | string | Gradient | Colourfuncs, names: Set<string>): void {
        if (typeof val === "number") {
            const slot = this.options.playerPalette[val - 1];
            if (slot?.kind === "pattern") {
                names.add(slot.value);
            }
            return;
        }
        if (typeof val === "string") {
            if (isPatternName(val)) {
                names.add(val);
            }
            return;
        }
        if ("stops" in val) {
            for (const stop of val.stops) {
                this.collectPatternsFromColourValue(stop.colour, names);
            }
            return;
        }
        if ("func" in val) {
            if (val.func === "flatten") {
                this.collectPatternsFromColourValue(val.fg, names);
                this.collectPatternsFromColourValue(val.bg, names);
            } else if (val.func === "lighten") {
                this.collectPatternsFromColourValue(val.colour, names);
            } else if (val.func === "bestContrast") {
                this.collectPatternsFromColourValue(val.bg, names);
                for (const c of val.fg) {
                    this.collectPatternsFromColourValue(c, names);
                }
            } else if (val.func === "custom") {
                this.collectPatternsFromColourValue(val.default, names);
                if (typeof val.palette === "number" || typeof val.palette === "string" || typeof val.palette === "object") {
                    this.collectPatternsFromColourValue(val.palette as number | string | Colourfuncs, names);
                }
            }
        }
    }

    protected preloadPatternsFromColourValues(values: Array<number | string | Gradient | Colourfuncs | undefined>): void {
        const names = new Set<string>();
        for (const val of values) {
            if (val !== undefined) {
                this.collectPatternsFromColourValue(val, names);
            }
        }
        for (const name of names) {
            this.loadPattern(name);
        }
    }

    protected applyPlayerFillTargets(got: SVGSymbol, suffix: string, fill: ResolvedFill, opacity: number): void {
        const fillAttr = `data-playerfill${suffix}`;
        const strokeAttr = `data-playerstroke${suffix}`;
        if (this.isPatternSVGElement(fill)) {
            got.find(`[${fillAttr}=true]`).each(function(this: SVGElement) {
                this.fill(fill);
                if (opacity < 1) {
                    this.opacity(opacity);
                }
            });
            return;
        }
        if (typeof fill === "object") {
            got.find(`[${fillAttr}=true]`).each(function(this: SVGElement) { this.fill(fill); });
            return;
        }
        got.find(`[${fillAttr}=true]`).each(function(this: SVGElement) { this.fill({color: fill, opacity}); });
        got.find(`[${strokeAttr}=true]`).each(function(this: SVGElement) { this.stroke({color: fill, opacity}); });
    }

    /**
     * Applies theme context fills/strokes to reference artwork data-context-* slots.
     */
    protected applyReferenceContextStyles(root: Svg | SVGG | SVGSymbol): void {
        const contextFill = this.options.colourContext.fill;
        const contextStroke = this.options.colourContext.strokes;
        const contextBackground = this.options.colourContext.background;
        root.find("[data-context-fill=true]").each(function (this: SVGElement) {
            this.fill(contextFill);
        });
        root.find("[data-context-background=true]").each(function (this: SVGElement) {
            this.fill(contextBackground);
        });
        root.find("[data-context-stroke=true]").each(function (this: SVGElement) {
            this.stroke(contextStroke);
        });
    }

    protected applyContextStylesToGlyphSymbol(got: SVGSymbol): void {
        const contextStroke = this.options.colourContext.strokes;
        const contextFill = this.options.colourContext.fill;
        const contextBorder = this.options.colourContext.borders;
        const contextBackground = this.options.colourContext.background;
        const contextBoard = this.options.colourContext.board;
        /** Sheet-encoded `data-context-*` defaults apply even on slotted art; slot paint overwrites after. */
        got.find("[data-context-fill=true]").each(function (this: SVGElement) {
            this.fill(contextFill);
        });
        got.find("[data-context-background=true]").each(function (this: SVGElement) {
            this.fill(contextBackground);
        });
        got.find("[data-context-stroke=true]").each(function (this: SVGElement) {
            this.stroke(contextStroke);
        });
        got.find("[data-context-border=true]").each(function (this: SVGElement) {
            this.stroke(contextBorder);
        });
        got.find("[data-context-border-fill=true]").each(function (this: SVGElement) {
            this.fill(contextBorder);
        });
        if (contextBoard !== undefined) {
            got.find("[data-context-board=true]").each(function (this: SVGElement) {
                this.fill(contextBoard);
            });
        }
    }

    /**
     * Colours a named slot (`data-ref-fill` / `data-ref-stroke`) on board reference artwork.
     */
    protected applyReferenceSlotStyle(root: Svg | SVGG | SVGSymbol, slotId: string, colourVal: ColourResolvable): void {
        const fill = this.resolveFill(colourVal as number | string | Gradient | Colourfuncs, "#000");
        const { fill: fillSelector, stroke: strokeSelector } = referenceStyleSelectors(slotId);
        if (this.isPatternSVGElement(fill)) {
            root.find(fillSelector).each(function (this: SVGElement) {
                this.fill(fill);
            });
            root.find(strokeSelector).each(function (this: SVGElement) {
                // @ts-expect-error (poor SVGjs typing)
                this.stroke(fill);
            });
            return;
        }
        if (typeof fill === "object") {
            root.find(fillSelector).each(function (this: SVGElement) {
                this.fill(fill);
            });
            return;
        }
        root.find(fillSelector).each(function (this: SVGElement) {
            this.fill({ color: fill, opacity: 1 });
        });
        root.find(strokeSelector).each(function (this: SVGElement) {
            this.stroke({ color: fill, opacity: 1 });
        });
    }

    protected resolveMarkerFill(val: number | string | Gradient | Colourfuncs, opacity: number, def?: string): FillData | SVGGradient | SVGElement {
        const fill = this.resolveFill(val, def);
        if (this.isPatternSVGElement(fill)) {
            return fill;
        }
        if (typeof fill === "object") {
            return fill;
        }
        return { color: fill, opacity };
    }

    protected patternDefId(name: string, opts?: { fg?: string; bg?: string }): string {
        if (opts?.fg === undefined && opts?.bg === undefined) {
            return name;
        }
        const fg = opts?.fg ?? this.options.colourContext.strokes;
        const bg = opts?.bg ?? this.options.colourContext.background;
        const sanitize = (value: string): string => value.replace(/[^a-zA-Z0-9_-]/g, "_");
        return `${name}--fg-${sanitize(fg)}--bg-${sanitize(bg)}`;
    }

    /**
     * Loads any requested patterns into the final SVG.
     *
     * @param name - The unique name of the pattern
     * @param canvas - The container into which to add the pattern
     * @returns The defs id of the pattern element (base name or colour variant)
     */
    public loadPattern(name: string, opts: {canvas?: Svg, fg?: string, bg?: string} = {}): string {
        let canvas: Svg|undefined = this.rootSvg;
        if (opts.canvas !== undefined) {
            canvas = opts.canvas;
        }
        if (canvas === undefined) {
            throw new Error("Object in an invalid state.");
        }

        let fg = this.options.colourContext.strokes;
        if (opts.fg !== undefined) {
            fg = opts.fg;
        }
        let bg = this.options.colourContext.background;
        if (opts.bg !== undefined) {
            bg = opts.bg;
        }

        const patternId = this.patternDefId(name, opts);
        if (canvas.findOne("#" + patternId) !== null) {
            return patternId;
        }

        // Keep in alphabetical order.
        // If you change any `id`s, you need to change them in the constructor, too.
        switch (name) {
            case "chevrons":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="30" height="15" viewbox="0 0 60 30"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="60" height="30"><defs><rect id="_r" width="30" height="15" fill="${bg}" stroke-width="2.5" stroke="${fg}"/><g id="_p"><use xlink:href="#_r"/><use y="15" xlink:href="#_r"/><use y="30" xlink:href="#_r"/><use y="45" xlink:href="#_r"/></g></defs><use xlink:href="#_p" transform="translate(0 -25) skewY(40)"/><use xlink:href="#_p" transform="translate(30 0) skewY(-40)"/></svg></pattern>`);
                break;
            case "cross":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="8" height="8"><svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><rect width="8" height="8" fill="${bg}"/><path d="M0 0L8 8ZM8 0L0 8Z" stroke-width="0.5" stroke="${fg}"/></svg></pattern>`);
                break;
            case "dots":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="10" height="10"><svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"><rect width="10" height="10" fill="${bg}" /><circle cx="2.5" cy="2.5" r="2.5" fill="${fg}"/></svg></pattern>`);
                break;
            case "honeycomb":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="22.4" height="40" viewbox="0 0 56 100"><svg xmlns="http://www.w3.org/2000/svg" width="56" height="100"><rect width="56" height="100" fill="${bg}"/><path d="M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100" fill="none" stroke="${fg}" stroke-width="2"/><path d="M28 0L28 34L0 50L0 84L28 100L56 84L56 50L28 34" fill="none" stroke="${fg}" stroke-width="2"/></svg></pattern>`);
                break;
            case "houndstooth":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="24" height="24" viewbox="0 0 24 24"><svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>houndstooth</title><g fill="${fg}" fill-opacity="1" fill-rule="evenodd"><path d="M0 18h6l6-6v6h6l-6 6H0M24 18v6h-6M24 0l-6 6h-6l6-6M12 0v6L0 18v-6l6-6H0V0"/></g></svg></pattern>`);
                break;
            case "microbial":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="20" height="20"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><rect width="40" height="40" fill="${bg}"/><circle r="9.2" stroke-width="1" stroke="${fg}" fill="none"/><circle cy="18.4" r="9.2" stroke-width="1px" stroke="${fg}" fill="none"/><circle cx="18.4" cy="18.4" r="9.2" stroke-width="1" stroke="${fg}" fill="none"/></svg></pattern>`);
                break;
            case "slant":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="10" height="10"><svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"><rect width="10" height="10" fill="${bg}"/><path d="M-1,1 l2,-2 M0,10 l10,-10 M9,11 l2,-2" stroke="${fg}" stroke-width="1"/></svg></pattern>`);
                break;
            case "starsWhite":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="40" height="40" viewbox="0 0 80 80"><svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="${bg}"/><circle cx="40" cy="40" r="40" fill="${fg}"/><path d="M0 40 A40 40 45 0 0 40 0 A40 40 315 0 0 80 40 A40 40 45 0 0 40 80 A40 40 270 0 0 0 40Z" fill="${bg}"/></svg></pattern>`);
                break;
            case "triangles":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="15" height="15"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"><rect width="15" height="15" fill="${bg}"/><path d="M0 15L7.5 0L15 15Z" fill="${fg}"/></svg></pattern>`);
                break;
            case "wavy":
                canvas.defs().svg(`<pattern id="${patternId}" patternUnits="userSpaceOnUse" width="15" height="20" viewbox="0 0 75 100"><svg xmlns="http://www.w3.org/2000/svg" width="75" height="100"><rect width="75" height="100" fill="${bg}"/><circle cx="75" cy="50" r="28.3%" stroke-width="12" stroke="${fg}" fill="none"/><circle cx="0" r="28.3%" stroke-width="12" stroke="${fg}" fill="none"/><circle cy="100" r="28.3%" stroke-width="12" stroke="${fg}" fill="none"/></svg></pattern>`);
                break;
            default:
                throw new Error(`The pattern name you requested (${name}) is not known.`);
        }
        return patternId;
    }

    /**
     * Goes through the list of sheets until it finds a matching glyph and adds it to the given Svg.
     * This is where glyph replacement happens (via `this.options.glyphmap`).
     *
     * @param glyph - The name of the glyph to load
     * @param canvas - The canvas into which to load the glyph
     * @returns The {@link SVGSymbol} that was loaded
     */
    protected loadGlyph(glyph: string, player: (number | undefined), canvas?: Svg): SVGSymbol {
        if (canvas === undefined) {
            if (this.rootSvg === undefined) {
                throw new Error("Object in an invalid state!");
            }
            canvas = this.rootSvg;
        }
        // check for substituted glyphs
        if (this.options.glyphmap.length > 0) {
            const idx = this.options.glyphmap.findIndex(t => t[0] === glyph);
            if (idx >= 0) {
                glyph = this.options.glyphmap[idx][1];
            }
        }
        for (const s of this.options.sheets) {
            const sheet = sheets.get(s);
            if (sheet !== undefined) {
                const func = sheet.glyphs.get(glyph);
                if (func !== undefined) {
                    return (func as (svg: Svg) => SVGSymbol)(canvas.defs() as Svg);
                }
            } else {
                throw new Error("Could not load the glyph sheet '" + s + "'");
            }
        }
        throw new Error("The glyph '" + glyph + "' could not be found in the requested sheets: " + this.options.sheets.join(", "));
    }

    protected loadCustomGlyph(defId: string, canvas?: Svg): SVGSymbol {
        if (this.json === undefined) {
            throw new Error("Object in an invalid state!");
        }
        const entry = this.json.glyphDefs?.[defId];
        if (entry === undefined) {
            throw new Error(`Custom glyph definition "${defId}" was not found in glyphDefs.`);
        }
        if (canvas === undefined) {
            if (this.rootSvg === undefined) {
                throw new Error("Object in an invalid state!");
            }
            canvas = this.rootSvg;
        }
        return buildCustomGlyphSymbol(entry, defId, canvas);
    }

    /**
     * Layer glyphs into a parent group (legend composite or isometric face overlay).
     */
    protected composeGlyphsLayer(
        parent: Svg,
        glyphs: Glyph[],
        opts: {
            legendKey: string;
            cellsize?: number;
            layout?: "legend" | "isoFace";
            finalizeLegendViewbox?: boolean;
            fontStyleRules?: string[];
            faceInset?: number;
            faceLocalW?: number;
            faceLocalH?: number;
            counterRotateWithBoard?: boolean;
            maxSquareSide?: number;
        },
    ): number {
        if (this.json === undefined || this.rootSvg === undefined) {
            throw new Error("Object in an invalid state!");
        }
        const cellsize = opts.cellsize ?? 500;
        const layout = opts.layout ?? "legend";
        const faceLocalW = opts.faceLocalW ?? cellsize;
        const faceLocalH = opts.faceLocalH ?? cellsize;
        const faceInset = opts.faceInset ?? 1;
        const isoFaceBasis = layout === "isoFace" ? Math.min(faceLocalW, faceLocalH) : cellsize;
        const maxSquareSide = opts.maxSquareSide ?? isoFaceBasis;
        const isoFaceDrawSize = layout === "isoFace" ? isoFaceBasis * faceInset : cellsize;
        const isoFaceOriginX = faceLocalW / 2;
        const isoFaceOriginY = faceLocalH / 2;
        let size = 0;
        for (const [idx, g] of glyphs.entries()) {
            const layoutGlyph = glyphForLayoutRotation(g, this.options.glyphmap);
            let baseScale = 1;
            let got: SVGSymbol;
            // Whether this glyph that was already built in this layer
            let shared = false;
            if (("name" in g) && (g.name !== undefined)) {
                let resolvedGlyphName = g.name;
                if (this.options.glyphmap.length > 0) {
                    const mapIdx = this.options.glyphmap.findIndex(t => t[0] === g.name);
                    if (mapIdx >= 0) {
                        resolvedGlyphName = this.options.glyphmap[mapIdx][1];
                    }
                }
                const colour2Slot = resolveColour2SlotForGlyph(resolvedGlyphName, this.options.sheets);
                const normalized = normalizeGlyphPaint(g, colour2Slot)!;
                const player = playerIndexFromSheetGlyph(g, normalized);
                const cacheKey = [
                    resolvedGlyphName, layout, paintFingerprint(normalized.paint),
                ].join("\u0000");
                const already = this.glyphCache.get(cacheKey);
                if (already !== undefined) {
                    got = already;
                    shared = true;
                } else {
                    got = this.loadGlyph(g.name, player, layout === "isoFace" ? parent : this.rootSvg);
                    this.glyphCache.set(cacheKey, got);
                }
                if (this.options.glyphmap.length > 0) {
                    const i = this.options.glyphmap.findIndex(t => t[0] === g.name);
                    if (i >= 0) {
                        baseScale = this.options.glyphmap[i][2];
                    }
                }
            } else if (isCustomGlyph(g)) {
                const defId = g.def;
                const colour2Slot = "border";
                const normalized = normalizeGlyphPaint(g, colour2Slot)!;
                const cacheKey = [
                    defId, layout, paintFingerprint(normalized.paint),
                ].join("\u0000");
                const already = this.glyphCache.get(cacheKey);
                if (already !== undefined) {
                    got = already;
                    shared = true;
                } else {
                    got = this.loadCustomGlyph(defId, layout === "isoFace" ? parent : this.rootSvg);
                    this.glyphCache.set(cacheKey, got);
                }
            } else if (("text" in g) && (g.text !== undefined) && (g.text.length > 0)) {
                const symbolParent = layout === "isoFace" ? (parent.defs() as Svg) : parent;
                const group = symbolParent.symbol();
                const fontsize = TEXT_GLYPH_BASE_FONT_SIZE;
                const fontOpts: Record<string, unknown> = {
                    anchor: "start",
                    fill: this.options.colourContext.strokes,
                    size: fontsize,
                };
                if (g.fontFamily !== undefined) {
                    fontOpts.family = g.fontFamily;
                }
                if (g.fontWeight !== undefined) {
                    fontOpts.weight = g.fontWeight;
                }
                const text = group.text(g.text).font(fontOpts);
                text.attr("data-playerfill", true);
                const temptext = this.rootSvg.text(g.text).font(fontOpts);
                const textBox = temptext.bbox();
                const squaresize = Math.max(textBox.height, textBox.width);
                group.viewbox(textBox.x, textBox.y, textBox.width, textBox.height);
                group.attr("data-cellsize", squaresize);
                temptext.remove();
                got = group;
            } else {
                throw new Error(`Could not load one of the components of the glyph '${opts.legendKey}': ${JSON.stringify(g)}.`);
            }

            const glyphId = shared ? (got.id() as string) : glyph2uid(g, opts.legendKey, idx);
            if (!shared) {
                got.id(glyphId);
            }

            if (opts.fontStyleRules !== undefined && "text" in g && (g.fontFamily !== undefined || g.fontWeight !== undefined)) {
                const props: string[] = [];
                if (g.fontFamily !== undefined) {
                    props.push(`font-family: ${g.fontFamily} !important`);
                }
                if (g.fontWeight !== undefined) {
                    props.push(`font-weight: ${g.fontWeight} !important`);
                }
                opts.fontStyleRules.push(`#${glyphId} text { ${props.join("; ")}; }`);
            }

            if (!shared) {
                const slotted = symbolHasSlotBindings(got);
                const paintApplier: GlyphPaintApplier = {
                    resolveFill: this.resolveFill.bind(this),
                    applyPlayerFillTargets: this.applyPlayerFillTargets.bind(this),
                    isPatternSVGElement: this.isPatternSVGElement.bind(this),
                };

                if (("name" in g) && (g.name !== undefined)) {
                    let resolvedGlyphName = g.name;
                    if (this.options.glyphmap.length > 0) {
                        const mapIdx = this.options.glyphmap.findIndex(t => t[0] === g.name);
                        if (mapIdx >= 0) {
                            resolvedGlyphName = this.options.glyphmap[mapIdx][1];
                        }
                    }
                    const colour2Slot = resolveColour2SlotForGlyph(resolvedGlyphName, this.options.sheets);
                    const normalized = normalizeGlyphPaint(g, colour2Slot)!;
                    const knownSlots = knownPaintSlotNames(resolvedGlyphName, this.options.sheets);
                    applySlotDefaults(got, normalized.paint, (symbol) => {
                        this.applyContextStylesToGlyphSymbol(symbol);
                    });

                    let sheetCellSize = got.viewbox().height;
                    if ((sheetCellSize === null) || (sheetCellSize === undefined)) {
                        sheetCellSize = got.attr("data-cellsize") as number;
                        if ((sheetCellSize === null) || (sheetCellSize === undefined)) {
                            throw new Error(`The glyph you requested (${opts.legendKey}) does not contain the necessary information for scaling. Please use a different sheet or contact the administrator.`);
                        }
                    }

                    const shadingProfile = proceduralShadingProfile(resolvedGlyphName);
                    let slotPaint = normalized.paint;
                    if (shadingProfile !== undefined) {
                        const baseHex = resolvedPaintBaseHex(normalized.paint, paintApplier.resolveFill);
                        if (isOrbShadingProfile(shadingProfile)) {
                            let detailOverride: string | undefined;
                            if (normalized.paint.detail !== undefined) {
                                const detailVal = paintColourValue(normalized.paint.detail);
                                if (detailVal !== undefined) {
                                    const resolvedDetail = paintApplier.resolveFill(
                                        detailVal as number | string | Gradient | Colourfuncs,
                                        "#000",
                                    );
                                    if (typeof resolvedDetail === "string") {
                                        detailOverride = resolvedDetail;
                                    }
                                }
                            }
                            applyProceduralShadedPaint(got, shadingProfile, baseHex, detailOverride);
                        } else if (isMarbledShadingProfile(shadingProfile)) {
                            applyProceduralMarbledPaint(got, baseHex);
                        }
                        const fillOpacity =
                            normalized.paint.fill !== undefined ? paintSlotOpacity(normalized.paint.fill) : 1;
                        if (fillOpacity !== 1) {
                            applyFillSlotFillChannelOpacity(got, "fill", fillOpacity, colour2Slot);
                        }
                        slotPaint = paintMapAfterProceduralShading(normalized.paint, shadingProfile);
                    }

                    let glyphPaintMode: string | undefined;
                    for (const sheetName of this.options.sheets) {
                        const sheetMeta = getGlyphDefinitionMeta(sheetName, resolvedGlyphName);
                        if (sheetMeta !== undefined) {
                            glyphPaintMode = sheetMeta.paintMode;
                            break;
                        }
                    }

                    if (glyphPaintMode !== "fixed") {
                        applySlotPaint(got, slotPaint, paintApplier, {
                            sheetCellSize,
                            colour2Slot,
                            slotted,
                            glyphName: resolvedGlyphName,
                            knownSlots,
                        });
                    }
                } else if (isCustomGlyph(g)) {
                    const defId = g.def;
                    const colour2Slot = "border";
                    const normalized = normalizeGlyphPaint(g, colour2Slot)!;
                    const slotMeta = inferSlotsFromSymbol(got, colour2Slot);
                    const knownSlots = Object.keys(slotMeta);
                    applySlotDefaults(got, normalized.paint, (symbol) => {
                        this.applyContextStylesToGlyphSymbol(symbol);
                    });

                    let sheetCellSize = got.viewbox().height;
                    if ((sheetCellSize === null) || (sheetCellSize === undefined)) {
                        sheetCellSize = got.attr("data-cellsize") as number;
                        if ((sheetCellSize === null) || (sheetCellSize === undefined)) {
                            throw new Error(`The custom glyph "${defId}" does not contain the necessary information for scaling.`);
                        }
                    }

                    applySlotPaint(got, normalized.paint, paintApplier, {
                        sheetCellSize,
                        colour2Slot,
                        slotted,
                        glyphName: defId,
                        knownSlots,
                    });
                } else if (("text" in g) && (g.text !== undefined)) {
                    applyTextGlyphFill(
                        got,
                        g,
                        glyphs,
                        idx,
                        {
                            resolveColour: this.resolveColour.bind(this) as TextGlyphFillContext["resolveColour"],
                            resolveFill: this.resolveFill.bind(this),
                            applyPlayerFillTargets: this.applyPlayerFillTargets.bind(this),
                            isPatternSVGElement: this.isPatternSVGElement.bind(this),
                            contextBackground: this.options.colourContext.background,
                        },
                        { fontSize: TEXT_GLYPH_BASE_FONT_SIZE },
                    );
                }
            }

            let layerOpacity = 1;
            if (("name" in g) && (g.name !== undefined)) {
                let resolvedGlyphName = g.name;
                if (this.options.glyphmap.length > 0) {
                    const mapIdx = this.options.glyphmap.findIndex(t => t[0] === g.name);
                    if (mapIdx >= 0) {
                        resolvedGlyphName = this.options.glyphmap[mapIdx][1];
                    }
                }
                const colour2Slot = resolveColour2SlotForGlyph(resolvedGlyphName, this.options.sheets);
                layerOpacity = normalizeGlyphPaint(g, colour2Slot)!.layerOpacity;
            } else if (isCustomGlyph(g)) {
                layerOpacity = normalizeGlyphPaint(g, "border")!.layerOpacity;
            } else if (("text" in g) && (g.text !== undefined) && (g.text.length > 0)) {
                layerOpacity = normalizeTextGlyphPaint(g).layerOpacity;
            } else if (g.opacity !== undefined) {
                layerOpacity = g.opacity;
            }

            let factor = baseScale;
            if (g.scale !== undefined) {
                factor *= g.scale;
            }

            let layerIsoFaceDrawSize = isoFaceDrawSize;
            let layerIsoFaceX = isoFaceOriginX;
            let layerIsoFaceY = isoFaceOriginY;
            if (layout === "isoFace") {
                if ((("name" in g) && (g.name !== undefined)) || isCustomGlyph(g)) {
                    layerIsoFaceDrawSize = isoFaceGlyphDrawSize(
                        got as unknown as Svg,
                        isoFaceBasis,
                        faceInset,
                        factor,
                        maxSquareSide,
                    );
                    const placement = isoFaceGlyphPlacement(
                        got as unknown as Svg,
                        layerIsoFaceDrawSize,
                        faceLocalW,
                        faceLocalH,
                    );
                    layerIsoFaceDrawSize = placement.drawSize;
                    layerIsoFaceX = placement.x;
                    layerIsoFaceY = placement.y;
                    factor = 1;
                } else {
                    layerIsoFaceDrawSize = Math.min(isoFaceBasis, maxSquareSide) * faceInset * factor;
                    const placement = isoFaceGlyphPlacement(
                        got as unknown as Svg,
                        layerIsoFaceDrawSize,
                        faceLocalW,
                        faceLocalH,
                    );
                    layerIsoFaceDrawSize = placement.drawSize;
                    layerIsoFaceX = placement.x;
                    layerIsoFaceY = placement.y;
                    factor = 1;
                }
            }

            const use = layout === "isoFace"
                ? parent.use(got).height(layerIsoFaceDrawSize).width(layerIsoFaceDrawSize)
                    .x(layerIsoFaceX).y(layerIsoFaceY)
                : parent.use(got).height(cellsize).width(cellsize).x(-cellsize / 2).y(-cellsize / 2);

            if (layerOpacity < 1) {
                use.opacity(layerOpacity);
            }

            const boardRotation = this.getRotation();
            const legendEmbedRotation =
                layout === "legend" ? this.getLegendGlyphBoardRotation() : boardRotation;
            const rotationOpts = layout === "legend"
                ? { counterRotateWithBoard: true, rotateFluidWithBoard: false }
                : {
                    counterRotateWithBoard: opts.counterRotateWithBoard ?? false,
                    rotateFluidWithBoard: opts.counterRotateWithBoard ?? false,
                };

            let nudgeDx = 0;
            let nudgeDy = 0;
            let nudgeRelativeTo: ReturnType<typeof resolveGlyphNudgeRelativeTo> = "glyph";
            if (g.nudge !== undefined) {
                if (g.nudge.dx !== undefined) {
                    nudgeDx = g.nudge.dx;
                }
                if (g.nudge.dy !== undefined) {
                    nudgeDy = g.nudge.dy;
                }
                nudgeRelativeTo = resolveGlyphNudgeRelativeTo(g, g.nudge);
                if (nudgeRelativeTo === "piece") {
                    let pdx = nudgeDx;
                    let pdy = nudgeDy;
                    if (
                        layout === "legend"
                        && glyphKeepsUpright(layoutGlyph)
                        && rotationOpts.counterRotateWithBoard !== false
                    ) {
                        const viewerRotation = boardRotation - legendEmbedRotation;
                        ({ dx: pdx, dy: pdy } = rotatePieceNudgeVector(pdx, pdy, viewerRotation));
                    }
                    applyPieceGlyphNudge(use, pdx, pdy);
                }
            }

            const rotation = resolveGlyphRotationDegrees(layoutGlyph, boardRotation, rotationOpts);
            if (rotation !== null) {
                const rotOrigin = layout === "isoFace" ? layerIsoFaceX + layerIsoFaceDrawSize / 2 : 0;
                const rotOriginY = layout === "isoFace" ? layerIsoFaceY + layerIsoFaceDrawSize / 2 : 0;
                rotate(use, rotation, rotOrigin, rotOriginY);
            }

            if (layout === "legend" && ("board" in this.json) && (this.json.board !== undefined) && (this.json.board !== null) && ("style" in this.json.board) && (this.json.board.style !== undefined)) {
                const style = this.json.board.style;
                if ((style === "hex-of-hex") || (style === "hex-slanted")) {
                    factor *= 0.85;
                } else if (style.endsWith("-tri-f")) {
                    factor *= 0.5;
                } else if (style.startsWith("vertex")) {
                    factor *= 1.16;
                }
            }

            if (factor !== 1) {
                const scaleOrigin = layout === "isoFace" ? layerIsoFaceX + layerIsoFaceDrawSize / 2 : 0;
                const scaleOriginY = layout === "isoFace" ? layerIsoFaceY + layerIsoFaceDrawSize / 2 : 0;
                scale(use, factor, scaleOrigin, scaleOriginY);
            }
            if (factor * cellsize > size) {
                size = factor * cellsize;
            }

            const { flipx, flipy } = resolveGlyphFlipAxes(layoutGlyph, boardRotation, rotationOpts);
            const flipOriginX = layout === "isoFace" ? layerIsoFaceX + layerIsoFaceDrawSize / 2 : 0;
            const flipOriginY = layout === "isoFace" ? layerIsoFaceY + layerIsoFaceDrawSize / 2 : 0;
            if (flipx) {
                use.scale(-1, 1, flipOriginX, flipOriginY);
            }
            if (flipy) {
                use.scale(1, -1, flipOriginX, flipOriginY);
            }

            if (g.nudge !== undefined && nudgeRelativeTo === "glyph") {
                applyGlyphLocalNudge(use, nudgeDx, nudgeDy);
            }
        }

        if (opts.finalizeLegendViewbox) {
            size *= Math.sqrt(2);
            parent.viewbox(-size / 2, -size / 2, size, size).size(size, size);
        } else if (layout === "isoFace") {
            parent.viewbox(0, 0, faceLocalW, faceLocalH);
        }
        return size;
    }

    protected preloadPatternsForGlyphs(glyphs: Glyph[]): void {
        if (this.json === undefined) {
            return;
        }
        const values: Array<number | string | Gradient | Colourfuncs | undefined> = [];
        for (const g of glyphs) {
            values.push(...collectPatternsFromGlyphPaint(g));
        }
        this.preloadPatternsFromColourValues(values);
    }

    /**
     * This function loads all the glyphs from the `legend` into the given Svg.
     * It deals with text glyphs and composite glyphs, including filling with colours, rotating, and scaling.
     *
     */
    protected loadLegend(args: {preserve: boolean} = {preserve: true}) {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Object in an invalid state!");
        }
        if ( ("legend" in this.json) && (this.json.legend !== undefined) ) {
            const preloadValues: Array<number | string | Gradient | Colourfuncs | undefined> = [];
            for (const key in this.json.legend) {
                const node = this.json.legend[key];
                if (typeof node === "string") {
                    continue;
                }
                if (typeof node === "object" && node !== null && !Array.isArray(node) && "piece" in node) {
                    continue;
                }
                let glyphs: Array<Glyph>;
                if (! Array.isArray(node)) {
                    glyphs = [node];
                } else if (Array.isArray(node[0])) {
                    continue;
                } else {
                    glyphs = node as [Glyph, ...Glyph[]];
                }
                for (const e of glyphs) {
                    preloadValues.push(...collectPatternsFromGlyphPaint(e));
                }
            }
            this.preloadPatternsFromColourValues(preloadValues);

            // Now look for composite and coloured pieces and add those to the <defs> section for placement

            const fontStyleRules: string[] = [];

            for (const key in this.json.legend) {
                const glyph = this.json.legend[key];
                if (typeof glyph === "object" && glyph !== null && !Array.isArray(glyph) && "piece" in glyph) {
                    continue;
                }
                let glyphs: Array<Glyph>;
                if (typeof glyph === "string") {
                    glyphs = [{name: glyph}];
                } else if (! Array.isArray(glyph)) {
                    glyphs = [glyph];
                } else if (Array.isArray(glyph[0])) {
                    // polymatrices aren't built here
                    continue;
                } else {
                    glyphs = glyph as [Glyph, ...Glyph[]];
                }

                // Create a new SVG.Nested to represent the composite piece and add it to <defs>
                const nested = this.rootSvg.defs().nested().id(key);
                if (! args.preserve) {
                    nested.attr("preserveAspectRatio", "none");
                }
                this.composeGlyphsLayer(nested, glyphs, {
                    legendKey: key,
                    cellsize: 500,
                    layout: "legend",
                    finalizeLegendViewbox: true,
                    fontStyleRules,
                });

            }

            // now look for and build polymatrices
            // we want them to be proportional, so load them *all* to determine max height and width
            let maxWidth = 0; let maxHeight = 0;

            for (const key in this.json.legend) {
                if (Array.isArray(this.json.legend[key]) && Array.isArray((this.json.legend[key] as unknown[])[0])) {
                    const matrix = this.json.legend[key] as Polymatrix;
                    const height = matrix.length;
                    let width = 0;
                    if (height > 0) {
                        width = matrix[0].length;
                    }
                    const realwidth = (width * this.cellsize);
                    const realheight = (height * this.cellsize);
                    maxWidth = Math.max(maxWidth, realwidth);
                    maxHeight = Math.max(maxHeight, realheight);
                }
            }
            // now build them properly, adjusting the viewbox so they're proportional and centred

            for (const key in this.json.legend) {
                if (Array.isArray(this.json.legend[key]) && Array.isArray((this.json.legend[key] as unknown[])[0])) {
                    const matrix = this.json.legend[key] as Polymatrix;
                    const height = matrix.length;
                    let width = 0;
                    if (height > 0) {
                        width = matrix[0].length;
                    }
                    const realwidth = (width * this.cellsize);
                    const realheight = (height * this.cellsize);
                    const deltax = (maxWidth - realwidth) / 2 * -1;
                    const deltay = (maxHeight - realheight) / 2 * -1;

                    // create nested SVG of the piece, with border
                    const nested = this.rootSvg.defs().nested().id(key)
                        .viewbox(deltax, deltay, maxWidth, maxHeight);
                    this.buildPoly(nested, matrix, {divided: true});
                    if (this.json.board === null) {
                        const iconSpan = Math.max(maxWidth, maxHeight) * Math.SQRT2;
                        const padX = (iconSpan - maxWidth) / 2;
                        const padY = (iconSpan - maxHeight) / 2;
                        nested.viewbox(deltax - padX, deltay - padY, iconSpan, iconSpan).size(iconSpan, iconSpan);
                    }
                }
            }

            if (fontStyleRules.length > 0) {
                this.rootSvg.defs().element("style").words(fontStyleRules.join("\n"));
            }
        }
    }

    /**
     * This is what applies annotations to a finished board.
     * Annotations are applied at the end, and so overlay pieces.
     *
     * @param grid - A map of row/column locations to x,y coordinates
     */
    public annotateBoard(grid: GridPoints, polys?: (Poly|null)[][], opts?: {
        annotations?: AnnotationBasic[];
        notesParent?: SVGG;
        show?: boolean;
    }) {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Object in an invalid state!");
        }
        const show = opts?.show ?? this.options.showAnnotations;
        if (!show) {
            return;
        }
        const annotations = opts?.annotations ?? this.json.annotations;
        if (annotations === undefined) {
            return;
        }
        type Shape = "square"|"circle"|"hexf"|"hexp";

        let notes: SVGG;
        if (opts?.notesParent !== undefined) {
            notes = opts.notesParent.group().id("annotations");
        } else {
            const board = this.rootSvg.findOne("#board") as SVGG|null;
            if (board !== null) {
                notes = board.group().id("annotations");
            } else {
                notes = this.rootSvg.group().id("annotations");
            }
        }
        const rIncrement = this.cellsize / 2;
        let radius = rIncrement;
        let direction = 1;
        for (const note of annotations as (AnnotationBasic|AnnotationSowing)[]) {
                if ( (! ("type" in note)) || (note.type === undefined) ) {
                    throw new Error("Invalid annotation format found.");
                }
                const cloned = {...note};
                if ("targets" in cloned) {
                    // @ts-expect-error (only used to generate UUID)
                    delete cloned.targets;
                }
                if ( (note.type !== undefined) && (note.type === "move") ) {
                    if (note.targets.length < 2) {
                        throw new Error("Move annotations require at least two 'targets'.");
                    }

                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) && (note.colour !== null) ) {
                        colour = this.resolveColour(note.colour ) as string;
                    }
                    let style: "solid"|"dashed" = "solid";
                    let dasharray: string|undefined;
                    if ( ("style" in note) && (note.style !== undefined) ) {
                        style = note.style;
                    }
                    if ( ("dashed" in note) && (Array.isArray(note.dashed)) ) {
                        style = "dashed";
                        dasharray = note.dashed.map(n => n.toString()).join(" ");
                    }
                    let arrow = true;
                    if ( ("arrow" in note) && (note.arrow !== undefined)) {
                        arrow = note.arrow;
                    }
                    let anchors = true;
                    if ( ("anchors" in note) && (note.anchors !== undefined) ) {
                        anchors = note.anchors;
                    }
                    let opacity = 1;
                    if ( ("opacity" in note) && (note.opacity !== undefined) ) {
                        opacity = note.opacity;
                    }
                    let strokeWidth = 0.03;
                    if ( ("strokeWidth" in note) && (note.strokeWidth !== undefined) ) {
                        strokeWidth = note.strokeWidth;
                    }
                    const unit = strokeWidth / 0.03;
                    const s = this.cellsize * strokeWidth / 2;
                    // const markerArrow = notes.marker(5, 5, (add) => add.path("M 0 0 L 10 5 L 0 10 z"));
                    const markerArrow = notes.marker(4 * unit + 3 * s, 4 * unit + 2 * s, (add) => add.path(`M${s},${s} L${s + 4 * unit},${s + 2 * unit} ${s},${s + 4 * unit} Z`).fill(colour)).attr({ 'pointer-events': 'none' }).addClass(`aprender-annotation-${x2uid(cloned)}`);
                    const markerCircle = notes.marker(2 * unit + 2 * s, 2 * unit + 2 * s, (add) => add.circle(2 * unit).center(unit + s, unit + s).fill(colour)).attr({ 'pointer-events': 'none' }).addClass(`aprender-annotation-${x2uid(cloned)}`);
                    const targets = note.targets as ITarget[];
                    const centerPoints: IPoint[] = [];
                    for (const node of targets) {
                        const pt = this.getStackedPoint(grid, node.col, node.row);
                        if (pt === undefined) {
                            throw new Error(`Annotation - Move: Could not find coordinates for row ${node.row}, column ${node.col}.`);
                        }
                        centerPoints.push(pt);
                    }
                    const boardStyle = (this.json.board !== undefined && this.json.board !== null && "style" in this.json.board)
                        ? this.json.board.style
                        : undefined;
                    const noteCellInset = ("cellInset" in note) ? note.cellInset : undefined;
                    const drawPoints = shouldApplyMoveCellInset(
                        boardStyle,
                        this.options.moveAnnotationCellInset,
                        noteCellInset,
                    )
                        ? applyCellInsetToMovePoints(centerPoints, targets, this.cellsize, polys)
                        : centerPoints;
                    const points = drawPoints.map((pt) => `${pt.x},${pt.y}`);
                    const stroke: StrokeData = {
                        color: colour,
                        opacity,
                        width: this.cellsize * strokeWidth,
                        linecap: "round", linejoin: "round"
                    };
                    if (style === "dashed") {
                        if (dasharray !== undefined) {
                            stroke.dasharray = dasharray;
                        } else {
                            stroke.dasharray = (4 * Math.ceil(strokeWidth / 0.03)).toString();
                        }
                    }
                    const line = notes.polyline(points.join(" ")).addClass(`aprender-annotation-${x2uid(cloned)}`).stroke(stroke).fill("none").attr({ 'pointer-events': 'none' });
                    if (anchors) {
                        line.marker("start", markerCircle);
                    }
                    if (arrow) {
                        line.marker("end", markerArrow);
                    } else if (anchors) {
                        line.marker("end", markerCircle);
                    }
                } else if ( (note.type !== undefined) && (note.type === "line") ) {
                    if (note.targets.length < 2) {
                        throw new Error("Line annotations require at least two 'targets'.");
                    }

                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) && (note.colour !== null) ) {
                        colour = this.resolveColour(note.colour ) as string;
                    }
                    let style: "solid"|"dashed" = "solid";
                    let dasharray: string|undefined;
                    if ( ("style" in note) && (note.style !== undefined) ) {
                        style = note.style;
                    }
                    if ( ("dashed" in note) && (Array.isArray(note.dashed)) ) {
                        style = "dashed";
                        dasharray = note.dashed.map(n => n.toString()).join(" ");
                    }
                    let opacity = 1;
                    if ( ("opacity" in note) && (note.opacity !== undefined) ) {
                        opacity = note.opacity;
                    }
                    let strokeWidth = 0.03;
                    if ( ("strokeWidth" in note) && (note.strokeWidth !== undefined) ) {
                        strokeWidth = note.strokeWidth;
                    }
                    let anchors = true;
                    if ( ("anchors" in note) && (note.anchors !== undefined) ) {
                        anchors = note.anchors;
                    }
                    const unit = strokeWidth / 0.03;
                    const s = this.cellsize * strokeWidth / 2;
                    const markerCircle = notes.marker(2 * unit + 2 * s, 2 * unit + 2 * s, (add) => add.circle(2 * unit).center(unit + s, unit + s).fill(colour)).attr({ 'pointer-events': 'none' }).addClass(`aprender-annotation-${x2uid(cloned)}`);
                    const points: string[] = [];
                    for (const node of (note.targets as ITarget[])) {
                        const pt = this.getStackedPoint(grid, node.col, node.row);
                        if (pt === undefined) {
                            throw new Error(`Annotation - Line: Could not find coordinates for row ${node.row}, column ${node.col}.`);
                        }
                        points.push(`${pt.x},${pt.y}`);
                    }
                    const stroke: StrokeData = {
                        color: colour,
                        opacity,
                        width: this.cellsize * strokeWidth,
                        linecap: "round", linejoin: "round"
                    };
                    if (style === "dashed") {
                        if (dasharray !== undefined) {
                            stroke.dasharray = dasharray;
                        } else {
                            stroke.dasharray = (4 * Math.ceil(strokeWidth / 0.03)).toString();
                        }
                    }
                    const line = notes.polyline(points.join(" ")).addClass(`aprender-annotation-${x2uid(cloned)}`).stroke(stroke).fill("none").attr({ 'pointer-events': 'none' });
                    if (anchors) {
                        line.marker("start", markerCircle);
                        line.marker("end", markerCircle);
                    }
                } else if ( (note.type !== undefined) && (note.type === "eject") ) {
                    if (note.targets.length !== 2) {
                        throw new Error("Eject annotations require exactly two 'targets'.");
                    }

                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) ) {
                        colour = this.resolveColour(note.colour , "#000") as string;
                    }
                    let style = "dashed";
                    if ( ("style" in note) && (note.style !== undefined) ) {
                        style = note.style as string;
                    }
                    let arrow = false;
                    if ( ("arrow" in note) && (note.arrow !== undefined)) {
                        arrow = note.arrow;
                    }
                    let opacity = 0.5;
                    if ( ("opacity" in note) && (note.opacity !== undefined) ) {
                        opacity = note.opacity;
                    }

                    // const markerArrow = notes.marker(5, 5, (add) => add.path("M 0 0 L 10 5 L 0 10 z"));
                    const markerArrow = notes.marker(4, 4, (add) => add.path("M0,0 L4,2 0,4").fill(colour)).attr({ 'pointer-events': 'none' }).addClass(`aprender-annotation-${x2uid(cloned)}`);
                    const markerCircle = notes.marker(2, 2, (add) => add.circle(2).fill(colour)).attr({ 'pointer-events': 'none' }).addClass(`aprender-annotation-${x2uid(cloned)}`);
                    const [from, to] = note.targets as ITarget[];
                    const ptFrom = this.getStackedPoint(grid, from.col, from.row);
                    if (ptFrom === undefined) {
                        throw new Error(`Annotation - Ejenct: Could not find coordinates for row ${from.row}, column ${from.col}.`);
                    }
                    const ptTo = this.getStackedPoint(grid, to.col, to.row);
                    if (ptTo === undefined) {
                        throw new Error(`Annotation - Eject: Could not find coordinates for row ${to.row}, column ${to.col}.`);
                    }
                    const ptCtr = this.getArcCentre(ptFrom, ptTo, radius * direction);
                    const stroke: StrokeData = {
                        color: colour,
                        opacity,
                        width: this.cellsize * 0.03,
                        linecap: "round", linejoin: "round"
                    };
                    if (style === "dashed") {
                        stroke.dasharray = "4";
                        if (note.dashed !== undefined && note.dashed !== null) {
                            stroke.dasharray = (note.dashed ).join(" ");
                        }
                    }
                    const line = notes.path(`M ${ptFrom.x} ${ptFrom.y} C ${ptCtr.x} ${ptCtr.y} ${ptCtr.x} ${ptCtr.y} ${ptTo.x} ${ptTo.y}`).addClass(`aprender-annotation-${x2uid(cloned)}`).stroke(stroke).fill("none").attr({ 'pointer-events': 'none' });
                    line.marker("start", markerCircle);
                    if (arrow) {
                        line.marker("end", markerArrow);
                    } else {
                        line.marker("end", markerCircle);
                    }
                    direction *= -1;
                    let fixed = false;
                    if ( ("static" in note) && (note.static !== undefined) && (typeof note.static === "boolean") ) {
                        fixed = note.static;
                    }
                    if (! fixed) {
                        if (direction > 0) {
                            radius += rIncrement;
                        }
                    }
                } else if ( (note.type !== undefined) && (note.type === "enter") ) {
                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) ) {
                        colour = this.resolveColour(note.colour ) as string;
                    }
                    let strokeWeight = this.cellsize* 0.05;
                    if (this.json.board !== null && this.json.board !== undefined && "strokeWeight" in this.json.board && this.json.board.strokeWeight !== undefined) {
                        strokeWeight = this.json.board.strokeWeight;
                    }
                    let dasharray: string|undefined;
                    if (note.style !== "solid") {
                        dasharray = (4 * Math.ceil(strokeWeight / (this.cellsize * 0.05))).toString();
                    }
                    if (note.dashed !== undefined && note.dashed !== null) {
                        dasharray = (note.dashed ).join(" ");
                    }
                    let bgopacity = 1;
                    if ( ("occlude" in note) && note.occlude === false) {
                        bgopacity = 0;
                    }
                    let opacity = 1;
                    if ( ("opacity" in note) && note.opacity !== undefined) {
                        opacity = note.opacity;
                    }
                    for (const node of (note.targets as ITarget[])) {
                        // outline the polygon if provided
                        if (polys !== undefined && polys[node.row][node.col] !== undefined) {
                            const poly = polys[node.row][node.col];
                            if (poly === null) { continue; }
                            if (poly.type === "circle") {
                                notes.circle(poly.r * 2)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(poly.cx, poly.cy)
                                    .attr({ 'pointer-events': 'none' });
                                notes.circle(poly.r * 2)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(poly.cx, poly.cy)
                                    .attr({ 'pointer-events': 'none' });
                            } else if (poly.type === "path") {
                                notes.path(poly.path)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .attr({ 'pointer-events': 'none' });
                                notes.path(poly.path)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .attr({ 'pointer-events': 'none' });
                            } else {
                                notes.polygon(poly.points.map(({x,y}) => [x,y].join(",")).join(" "))
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .attr({ 'pointer-events': 'none' });
                                notes.polygon(poly.points.map(({x,y}) => [x,y].join(",")).join(" "))
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .attr({ 'pointer-events': 'none' });
                            }
                        }
                        // otherwise, just draw the shape
                        else {
                            let shape: Shape = "square";
                            if ( ("shape" in note) && (note.shape !== undefined) ) {
                                shape = note.shape as Shape;
                            }
                            // const pt = grid[node.row][node.col];
                            const pt = this.getStackedPoint(grid, node.col, node.row);
                            if (pt === undefined) {
                                throw new Error(`Annotation - Enter: Could not find coordinates for row ${node.row}, column ${node.col}.`);
                            }
                            if (shape === "square") {
                                notes.rect(this.cellsize, this.cellsize)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                                notes.rect(this.cellsize, this.cellsize)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                            } else if (shape === "circle") {
                                notes.circle(this.cellsize * 1.1)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                                notes.circle(this.cellsize * 1.1)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                            } else if (shape.startsWith("hex")) {
                                let start = 0;
                                if (shape === "hexf") {
                                    start = -30;
                                }
                                const periph: [number,number][] = [];
                                for (let i = 0; i < 6; i++) {
                                    periph.push(projectPoint(pt.x, pt.y, this.cellsize * 1.25 * 0.5, start + (i*60)));
                                }
                                notes.polygon(periph)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                                notes.polygon(periph)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                            }
                        }
                    }
                } else if ( (note.type !== undefined) && (note.type === "exit") ) {
                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) ) {
                        colour = this.resolveColour(note.colour ) as string;
                    }
                    let strokeWeight = this.cellsize* 0.05;
                    if (this.json.board !== null && this.json.board !== undefined && "strokeWeight" in this.json.board && this.json.board.strokeWeight !== undefined) {
                        strokeWeight = this.json.board.strokeWeight;
                    }
                    let dasharray: string|undefined;
                    if (note.style !== "solid") {
                        dasharray = (4 * Math.ceil(strokeWeight / (this.cellsize * 0.05))).toString();
                    }
                    if (note.dashed !== undefined && note.dashed !== null) {
                        dasharray = (note.dashed ).join(" ");
                    }
                    let bgopacity = 1;
                    if ( ("occlude" in note) && note.occlude === false) {
                        bgopacity = 0;
                    }
                    let opacity = 1;
                    if ( ("opacity" in note) && note.opacity !== undefined) {
                        opacity = note.opacity;
                    }
                    for (const node of (note.targets as ITarget[])) {
                        // outline the polygon if provided
                        if (polys !== undefined) {
                            const poly = polys[node.row][node.col];
                            if (poly === null) { continue; }
                            if (poly.type === "circle") {
                                notes.circle(poly.r * 2)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(poly.cx, poly.cy)
                                    .attr({ 'pointer-events': 'none' });
                                notes.circle(poly.r * 2)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(poly.cx, poly.cy)
                                    .attr({ 'pointer-events': 'none' });
                            } else if (poly.type === "path") {
                                notes.path(poly.path)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .attr({ 'pointer-events': 'none' });
                                notes.path(poly.path)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .attr({ 'pointer-events': 'none' });
                            } else {
                                notes.polygon(poly.points.map(({x,y}) => [x,y].join(",")).join(" "))
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .attr({ 'pointer-events': 'none' });
                                notes.polygon(poly.points.map(({x,y}) => [x,y].join(",")).join(" "))
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .attr({ 'pointer-events': 'none' });
                            }
                        }
                        // otherwise, just draw the square
                        else {
                            let shape: Shape = "square";
                            if ( ("shape" in note) && (note.shape !== undefined) ) {
                                shape = note.shape as Shape;
                            }
                            // const pt = grid[node.row][node.col];
                            const pt = this.getStackedPoint(grid, node.col, node.row);
                            if (pt === undefined) {
                                throw new Error(`Annotation - Exit: Could not find coordinates for row ${node.row}, column ${node.col}.`);
                            }
                            if (shape === "square") {
                                notes.rect(this.cellsize, this.cellsize)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                                notes.rect(this.cellsize, this.cellsize)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                            } else if (shape === "circle") {
                                notes.circle(this.cellsize * 1.1)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                                notes.circle(this.cellsize * 1.1)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                            } else if (shape.startsWith("hex")) {
                                let start = 0;
                                if (shape === "hexf") {
                                    start = -30;
                                }
                                const periph: [number,number][] = [];
                                for (let i = 0; i < 6; i++) {
                                    periph.push(projectPoint(pt.x, pt.y, this.cellsize * 1.25 * 0.5, start + (i*60)));
                                }
                                notes.polygon(periph)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: this.options.colourContext.board ?? this.options.colourContext.background, width: strokeWeight, linecap: "round", linejoin: "round", opacity: bgopacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                                notes.polygon(periph)
                                    .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                    .fill("none")
                                    .stroke({color: colour, width: strokeWeight, dasharray, linecap: "round", linejoin: "round", opacity})
                                    .center(pt.x, pt.y)
                                    .attr({ 'pointer-events': 'none' });
                            }
                        }
                    }
                } else if ( (note.type !== undefined) && (note.type === "outline") ) {
                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) ) {
                        colour = this.resolveColour(note.colour ) as string;
                    }
                    let dasharray = "4";
                    if (note.dashed !== undefined && note.dashed !== null) {
                        dasharray = (note.dashed ).join(" ");
                    }
                    const pts: IPoint[] = [];
                    for (const node of (note.targets as ITarget[])) {
                        const pt = this.getStackedPoint(grid, node.col, node.row);
                        if (pt === undefined) {
                            throw new Error(`Annotation - Outline: Could not find coordinates for row ${node.row}, column ${node.col}.`);
                        }
                        pts.push(pt);
                    }
                    if (pts.length < 3) {
                        throw new Error("The 'outline' annotation requires at least three points");
                    }
                    notes.polygon(pts.map(pt => `${pt.x},${pt.y}`).join(" "))
                         .addClass(`aprender-annotation-${x2uid(cloned)}`)
                         .fill("none")
                         .stroke({color: colour, width: this.cellsize * 0.05, dasharray, linecap: "round", linejoin: "round"})
                         .attr({ 'pointer-events': 'none' });
                } else if ( (note.type !== undefined) && (note.type === "dots") ) {
                    let colour = this.options.colourContext.annotations;
                    if ( ("colour" in note) && (note.colour !== undefined) ) {
                        colour = this.resolveColour(note.colour ) as string;
                    }
                    let opacity = 1;
                    if ( ("opacity" in note) && (note.opacity !== undefined) ) {
                        opacity = note.opacity;
                    }
                    let diameter = 0.2;
                    if ( ("size" in note) && (note.size !== undefined) ) {
                        diameter = note.size;
                    }
                    // Get shape type (default to filled circle for backwards compatibility)
                    let dotShape: string = "circle";
                    if ( ("dotShape" in note) && (note.dotShape !== undefined) ) {
                        dotShape = note.dotShape;
                    }
                    let rotation = 0;
                    if ( ("rotation" in note) && (note.rotation !== undefined) ) {
                        rotation = note.rotation;
                    }

                    for (const node of (note.targets as ITarget[])) {
                        const pt = this.getStackedPoint(grid, node.col, node.row);
                        if (pt === undefined) {
                            throw new Error(`Annotation - Dots: Could not find coordinates for row ${node.row}, column ${node.col}.`);
                        }

                        if (dotShape === "circle") {
                            // Default filled circle
                            notes.circle(this.cellsize * diameter)
                                .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                .fill(colour)
                                .opacity(opacity)
                                .stroke({width: 0})
                                .center(pt.x, pt.y)
                                .attr({ 'pointer-events': 'none' });
                        } else if (dotShape === "ring") {
                            // Unfilled circle (ring) for dive indication
                            const strokeWidth = this.cellsize * diameter * 0.3;
                            notes.circle(this.cellsize * diameter * 0.8)
                                .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                .fill("none")
                                .opacity(opacity)
                                .stroke({color: colour, width: strokeWidth})
                                .center(pt.x, pt.y)
                                .attr({ 'pointer-events': 'none' });
                        } else if (dotShape === "ring-large") {
                            // Larger unfilled circle for power dive indication
                            const largeDiameter = diameter * 1.1;
                            const strokeWidth = this.cellsize * largeDiameter * 0.3;
                            notes.circle(this.cellsize * largeDiameter)
                                .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                .fill("none")
                                .opacity(opacity)
                                .stroke({color: colour, width: strokeWidth})
                                .center(pt.x, pt.y)
                                .attr({ 'pointer-events': 'none' });
                        } else if (dotShape === "chevron") {
                            // Chevron shape pointing up at 0°, rotated by rotation degrees
                            const size = this.cellsize * diameter;
                            const halfSize = size / 2;
                            // Draw a "V" shape pointing up (chevron)
                            const chevronPath = `M ${-halfSize * 0.7} ${halfSize * 0.3} L 0 ${-halfSize * 0.5} L ${halfSize * 0.7} ${halfSize * 0.3}`;
                            notes.path(chevronPath)
                                .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                .fill("none")
                                .opacity(opacity)
                                .stroke({color: colour, width: this.cellsize * 0.04, linecap: "round", linejoin: "round"})
                                .center(pt.x, pt.y)
                                .transform({rotate: rotation, originX: pt.x, originY: pt.y})
                                .attr({ 'pointer-events': 'none' });
                        } else if (dotShape === "explosion") {
                            // Simple starburst/explosion shape
                            const size = this.cellsize * diameter * 1.2;
                            const outerRadius = size / 2;
                            const innerRadius = outerRadius * 0.4;
                            const numPoints = 8;
                            let pathData = "";
                            for (let i = 0; i < numPoints * 2; i++) {
                                const angle = (i * Math.PI) / numPoints - Math.PI / 2;
                                const radius = i % 2 === 0 ? outerRadius : innerRadius;
                                const x = Math.cos(angle) * radius;
                                const y = Math.sin(angle) * radius;
                                pathData += (i === 0 ? "M " : " L ") + x + " " + y;
                            }
                            pathData += " Z";
                            notes.path(pathData)
                                .addClass(`aprender-annotation-${x2uid(cloned)}`)
                                .fill(colour)
                                .opacity(opacity)
                                .stroke({width: 0})
                                .center(pt.x, pt.y)
                                .transform({rotate: rotation, originX: pt.x, originY: pt.y})
                                .attr({ 'pointer-events': 'none' });
                        }
                    }
                } else if ( (note.type !== undefined) && (note.type === "glyph")) {
                    if ( (! ("targets" in note)) || (note.targets.length < 1) ) {
                        throw new Error(`At least one target must be given for glyph annotations.`);
                    }
                    const key = note.glyph as string;
                    const piece = notes.root().findOne("#" + key) as Svg;
                    if ( (piece === null) || (piece === undefined) ) {
                        throw new Error(`Could not find the requested piece (${key}). The glyph *must* exist in the \`legend\`.`);
                    }
                    for (const pt of (note.targets as ITarget[])) {
                        const point = this.getStackedPoint(grid, pt.col, pt.row);
                        if (point === undefined) {
                            throw new Error(`Annotation - Enter: Could not find coordinates for row ${pt.row}, column ${pt.col}.`);
                        }
                        const use = usePieceAt({svg: notes, piece, cellsize: this.cellsize, x: point.x, y: point.y, scalingFactor: 1});
                        use.attr({ 'pointer-events': 'none' });
                        // if (this.options.rotate && this.json.options && this.json.options.includes('rotate-pieces')) {
                        //     rotate(use, this.options.rotate, point.x, point.y);
                        // }
                    }
                } else if ( (note.type !== undefined) && (note.type === "deltas") ) {
                    type Delta = {
                        row: number;
                        col: number;
                        delta: number;
                    };
                    // generate numerical glyphs for each unique delta
                    const rotation = this.getRotation();
                    const deltas = new Set<number>((note.deltas as Delta[]).map(d => d.delta));
                    for (const delta of deltas) {
                        if (delta === 0) {
                            continue;
                        }
                        const strDelta = `${delta > 0 ? "+" : ""}${delta}`;
                        const cellsize = 500;
                        const nested = this.rootSvg.defs().nested().id(`_delta_${delta < 0 ? `n${Math.abs(delta)}` : delta}`).attr({ 'pointer-events': 'none' });
                        nested.rect(cellsize, cellsize).fill({color: this.options.colourContext.background, opacity: 1}).move(-cellsize / 2, -cellsize / 2);
                        const nestedGroup = nested.symbol();
                        const fontsize = 17;
                        const text = nestedGroup.text(strDelta).font({
                            anchor: "start",
                            fill: this.options.colourContext.strokes,
                            size: fontsize,
                            opacity: 0.5,
                        });
                        text.attr("data-playerfill", true);
                        text.attr("font-weight", "bold");
                        const temptext = this.rootSvg.text(strDelta).font({
                            anchor: "start",
                            fill: this.options.colourContext.strokes,
                            size: fontsize,
                            opacity: 0.5,
                        });
                        const textBox = temptext.bbox();
                        const squaresize = Math.max(textBox.height, textBox.width);
                        nestedGroup.viewbox(textBox.x, textBox.y, textBox.width, textBox.height);
                        nestedGroup.attr("data-cellsize", squaresize);
                        temptext.remove();
                        const got = nestedGroup;

                        let sheetCellSize = got.viewbox().height;
                        if ( (sheetCellSize === null) || (sheetCellSize === undefined) ) {
                            sheetCellSize = got.attr("data-cellsize") as number;
                            if ( (sheetCellSize === null) || (sheetCellSize === undefined) ) {
                                throw new Error(`The glyph you requested (${delta}) does not contain the necessary information for scaling. Please use a different sheet or contact the administrator.`);
                            }
                        }

                        const use = nested.use(got).height(cellsize).width(cellsize).x(-cellsize / 2).y(-cellsize / 2);
                        use.rotate(rotation * -1, 0, 0);

                        // Scale it appropriately
                        scale(use, 0.5, 0, 0);
                        const size = 0.75 * cellsize;
                        nested.viewbox(-size / 2, -size / 2, size, size).size(size, size);
                    }

                    // Place each delta
                    for (const delta of note.deltas as Delta[]) {
                        if (delta.delta !== 0) {
                            const key = `_delta_${delta.delta < 0 ? `n${Math.abs(delta.delta)}` : delta.delta}`;
                            let point: IPoint;
                            try {
                                const [gx, gy] = this.interpolateFromGrid(grid, delta);
                                point = { x: gx, y: gy };
                            } catch {
                                continue;
                            }
                            const piece = this.rootSvg.findOne(`#${key}`) as Svg;
                            if ( (piece === null) || (piece === undefined) ) {
                                throw new Error(`Could not find the requested delta (${key}).`);
                            }
                            const factor = 0.33; // 0.85;
                            let cornerX: number; let cornerY: number;
                            if (rotation === 180) {
                                cornerX = point.x - (this.cellsize / 2) + (this.cellsize / 5);
                                cornerY = point.y + (this.cellsize / 2) - (this.cellsize / 5);
                            } else {
                                cornerX = point.x + (this.cellsize / 2) - (this.cellsize / 5);
                                cornerY = point.y - (this.cellsize / 2) + (this.cellsize / 5);
                            }
                            usePieceAt({svg: notes, piece, cellsize: this.cellsize, x: cornerX, y: cornerY, scalingFactor: factor});
                        }
                    }
                } else {
                    throw new Error(`The requested annotation (${ note.type as string }) is not supported.`);
                }
            }
    }

    private interpolateFromGrid(grid: GridPoints, point: ITarget) : [number, number] {
        const [x, y] = [point.col, point.row];
        const col = Math.floor(x);
        const row = Math.floor(y);
        let gridx = grid[row][col].x;
        if (!Number.isInteger(x))
            gridx += (x - col) * (grid[row][col + 1].x - grid[row][col].x);
        let gridy = grid[row][col].y;
        if (!Number.isInteger(y))
            gridy += (y - row) * (grid[row + 1][col].y - grid[row][col].y);
        return [gridx, gridy];
    }

    private resolveSquareMarkerPoint(
        point: SquarePoint,
        gridExpanded: GridPoints,
        polys: Poly[][] | undefined,
    ): [number, number] {
        if (isTileCornerPoint(point)) {
            if (polys === undefined || this.json?.board === undefined || this.json.board === null) {
                throw new Error("Tile-corner square board points require polygons.");
            }
            const board = this.json.board;
            let tileWidth = 0;
            let tileHeight = 0;
            let tileSpacing = 0;
            if ("tileWidth" in board && board.tileWidth !== undefined) {
                tileWidth = board.tileWidth;
            }
            if ("tileHeight" in board && board.tileHeight !== undefined) {
                tileHeight = board.tileHeight;
            }
            if ("tileSpacing" in board && board.tileSpacing !== undefined) {
                tileSpacing = board.tileSpacing;
            }
            const width = ("width" in board && board.width !== undefined) ? board.width : 0;
            const height = ("height" in board && board.height !== undefined) ? board.height : 0;

            return resolveSquareBoardPoint(point, {
                polys: polys as IPolyPolygon[][],
                gridExpanded,
                tileWidth,
                tileHeight,
                tileSpacing,
                boardWidth: width,
                boardHeight: height,
                interpolate: (p) => this.interpolateFromGrid(gridExpanded, p),
            });
        }

        if (point.row === undefined || point.col === undefined) {
            throw new Error("Square board points require row/col or tileRow/tileCol/corner.");
        }

        // Macro row/col coordinates keep the legacy unspaced corner grid so existing
        // content (e.g. label nudges that account for tile gaps) is unchanged.
        const board = this.json?.board;
        if (board === undefined || board === null) {
            return this.interpolateFromGrid(gridExpanded, { row: point.row, col: point.col });
        }
        let tileWidth = 0;
        let tileHeight = 0;
        if ("tileWidth" in board && board.tileWidth !== undefined) {
            tileWidth = board.tileWidth;
        }
        if ("tileHeight" in board && board.tileHeight !== undefined) {
            tileHeight = board.tileHeight;
        }
        const width = ("width" in board && board.width !== undefined) ? board.width : 0;
        const height = ("height" in board && board.height !== undefined) ? board.height : 0;
        const legacyGrid = expandSquareGrid(height, width, this.cellsize, tileWidth, tileHeight, 0);
        return this.interpolateFromGrid(legacyGrid, { row: point.row, col: point.col });
    }

    /**
     * Markers are placed right after the board itself is generated, and so are obscured by placed pieces.
     *
     * @param svgGroup - The SVG `<group>` you want to add the markers too. This is just for the sake of organization.
     * @param grid - The map of row/column to x/y created by one of the grid point generators.
     * @param gridExpanded - Square maps need to be expanded a little for all the markers to work. If provided, this is what will be used.
     */
    // protected markBoard(svgGroup: SVGG, preGridLines: boolean, grid: GridPoints, gridExpanded?: GridPoints, hexGrid?: Grid<Hex>, hexWidth?: number, hexHeight?: number): void {
    public markBoard(opts: IMarkBoardOptions): void {
        const svgGroup = opts.svgGroup;
        const preGridLines = opts.preGridLines;
        const grid = opts.grid;
        let gridExpanded: GridPoints|undefined;
        if (opts.gridExpanded !== undefined) {
            gridExpanded = opts.gridExpanded;
        }
        let hexGrid: Grid<Hex>|undefined;
        if (opts.hexGrid !== undefined) {
            hexGrid = opts.hexGrid;
        }
        let hexWidth: number|undefined;
        if (opts.hexWidth !== undefined) {
            hexWidth = opts.hexWidth
        }
        let hexHeight: number|undefined;
        if (opts.hexHeight !== undefined) {
            hexHeight = opts.hexHeight
        }
        let polys: Poly[][]|undefined;
        if (opts.polys !== undefined) {
            polys = opts.polys;
        }
        const boardEdges: Map<CompassDirection, IPolyPolygon> | undefined = opts.boardEdges;

        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Object in an invalid state!");
        }

        const rootId = svgGroup.id();
        let targetGroup: SVGG = svgGroup;
        if (rootId) {
            if (preGridLines) {
                const below = svgGroup.findOne(`#${rootId}-below`) as SVGG | undefined;
                if (below) {
                    targetGroup = below;
                }
            } else {
                const markers = svgGroup.findOne(`#${rootId}-markers`) as SVGG | undefined;
                if (markers) {
                    targetGroup = markers;
                }
            }
        }

        if ( ("board" in this.json) && (this.json.board !== undefined) && ("markers" in this.json.board!) && (this.json.board.markers !== undefined) && (Array.isArray(this.json.board.markers)) && (this.json.board.markers.length > 0) ) {
            if ( (! ("style" in this.json.board)) || (this.json.board.style === undefined) ) {
                throw new Error("This `markBoard` function only works with renderers that include a `style` property.");
            }

            let baseStroke = 1;
            let baseColour = this.options.colourContext.strokes;
            let baseOpacity = 1;
            if ( ("strokeWeight" in this.json.board) && (this.json.board.strokeWeight !== undefined) ) {
                baseStroke = this.json.board.strokeWeight;
            }
            if ( ("strokeColour" in this.json.board) && (this.json.board.strokeColour !== undefined) ) {
                baseColour = this.resolveColour(this.json.board.strokeColour) as string;
            }
            if ( ("strokeOpacity" in this.json.board) && (this.json.board.strokeOpacity !== undefined) ) {
                baseOpacity = this.json.board.strokeOpacity;
            }

            const allMarkers = this.json.board.markers.filter(m => m.type !== "fences");
            const fences = this.json.board.markers.filter(m => m.type === "fences") as MarkerFences[];
            if (fences.length > 0) {
                for (const decl of fences) {
                    for (const side of decl.sides) {
                        allMarkers.push({type: "fence", ...side} as MarkerFence);
                    }
                }
            }

            for (const marker of allMarkers) {
                let belowGrid: boolean|undefined;
                if ((marker.type === "flood") && marker.belowGrid === undefined) {
                    const boardStyle =
                        "style" in this.json.board! ? this.json.board!.style : undefined;
                    marker.belowGrid = boardStyle !== "eleven";
                }
                if ("belowGrid" in marker) {
                    belowGrid = marker.belowGrid;
                }
                if (! ((preGridLines && belowGrid === true) || (!preGridLines && (belowGrid === undefined || belowGrid === false)) || (preGridLines && marker.type === "halo") )) {
                    continue;
                }
                const cloned = {...marker};
                if ("points" in cloned) {
                    // @ts-expect-error (only used to generate UUID)
                    delete cloned.points;
                }
                if (marker.type === "dots") {
                    let colour: string|SVGGradient = baseColour;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        colour = this.resolveColour(marker.colour as string|number|Gradient);
                    }
                    let opacity = baseOpacity;
                    if ( ("opacity" in marker) && (marker.opacity !== undefined) ) {
                        opacity = marker.opacity;
                    }
                    let diameter = 0.2;
                    if ( ("size" in marker) && (marker.size !== undefined) ) {
                        diameter = marker.size;
                    }
                    const pts: [number, number][] = [];
                    for (const point of marker.points as ITarget[]) {
                        pts.push([point.row, point.col]);
                    }
                    pts.forEach((p) => {
                        const [ptx, pty] = this.interpolateFromGrid(grid, {row: p[0], col: p[1]});
                        // const pt = grid[p[0]][p[1]];
                        // these exceptions are due to poor SVGjs typing
                        let dotDiameter = this.cellsize * diameter;
                        const board = this.json?.board;
                        if (
                            board !== null &&
                            board !== undefined &&
                            "style" in board &&
                            board.style === "eleven" &&
                            polys !== undefined
                        ) {
                            const cell = polys[p[0]]?.[p[1]];
                            if (cell !== undefined && cell.type === "circle") {
                                dotDiameter = cell.r * 2 * diameter;
                            }
                        }

                        targetGroup.circle(dotDiameter)
                            // @ts-expect-error (poor SVGjs typing)
                            .fill(colour)
                            .opacity(opacity)
                            .stroke({width: 0})
                            .center(ptx, pty)
                            .attr({ 'pointer-events': 'none' })
                            .addClass(`aprender-marker-${x2uid(cloned)}`);
                    });
                } else if (marker.type === "shading") {
                    let colour: string|SVGGradient = this.options.colourContext.fill;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        colour = this.resolveColour(marker.colour as string|number|Gradient);
                    }
                    let opacity = 0.25;
                    if ( ("opacity" in marker) && (marker.opacity !== undefined) ) {
                        opacity = marker.opacity;
                    }
                    const points: [number, number][] = [];
                    if ( ( (this.json.board.style.startsWith("squares")) || (this.json.board.style.startsWith("sowing")) || this.json.board.style === "pegboard") && (gridExpanded !== undefined) ) {
                        const ptList = marker.points as SquarePoint[];
                        for (const point of ptList) {
                            const [x, y] = this.resolveSquareMarkerPoint(point, gridExpanded, polys);
                            points.push([x, y]);
                        }
                    } else {
                        for (const point of marker.points as ITarget[]) {
                            points.push([grid[point.row][point.col].x, grid[point.row][point.col].y]);
                        }
                    }
                    const ptstr = points.map((p) => p.join(",")).join(" ");
                    // @ts-expect-error (poor SVGjs typing)
                    targetGroup.polygon(ptstr).addClass(`aprender-marker-${x2uid(cloned)}`).fill(colour).opacity(opacity).attr({ 'pointer-events': 'none' });
                } else if (marker.type === "flood") {
                    if (polys === undefined) {
                        if (this.options.sanitizeMode === "live") {
                            continue;
                        }
                        throw new Error("The `flood` marker can only be used if polygons are passed to the marking code.");
                    }
                    let isGradient = false;
                    let isFlatten = false;
                    let resolvedFill: ResolvedFill = this.options.colourContext.fill;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        if (typeof marker.colour === "object" && ("stops" in marker.colour)) {
                            isGradient = true;
                        } else if (typeof marker.colour === "object" && ("func" in marker.colour) && marker.colour.func === "flatten") {
                            isFlatten = true;
                        }
                        resolvedFill = this.resolveFill(marker.colour);
                    }
                    let opacity = 0.25;
                    if ( ("opacity" in marker) && (marker.opacity !== undefined) ) {
                        opacity = marker.opacity;
                    }
                    // don't do automatic opacity of function is flatten
                    else if (isFlatten) {
                        opacity = 1;
                    }
                    let pattern: string | undefined;
                    if ( ("pattern" in marker) && (marker.pattern !== undefined) && (marker.pattern.length > 0) ) {
                        pattern = marker.pattern;
                    }
                    let fill: FillData|SVGGradient|SVGElement;
                    if (isGradient) {
                        fill = resolvedFill as SVGGradient;
                    } else if (pattern !== undefined) {
                        const patternOpts = {
                            bg: "none",
                            fg: typeof resolvedFill === "string" ? resolvedFill : undefined,
                        };
                        const patternId = this.loadPattern(pattern, patternOpts);
                        fill = this.rootSvg.findOne(`#${patternId}`) as SVGElement;
                        if (fill === undefined) {
                            throw new Error("Could not load the requested pattern.");
                        }
                    } else if (this.isPatternSVGElement(resolvedFill)) {
                        fill = resolvedFill;
                    } else if (typeof resolvedFill === "object") {
                        fill = resolvedFill;
                    } else {
                        fill = {color: resolvedFill, opacity} as FillData;
                    }
                    for (const point of marker.points as ITarget[]) {
                        let floodEle: SVGCircle|SVGPolygon|SVGPath|undefined;
                        const cell = polys[point.row][point.col];
                        if (cell === undefined || cell === null) {
                            if (this.options.sanitizeMode === "live") {
                                continue;
                            }
                            throw new Error(`There is no polygon at row ${point.row}, col ${point.col}. (In "wheel" boards, polygons are only present on odd-numbered rows.)`);
                        }
                        // the following eslint and ts exceptions are due to poor SVGjs typing
                        switch (cell.type) {
                            case "circle":
                                // @ts-expect-error (poor SVGjs typing)
                                floodEle = targetGroup.circle(cell.r * 2).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({color: "none", width: baseStroke}).fill(fill).center(cell.cx, cell.cy).attr({ 'pointer-events': 'none' });
                                break;
                            case "poly":
                                // @ts-expect-error (poor SVGjs typing)
                                floodEle = targetGroup.polygon(cell.points.map(pt => `${pt.x},${pt.y}`).join(" ")).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({color: "none", width: baseStroke, linecap: "round", linejoin: "round"}).fill(fill).attr({ 'pointer-events': 'none' });
                                break;
                            case "path":
                                // @ts-expect-error (poor SVGjs typing)
                                floodEle = targetGroup.path(cell.path).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({color: "none", width: baseStroke, linecap: "round", linejoin: "round"}).fill(fill).attr({ 'pointer-events': 'none' });
                                break;
                        }
                        if (marker.pulse !== undefined && floodEle !== undefined) {
                            attachMarkerPulse(floodEle, marker.pulse, {
                                static: this.options.staticAnimations,
                                rootSvg: this.rootSvg,
                            });
                        }
                    }
                } else if (marker.type === "line") {
                    let colour = baseColour;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        colour = this.resolveColour(marker.colour ) as string;
                    }
                    let opacity = baseOpacity;
                    if ( ("opacity" in marker) && (marker.opacity !== undefined) ) {
                        opacity = marker.opacity;
                    }
                    let width = baseStroke;
                    if ( ("width" in marker) && (marker.width !== undefined) ) {
                        width = marker.width;
                    }
                    const stroke: StrokeData = {
                        color: colour,
                        opacity,
                        width,
                        linecap: "round", linejoin: "round"
                    };
                    if ( ("style" in marker) && (marker.style !== undefined) && (marker.style === "dashed") ) {
                        if ( ("dasharray" in marker) && (marker.dasharray !== undefined) && (Array.isArray(marker.dasharray)) ) {
                            stroke.dasharray = marker.dasharray.join(" ");
                        } else {
                            stroke.dasharray = "4";
                        }
                    }
                    let centered = false;
                    if ( "centered" in marker && marker.centered !== undefined && marker.centered) {
                        centered = true;
                    }
                    let clickable = false;
                    if ( "clickable" in marker && marker.clickable !== undefined && marker.clickable && this.options.boardClick !== undefined) {
                        clickable = true;
                    }

                    let x1: number; let x2: number; let y1: number; let y2: number;
                    const point1 = (marker.points as SquarePoint[])[0]!;
                    const point2 = (marker.points as SquarePoint[])[1]!;
                    if ( (this.json.board.style.startsWith("squares") || this.json.board.style === "pegboard") && (gridExpanded !== undefined) && (! centered) ) {
                        [x1, y1] = this.resolveSquareMarkerPoint(point1, gridExpanded, polys);
                        [x2, y2] = this.resolveSquareMarkerPoint(point2, gridExpanded, polys);
                    } else {
                        [x1, y1] = this.interpolateFromGrid(grid, { row: point1.row!, col: point1.col! });
                        [x2, y2] = this.interpolateFromGrid(grid, { row: point2.row!, col: point2.col! });
                    }

                    if ("shorten" in marker && marker.shorten !== undefined && marker.shorten > 0) {
                        [x1, y1, x2, y2] = shortenLine(x1, y1, x2, y2, marker.shorten);
                    }

                    const line = targetGroup.line(x1, y1, x2, y2).stroke(stroke).addClass(`aprender-marker-${x2uid(cloned)}`);
                    if (clickable) {
                        const id = (point1.row !== undefined && point1.col !== undefined)
                            ? `${point1.col},${point1.row}|${point2.col},${point2.row}`
                            : `${point1.tileRow},${point1.tileCol},${point1.corner}|${point2.tileRow},${point2.tileCol},${point2.corner}`;
                        line.click((e: MouseEvent) => {
                            this.options.boardClick!(-1, -1, id);
                            e.stopPropagation();
                        });
                    } else {
                        line.attr({ 'pointer-events': 'none' });
                    }
                } else if (marker.type === "halo") {
                    if (! this.haloStyleSupported(this.json.board.style) ) {
                        if (this.options.sanitizeMode === "live") {
                            continue;
                        }
                        throw new Error("The `halo` marker only works with `circular-*`, `conical-hex*`, `hex-of*`, and `bricka` boards.");
                    }

                    const wedge = marker.wedge === true;
                    let polys: Poly[][]|undefined;
                    if (opts.polys !== undefined) {
                        polys = opts.polys;
                    }
                    if (polys === undefined) {
                        if (this.options.sanitizeMode === "live") {
                            continue;
                        }
                        throw new Error("The `halo` marker requires that the polygons be passed.");
                    }

                    const boardStyle = this.json.board.style;
                    let rx = 0;
                    let ry = 0;
                    let cx = 0;
                    let cy = 0;
                    if (boardStyle.startsWith("hex-of")) {
                        const union = unionPolys(polys.flat()).map(([x, y]) => { return { x, y }; }) as IPoint[];
                        const minx = Math.min(...union.map((pt) => pt.x));
                        const maxx = Math.max(...union.map((pt) => pt.x));
                        const hullWidth = maxx - minx;
                        cx = minx + (hullWidth / 2);
                        const miny = Math.min(...union.map((pt) => pt.y));
                        const maxy = Math.max(...union.map((pt) => pt.y));
                        const hullHeight = maxy - miny;
                        cy = miny + (hullHeight / 2);
                        const diameter = Math.max(hullHeight, hullWidth) + (this.cellsize / 2);
                        const r = diameter / 2;
                        rx = r;
                        ry = r;
                    } else if (boardStyle.startsWith("circular") && !boardStyle.endsWith("moon")) {
                        for (const poly of polys.flat()) {
                            if (poly.type !== "circle") {
                                for (const pt of poly.points) {
                                    rx = rx === undefined ? Math.max(pt.x, pt.y) : Math.max(rx, pt.x, pt.y);
                                    ry = rx;
                                }
                            }
                        }
                    } else {
                        const allCoords: IPoint[] = (polys.flat() as IPolyPolygon[]).map((p) => p.points).flat();
                        const minx = Math.min(...allCoords.map((pt) => pt.x));
                        const maxx = Math.max(...allCoords.map((pt) => pt.x));
                        let miny = Math.min(...allCoords.map((pt) => pt.y));
                        if (boardStyle.endsWith("narrow")) {
                            miny -= 15;
                        }
                        const maxy = Math.max(...allCoords.map((pt) => pt.y));
                        const width = maxx - minx;
                        const height = maxy - miny;
                        rx = (width / 2);
                        if (!boardStyle.endsWith("moon")) {
                            rx *= 1.05;
                        } else {
                            rx *= 1.02;
                        }
                        const dx = Math.abs(rx - (width / 2));
                        cx = minx + rx - dx;
                        ry = (height / 2);
                        if (!boardStyle.endsWith("moon")) {
                            ry *= 1.05;
                        } else {
                            ry *= 1.02;
                        }
                        const dy = Math.abs(ry - (height / 2));
                        cy = miny + ry - dy;

                        if (boardStyle.endsWith("moon")) {
                            const max = Math.max(rx, ry);
                            rx = max;
                            ry = max;
                        }
                    }

                    if ( ("nudge" in marker) && (marker.nudge !== undefined) ) {
                        cx += marker.nudge.dx;
                        cy += marker.nudge.dy;
                    }

                    if (preGridLines) {
                        if (!wedge && ("fill" in marker) && (marker.fill !== undefined) ) {
                            const fill = this.resolveColour(marker.fill) as string;
                            if (boardStyle.startsWith("circular")) {
                                targetGroup.circle(rx * 2).fill(fill).center(cx, cy);
                            } else {
                                targetGroup.ellipse(rx * 2, ry * 2).fill(fill).center(cx, cy);
                            }
                        }
                        continue;
                    }

                    let width = baseStroke;
                    if ( ("width" in marker) && (marker.width !== undefined) ) {
                        width = marker.width;
                    }
                    const ringRx = wedge ? rx : rx + width / 2;
                    const ringRy = wedge ? ry : ry + width / 2;

                    let degStart = 0;
                    if ( ("circular-start" in this.json.board) && (this.json.board["circular-start"] !== undefined) ) {
                        degStart = this.json.board["circular-start"];
                    }
                    if ( ("offset" in marker) && (marker.offset !== undefined) ) {
                        degStart += marker.offset;
                    }
                    let spans;
                    try {
                        spans = resolveHaloSegmentSpans(marker.segments);
                    } catch (e) {
                        if (e instanceof HaloSegmentDegreesError) {
                            if (this.options.sanitizeMode === "live") {
                                continue;
                            }
                            throw e;
                        }
                        throw e;
                    }

                    const haloUsesCircularArc =
                        boardStyle.startsWith("circular")
                        || boardStyle.startsWith("hex-of")
                        || Math.abs(ringRx - ringRy) < 1e-6;
                    const ringPoint = (deg: number): [number, number] => {
                        if (haloUsesCircularArc) {
                            return projectPoint(cx, cy, ringRx, deg);
                        }
                        return projectPointEllipse(cx, cy, ringRx, ringRy, deg);
                    };

                    const segmentStroke = (segment: ISegment): StrokeData => {
                        let colour = baseColour;
                        if ( ("colour" in segment) && (segment.colour !== undefined) ) {
                            colour = this.resolveColour(segment.colour) as string;
                        }
                        let opacity = baseOpacity;
                        if ( ("opacity" in segment) && (segment.opacity !== undefined) ) {
                            opacity = segment.opacity;
                        }
                        const stroke: StrokeData = {
                            color: colour,
                            opacity,
                            width,
                            linecap: "round",
                            linejoin: "round",
                        };
                        if ( ("style" in segment) && (segment.style !== undefined) && (segment.style === "dashed") ) {
                            stroke.dasharray = "4";
                        }
                        return stroke;
                    };
                    const segmentFill = (segment: ISegment): FillData => {
                        const stroke = segmentStroke(segment);
                        return { color: stroke.color, opacity: stroke.opacity };
                    };

                    if (wedge) {
                        const wedgeLayer = this.rootSvg;
                        if (haloIsFullCircle(spans)) {
                            const segment: ISegment = marker.segments[0] as ISegment;
                            const fill = segmentFill(segment);
                            if (haloUsesCircularArc) {
                                wedgeLayer.circle(ringRx * 2).addClass(`aprender-marker-${x2uid(cloned)}-segment1`).fill(fill).stroke("none").back();
                            } else {
                                wedgeLayer.ellipse(ringRx * 2, ringRy * 2).addClass(`aprender-marker-${x2uid(cloned)}-segment1`).fill(fill).stroke("none").back();
                            }
                        } else {
                            for (const span of spans) {
                                if (!span.draw) {
                                    continue;
                                }
                                const segment: ISegment = marker.segments[span.segmentIndex] as ISegment;
                                const fill = segmentFill(segment);
                                const [xleft, yleft] = ringPoint(degStart + span.startDeg);
                                const [xright, yright] = ringPoint(degStart + span.endDeg);
                                const largeArc = span.spanDeg > 180 ? 1 : 0;
                                wedgeLayer.path(`M${xleft},${yleft} A ${ringRx} ${ringRy} 0 ${largeArc} 1 ${xright},${yright} L${cx},${cy} L${xleft},${yleft}`)
                                    .addClass(`aprender-marker-${x2uid(cloned)}-segment${span.segmentIndex + 1}`)
                                    .fill(fill).stroke("none").back();
                            }
                        }
                    } else if (haloIsFullCircle(spans)) {
                        const segment: ISegment = marker.segments[0] as ISegment;
                        const stroke = segmentStroke(segment);
                        if (boardStyle.startsWith("circular")) {
                            targetGroup.circle(ringRx * 2).addClass(`aprender-marker-${x2uid(cloned)}-segment1`).fill("none").stroke(stroke);
                        } else {
                            targetGroup.ellipse(ringRx * 2, ringRy * 2).addClass(`aprender-marker-${x2uid(cloned)}-segment1`).fill("none").stroke(stroke);
                        }
                    } else {
                        for (const span of spans) {
                            if (!span.draw) {
                                continue;
                            }
                            const segment: ISegment = marker.segments[span.segmentIndex] as ISegment;
                            const stroke = segmentStroke(segment);
                            const [xleft, yleft] = ringPoint(degStart + span.startDeg);
                            const [xright, yright] = ringPoint(degStart + span.endDeg);
                            const largeArc = span.spanDeg > 180 ? 1 : 0;
                            targetGroup.path(`M${xleft},${yleft} A ${ringRx} ${ringRy} 0 ${largeArc} 1 ${xright},${yright}`)
                                .addClass(`aprender-marker-${x2uid(cloned)}-segment${span.segmentIndex + 1}`)
                                .fill("none").stroke(stroke);
                        }
                    }
                } else if (marker.type === "label") {
                    let colour = baseColour;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        colour = this.resolveColour(marker.colour ) as string;
                    }

                    let x1: number; let x2: number; let y1: number; let y2: number;
                    if ( (this.json.board.style.startsWith("squares") || this.json.board.style === "pegboard") && (gridExpanded !== undefined) ) {
                        const point1 = (marker.points as SquarePoint[])[0]!;
                        const point2 = (marker.points as SquarePoint[])[1]!;
                        [x1, y1] = this.resolveSquareMarkerPoint(point1, gridExpanded, polys);
                        [x2, y2] = this.resolveSquareMarkerPoint(point2, gridExpanded, polys);
                    } else {
                        const point1 = (marker.points as ITarget[])[0];
                        [x1, y1] = this.interpolateFromGrid(grid, point1);
                        const point2 = (marker.points as ITarget[])[1];
                        [x2, y2] = this.interpolateFromGrid(grid, point2);
                    }

                    // check for nudging
                    if ( ("nudge" in marker) && (marker.nudge !== undefined) && (marker.nudge !== null) ) {
                        const {dx, dy} = marker.nudge as {dx: number; dy: number};
                        x1 += (dx * this.cellsize); x2 += (dx * this.cellsize);
                        y1 += (dy * this.cellsize); y2 += (dy * this.cellsize);
                    }
                    let font = 'font-size: 17px;';
                    if ( ("size" in marker) && (marker.size !== undefined) ) {
                        font = `font-size: ${marker.size }px;`;
                    }
                    font += marker.font ?? '';
                    const text = targetGroup.text((add) => {
                            add.tspan(labelDisplayText(marker.label)).attr('style', font).attr("dy", "0.55em");
                        })
                        .addClass(`aprender-marker-${x2uid(cloned)}`)
                        .font({ fill: colour, anchor: "middle"})
                        .attr("dominant-baseline", "middle");
                    text.path(`M${x1},${y1} L${x2},${y2}`)
                        .attr("startOffset", "50%");
                } else if (marker.type === "edge") {
                    let colour = this.options.colourContext.strokes;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        colour = this.resolveColour(marker.colour ) as string;
                    }
                    let opacity = baseOpacity + ((1 - baseOpacity) / 2);
                    if ( ("opacity" in marker) && (marker.opacity !== undefined) ) {
                        opacity = marker.opacity;
                    }
                    const style = this.json.board.style;
                    if ( style.startsWith("vertex")|| style.startsWith("conhex") ) {
                        let xFrom = 0; let yFrom = 0;
                        let xTo = 0; let yTo = 0;
                        switch (marker.edge) {
                            case "N":
                                xFrom = grid[0][0].x;
                                yFrom = grid[0][0].y;
                                xTo = grid[0][grid[0].length - 1].x;
                                yTo = grid[0][grid[0].length - 1].y;
                                break;
                            case "E":
                                xFrom = grid[0][grid[0].length - 1].x;
                                yFrom = grid[0][grid[0].length - 1].y;
                                xTo = grid[grid.length - 1][grid[0].length - 1].x;
                                yTo = grid[grid.length - 1][grid[0].length - 1].y;
                                break;
                            case "S":
                                xFrom = grid[grid.length - 1][0].x;
                                yFrom = grid[grid.length - 1][0].y;
                                xTo = grid[grid.length - 1][grid[0].length - 1].x;
                                yTo = grid[grid.length - 1][grid[0].length - 1].y;
                                break;
                            case "W":
                                xFrom = grid[0][0].x;
                                yFrom = grid[0][0].y;
                                xTo = grid[grid.length - 1][0].x;
                                yTo = grid[grid.length - 1][0].y;
                                break;
                        }
                        targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                    } else if ( ( (style.startsWith("squares")) || (style === "sowing") || style === "pegboard" ) && (gridExpanded !== undefined) ) {
                        let xFrom = 0; let yFrom = 0;
                        let xTo = 0; let yTo = 0;
                        switch (marker.edge) {
                            case "N":
                                xFrom = gridExpanded[0][0].x;
                                yFrom = gridExpanded[0][0].y;
                                xTo = gridExpanded[0][gridExpanded[0].length - 1].x;
                                yTo = gridExpanded[0][gridExpanded[0].length - 1].y;
                                break;
                            case "E":
                                xFrom = gridExpanded[0][gridExpanded[0].length - 1].x;
                                yFrom = gridExpanded[0][gridExpanded[0].length - 1].y;
                                xTo = gridExpanded[gridExpanded.length - 1][gridExpanded[0].length - 1].x;
                                yTo = gridExpanded[gridExpanded.length - 1][gridExpanded[0].length - 1].y;
                                break;
                            case "S":
                                xFrom = gridExpanded[gridExpanded.length - 1][0].x;
                                yFrom = gridExpanded[gridExpanded.length - 1][0].y;
                                xTo = gridExpanded[gridExpanded.length - 1][gridExpanded[0].length - 1].x;
                                yTo = gridExpanded[gridExpanded.length - 1][gridExpanded[0].length - 1].y;
                                break;
                            case "W":
                                xFrom = gridExpanded[0][0].x;
                                yFrom = gridExpanded[0][0].y;
                                xTo = gridExpanded[gridExpanded.length - 1][0].x;
                                yTo = gridExpanded[gridExpanded.length - 1][0].y;
                                break;
                        }
                        targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                    } else if (style === "squares-diamonds" || style === "squares-diamonds-checkered") {
                        const xMin = Math.min(...grid.flat().flat().map(pt => pt.x));
                        const yMin = Math.min(...grid.flat().flat().map(pt => pt.y));
                        const xMax = Math.max(...grid.flat().flat().map(pt => pt.x));
                        const yMax = Math.max(...grid.flat().flat().map(pt => pt.y));
                        let xFrom = 0; let yFrom = 0;
                        let xTo = 0; let yTo = 0;
                        let buffer = (this.cellsize / 2) + (baseStroke * 3);
                        if ("sdStart" in this.json.board && this.json.board.sdStart === "D") {
                            buffer = this.cellsize * 0.25;
                        }
                        switch (marker.edge) {
                            case "N":
                                xFrom = xMin - buffer;
                                yFrom = yMin - buffer;
                                xTo = xMax + buffer;
                                yTo = yFrom;
                                break;
                            case "E":
                                xFrom = xMax + buffer;
                                yFrom = yMin - buffer;
                                xTo = xFrom;
                                yTo = yMax + buffer;
                                break;
                            case "S":
                                xFrom = xMin - buffer;
                                yFrom = yMax + buffer;
                                xTo = xMax + buffer;
                                yTo = yFrom;
                                break;
                            case "W":
                                xFrom = xMin - buffer;
                                yFrom = yMin - buffer;
                                xTo = xFrom;
                                yTo = yMax + buffer;
                                break;
                        }
                        targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                    } else if (style === "hex-of-tri") {
                        const midrow = Math.floor(grid.length / 2);
                        let xFrom = 0; let yFrom = 0;
                        let xTo = 0; let yTo = 0;
                        switch (marker.edge) {
                            case "N":
                                xFrom = grid[0][0].x;
                                yFrom = grid[0][0].y;
                                xTo = grid[0][grid[0].length - 1].x;
                                yTo = grid[0][grid[0].length - 1].y;
                                break;
                            case "NE":
                                xFrom = grid[0][grid[0].length - 1].x;
                                yFrom = grid[0][grid[0].length - 1].y;
                                xTo = grid[midrow][grid[midrow].length - 1].x;
                                yTo = grid[midrow][grid[midrow].length - 1].y;
                                break;
                            case "SE":
                                xFrom = grid[midrow][grid[midrow].length - 1].x;
                                yFrom = grid[midrow][grid[midrow].length - 1].y;
                                xTo = grid[grid.length - 1][grid[grid.length - 1].length - 1].x;
                                yTo = grid[grid.length - 1][grid[grid.length - 1].length - 1].y;
                                break;
                            case "S":
                                xFrom = grid[grid.length - 1][0].x;
                                yFrom = grid[grid.length - 1][0].y;
                                xTo = grid[grid.length - 1][grid[grid.length - 1].length - 1].x;
                                yTo = grid[grid.length - 1][grid[grid.length - 1].length - 1].y;
                                break;
                            case "SW":
                                xFrom = grid[grid.length - 1][0].x;
                                yFrom = grid[grid.length - 1][0].y;
                                xTo = grid[midrow][0].x;
                                yTo = grid[midrow][0].y;
                                break;
                            case "NW":
                                xFrom = grid[midrow][0].x;
                                yFrom = grid[midrow][0].y;
                                xTo = grid[0][0].x;
                                yTo = grid[0][0].y;
                                break;
                        }
                        targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                    } else if ( (style === "hex-of-hex") && (polys !== undefined) ) {
                        /*
                         * Polys is populated.
                         * Point 0 is the top point (pointy topped).
                         * Corners need to share edges.
                         * N: 5-0, 0-1
                         * NE: 0-1, 1-2
                         * SE: 1-2, 2-3
                         * S: 2-3, 3-4
                         * SW: 3-4, 4-5
                         * NW: 4-5, 5-0
                         */
                        const midrow = Math.floor(grid.length / 2);
                        let hexes: Poly[];
                        let idxs: [[number,number],[number,number]];
                        switch (marker.edge) {
                            case "N":
                                hexes = polys[0];
                                idxs = [[5,0],[0,1]];
                                break;
                            case "NE":
                                hexes = [];
                                for (let row = 0; row <= midrow; row++) {
                                    hexes.push(polys[row][polys[row].length - 1]);
                                }
                                idxs = [[0,1],[1,2]];
                                break;
                            case "SE":
                                hexes = [];
                                for (let row = midrow; row < grid.length; row++) {
                                    hexes.push(polys[row][polys[row].length - 1]);
                                }
                                idxs = [[1,2],[2,3]];
                                break;
                            case "S":
                                hexes = [...polys[polys.length - 1]];
                                hexes.reverse();
                                idxs = [[2,3],[3,4]];
                                break;
                            case "SW":
                                hexes = [];
                                for (let row = midrow; row < grid.length; row++) {
                                    hexes.push(polys[row][0]);
                                }
                                hexes.reverse();
                                idxs = [[3,4],[4,5]];
                                break;
                            case "NW":
                                hexes = [];
                                for (let row = 0; row <= midrow; row++) {
                                    hexes.push(polys[row][0]);
                                }
                                hexes.reverse();
                                idxs = [[4,5],[5,0]];
                                break;
                            default:
                                throw new Error(`(hex-of-hex edge markings) Invalid edge direction given: ${marker.edge as string}`);
                        }
                        const lines: [number,number,number,number][] = [];
                        for (let i = 0; i < hexes.length; i++) {
                            const hex = hexes[i] as IPolyPolygon;
                            const pt1 = hex.points[idxs[0][0]];
                            const pt2 = hex.points[idxs[0][1]];
                            const pt3 = hex.points[idxs[1][1]];
                            // first corner
                            if (i === 0) {
                                const midx = (pt1.x + pt2.x) / 2;
                                const midy = (pt1.y + pt2.y) / 2;
                                lines.push([midx, midy, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                            }
                            // last corner
                            else if (i === hexes.length - 1) {
                                const midx = (pt2.x + pt3.x) / 2;
                                const midy = (pt2.y + pt3.y) / 2;
                                lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, midx, midy]);
                            }
                            // everything in between
                            else {
                                lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                            }
                        }
                        for (const line of lines) {
                            targetGroup.line(...line).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                        }
                    } else if ( (style === "hex-slanted") && (polys !== undefined) ) {
                        /*
                         * Polys is populated.
                         * Point 0 is the top point (pointy topped).
                         * Corners need to share edges.
                         * N: 5-0, 0-1
                         * E: 0-1, 1-2
                         * S: 2-3, 3-4
                         * W: 3-4, 4-5
                         */
                        let hexes: Poly[];
                        let idxs: [[number,number],[number,number]];
                        switch (marker.edge) {
                            case "N":
                                hexes = polys[0];
                                idxs = [[5,0],[0,1]];
                                break;
                            case "E":
                                hexes = polys.map(p => p[p.length - 1]);
                                idxs = [[0,1],[1,2]];
                                break;
                            case "S":
                                hexes = [...polys[polys.length - 1]];
                                hexes.reverse();
                                idxs = [[2,3],[3,4]];
                                break;
                            case "W":
                                hexes = polys.map(p => p[0]);
                                hexes.reverse();
                                idxs = [[3,4],[4,5]];
                                break;
                            default:
                                throw new Error(`(hex-slanted edge markings) Invalid edge direction given: ${marker.edge as string}`);
                        }
                        const lines: [number,number,number,number][] = [];
                        for (let i = 0; i < hexes.length; i++) {
                            const hex = hexes[i] as IPolyPolygon;
                            const pt1 = hex.points[idxs[0][0]];
                            const pt2 = hex.points[idxs[0][1]];
                            const pt3 = hex.points[idxs[1][1]];
                            // top right N
                            if ( (marker.edge === "N") && (i === hexes.length - 1) ) {
                                const midx = (pt2.x + pt3.x) / 2;
                                const midy = (pt2.y + pt3.y) / 2;
                                lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, midx, midy]);
                            }
                            // top right E
                            else if ( (marker.edge === "E") && (i === 0) ) {
                                const midx = (pt1.x + pt2.x) / 2;
                                const midy = (pt1.y + pt2.y) / 2;
                                lines.push([midx, midy, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                            }
                            // bottom left S
                            else if ( (marker.edge === "S") && (i === hexes.length - 1) ) {
                                const midx = (pt2.x + pt3.x) / 2;
                                const midy = (pt2.y + pt3.y) / 2;
                                lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, midx, midy]);
                            }
                            // bottom left W
                            else if ( (marker.edge === "W") && (i === 0) ) {
                                const midx = (pt1.x + pt2.x) / 2;
                                const midy = (pt1.y + pt2.y) / 2;
                                lines.push([midx, midy, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                            }
                            // everything else
                            else {
                                lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                            }
                        }
                        for (const line of lines) {
                            targetGroup.line(...line).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                        }
                    } else if (style === "cairo-catalan" && polys !== undefined) {
                        const lines: [number,number,number,number][] = [];
                        if (marker.edge === "N") {
                            for (let i = 0; i < polys[0].length; i++) {
                                const poly = polys[0][i] as IPolyPolygon;
                                if (i % 2 === 0) {
                                    const pt1 = poly.points[0];
                                    const pt2 = poly.points[1];
                                    const pt3 = poly.points[2];
                                    lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                    lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                                } else {
                                    const pt1 = poly.points[0];
                                    const pt2 = poly.points[1];
                                    lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                }
                            }
                        } else if (marker.edge === "S") {
                            for (let i = 0; i < polys[polys.length - 1].length; i++) {
                                const poly = polys[polys.length - 1][i] as IPolyPolygon;
                                if (polys.length % 2 === 0) {
                                    if (i % 2 === 0) {
                                        const pt1 = poly.points[2];
                                        const pt2 = poly.points[3];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                    } else {
                                        const pt1 = poly.points[2];
                                        const pt2 = poly.points[3];
                                        const pt3 = poly.points[4];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                        lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                                    }
                                } else {
                                    if (i % 2 === 0) {
                                        const pt1 = poly.points[3];
                                        const pt2 = poly.points[4];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                    } else {
                                        const pt1 = poly.points[2];
                                        const pt2 = poly.points[3];
                                        const pt3 = poly.points[4];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                        lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                                    }
                                }
                            }
                        } else if (marker.edge === "W") {
                            for (let i = 0; i < polys.length; i++) {
                                const poly = polys[i][0] as IPolyPolygon;
                                if (i % 2 === 0) {
                                    const pt1 = poly.points[4];
                                    const pt2 = poly.points[0];
                                    lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                } else {
                                    const pt1 = poly.points[0];
                                    const pt2 = poly.points[4];
                                    const pt3 = poly.points[3];
                                    lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                    lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                                }
                            }
                        } else {
                            for (let i = 0; i < polys.length; i++) {
                                const rowWidth = polys[i].length;
                                const poly = polys[i][rowWidth - 1] as IPolyPolygon;
                                if (rowWidth % 2 === 0) {
                                    if (i % 2 === 0) {
                                        const pt1 = poly.points[1];
                                        const pt2 = poly.points[2];
                                        const pt3 = poly.points[3];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                        lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                                    } else {
                                        const pt1 = poly.points[1];
                                        const pt2 = poly.points[2];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                    }
                                } else {
                                    if (i % 2 === 0) {
                                        const pt1 = poly.points[1];
                                        const pt2 = poly.points[2];
                                        const pt3 = poly.points[3];
                                        if (i !== 0) {
                                            lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                        }
                                        lines.push([pt2.x, pt2.y, pt3.x, pt3.y]);
                                    } else {
                                        const pt1 = poly.points[1];
                                        const pt2 = poly.points[2];
                                        lines.push([pt1.x, pt1.y, pt2.x, pt2.y]);
                                    }
                                }
                            }
                        }
                        for (const line of lines) {
                            targetGroup.line(...line).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                        }
                    } else if (style === "cairo-collinear" && polys !== undefined) {
                        const lines: [number,number,number,number][] = [];
                        let startOrientation: "H"|"V" = "H";
                        if (opts.cairoStart !== undefined) {
                            startOrientation = opts.cairoStart;
                        }
                        const orientations: ("H"|"V")[] = [startOrientation];
                        if (startOrientation === "H") {
                            orientations.push("V");
                        } else {
                            orientations.push("H");
                        }

                        if (marker.edge === "N") {
                            const row = polys[0] as IPolyPolygon[];
                            for (let i = 0; i < row.length / 2; i++) {
                                const poly1 = row[i * 2];
                                const poly2 = row[(i * 2) + 1];
                                const orientation = orientations[i % 2];
                                if (orientation === "H") {
                                    lines.push([poly1.points[1].x, poly1.points[1].y, poly1.points[2].x, poly1.points[2].y]);
                                    lines.push([poly2.points[3].x, poly2.points[3].y, poly2.points[4].x, poly2.points[4].y])
                                } else {
                                    lines.push([poly1.points[4].x, poly1.points[4].y, poly1.points[0].x, poly1.points[0].y]);
                                    lines.push([poly1.points[0].x, poly1.points[0].y, poly1.points[1].x, poly1.points[1].y]);
                                }
                            }
                        } else if (marker.edge === "S") {
                            if (polys.length % 2 === 0) {
                                orientations.reverse();
                            }
                            const row = polys[polys.length - 1] as IPolyPolygon[];
                            for (let i = 0; i < row.length / 2; i++) {
                                const poly1 = row[i * 2];
                                const poly2 = row[(i * 2) + 1];
                                const orientation = orientations[i % 2];
                                if (orientation === "H") {
                                    lines.push([poly1.points[3].x, poly1.points[3].y, poly1.points[4].x, poly1.points[4].y]);
                                    lines.push([poly2.points[1].x, poly2.points[1].y, poly2.points[2].x, poly2.points[2].y])
                                } else {
                                    lines.push([poly2.points[4].x, poly2.points[4].y, poly2.points[0].x, poly2.points[0].y]);
                                    lines.push([poly2.points[0].x, poly2.points[0].y, poly2.points[1].x, poly2.points[1].y]);
                                }
                            }
                        } else if (marker.edge === "W") {
                            for (let i = 0; i < polys.length; i++) {
                                const poly1 = polys[i][0] as IPolyPolygon;
                                const poly2 = polys[i][1] as IPolyPolygon;
                                const orientation = orientations[i % 2];
                                if (orientation === "H") {
                                    lines.push([poly1.points[1].x, poly1.points[1].y, poly1.points[0].x, poly1.points[0].y]);
                                    lines.push([poly1.points[0].x, poly1.points[0].y, poly1.points[4].x, poly1.points[4].y]);
                                } else {
                                    lines.push([poly1.points[3].x, poly1.points[3].y, poly1.points[4].x, poly1.points[4].y]);
                                    lines.push([poly2.points[1].x, poly2.points[1].y, poly2.points[2].x, poly2.points[2].y])
                                }
                            }
                        } else {
                            if (Math.floor(polys[0].length / 2) % 2 === 0) {
                                orientations.reverse();
                            }
                            for (let i = 0; i < polys.length; i++) {
                                const poly1 = polys[i][polys[i].length - 1] as IPolyPolygon;
                                const poly2 = polys[i][polys[i].length - 2] as IPolyPolygon;
                                const orientation = orientations[i % 2];
                                if (orientation === "H") {
                                    lines.push([poly1.points[1].x, poly1.points[1].y, poly1.points[0].x, poly1.points[0].y]);
                                    lines.push([poly1.points[0].x, poly1.points[0].y, poly1.points[4].x, poly1.points[4].y]);
                                } else {
                                    lines.push([poly1.points[3].x, poly1.points[3].y, poly1.points[4].x, poly1.points[4].y]);
                                    lines.push([poly2.points[1].x, poly2.points[1].y, poly2.points[2].x, poly2.points[2].y]);
                                }
                            }
                        }

                        for (const line of lines) {
                            targetGroup.line(...line).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                        }
                    } else if (style === "dvgc") {
                        let xFrom = 0; let yFrom = 0;
                        let xTo = 0; let yTo = 0;
                        switch (marker.edge) {
                            case "N":
                                xFrom = 0;
                                yFrom = 0;
                                xTo = 450;
                                yTo = 0;
                                break;
                            case "S":
                                xFrom = 0;
                                yFrom = 300;
                                xTo = 450;
                                yTo = 300;
                                break;
                            default:
                                throw new Error(`The dvgc board can only mark N and S edges.`);
                        }
                        targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"});
                    } else if (style === "snubsquare" || style === "onyx") {
                        let realgrid = grid;
                        if (style === "onyx") {
                            realgrid = grid.reduce((prev, curr, idx) => idx % 2 === 0 ? [...prev, curr] : [...prev], [] as GridPoints);
                        }
                        const pts: IPoint[] = [];
                        switch (marker.edge) {
                            case "N":
                                pts.push(...realgrid[0]);
                                break;
                            case "S":
                                pts.push(...realgrid[realgrid.length - 1]);
                                break;
                            case "W":
                                pts.push(...realgrid.map(row => row[0]));
                                break;
                            case "E":
                                pts.push(...realgrid.map(row => row[row.length - 1]))
                        }
                        targetGroup.polyline(pts.map(pt => `${pt.x},${pt.y}`).join(" ")).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"}).fill("none");
                    } else if (style === "snubsquare-cells" && opts.gridExpanded !== undefined) {
                        const realgrid = opts.gridExpanded;
                        const pts: IPoint[] = [];
                        switch (marker.edge) {
                            case "N":
                                pts.push(...realgrid[0]);
                                break;
                            case "S":
                                pts.push(...realgrid[realgrid.length - 1]);
                                break;
                            case "W":
                                pts.push(...realgrid.map(row => row[0]));
                                break;
                            case "E":
                                pts.push(...realgrid.map(row => row[row.length - 1]))
                        }
                        targetGroup.polyline(pts.map(pt => `${pt.x},${pt.y}`).join(" ")).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"}).fill("none");
                    } else if (boardEdges !== undefined && boardEdges.has(marker.edge)) {
                        const pts: IPoint[] = boardEdges.get(marker.edge)!.points;
                        targetGroup.polyline(pts.map(pt => `${pt.x},${pt.y}`).join(" ")).addClass(`aprender-marker-${x2uid(cloned)}`).stroke({width: baseStroke * 3, color: colour, opacity, linecap: "round", linejoin: "round"}).fill("none");
                    }
                } else if (marker.type === "fence") {
                    let colour = this.options.colourContext.strokes;
                    if ( ("colour" in marker) && (marker.colour !== undefined) ) {
                        colour = this.resolveColour(marker.colour ) as string;
                    }
                    let multiplier = 6;
                    if ( ("width" in marker) && (marker.width !== undefined) ) {
                        multiplier = marker.width;
                    }
                    const stroke: StrokeData = {
                        color: colour,
                        width: baseStroke * multiplier,
                        linecap: "round",
                        linejoin: "round",
                    };
                    if ( ("dashed" in marker) && (marker.dashed !== undefined) && (Array.isArray(marker.dashed)) && (marker.dashed.length > 0) ) {
                        stroke.dasharray = (marker.dashed ).join(" ");
                    }
                    const style = this.json.board.style;
                    if ( (style.startsWith("squares") || style === "pegboard") && (gridExpanded !== undefined) ) {
                        if (!("row" in marker.cell) || !("col" in marker.cell)
                            || marker.cell.row === undefined || marker.cell.col === undefined) {
                            throw new Error("Fence markers require cell row/col coordinates.");
                        }
                        const row = marker.cell.row;
                        const col = marker.cell.col;
                        let xFrom = 0; let yFrom = 0;
                        let xTo = 0; let yTo = 0;
                        const cell = gridExpanded[row][col];
                        const east = gridExpanded[row][col + 1];
                        const southeast = gridExpanded[row + 1][col + 1];
                        const south = gridExpanded[row + 1][col];
                        switch (marker.side) {
                            case "N":
                                xFrom = cell.x;
                                yFrom = cell.y;
                                xTo = east.x;
                                yTo = east.y;
                                break;
                            case "E":
                                xFrom = east.x;
                                yFrom = east.y;
                                xTo = southeast.x;
                                yTo = southeast.y;
                                break;
                            case "S":
                                xFrom = south.x;
                                yFrom = south.y;
                                xTo = southeast.x;
                                yTo = southeast.y;
                                break;
                            case "W":
                                xFrom = cell.x;
                                yFrom = cell.y;
                                xTo = south.x;
                                yTo = south.y;
                                break;
                        }
                        if ("shorten" in marker && marker.shorten !== undefined && marker.shorten > 0) {
                            [xFrom, yFrom, xTo, yTo] = shortenLine(xFrom, yFrom, xTo, yTo, marker.shorten);
                        }
                        const newclone = {...cloned};
                        // @ts-expect-error (only used to generate UUID)
                        delete newclone.cell;
                        // @ts-expect-error (only used to gnerate UUID)
                        delete newclone.side;
                        targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(newclone)}`).stroke(stroke).attr({ 'pointer-events': 'none' });
                    } else if ( (hexGrid !== undefined) && (hexWidth !== undefined) && (hexHeight !== undefined) && ( (style.startsWith("hex-odd")) || (style.startsWith("hex-even")) ) ) {
                        if (!("row" in marker.cell) || !("col" in marker.cell)
                            || marker.cell.row === undefined || marker.cell.col === undefined) {
                            throw new Error("Fence markers require cell row/col coordinates.");
                        }
                        const row = marker.cell.row;
                        const col = marker.cell.col;
                        const hex = hexGrid.getHex({col, row});
                        if (hex !== undefined) {
                            const side = marker.side as CompassDirection;
                            const edges = edges2corners.get(hex.orientation)!;
                            const edge = edges.find(e => e.dir === side);
                            if (edge !== undefined) {
                                const [idx1, idx2] = edge.corners;
                                let xFrom = hex.corners[idx1].x;
                                let yFrom = hex.corners[idx1].y;
                                let xTo = hex.corners[idx2].x;
                                let yTo = hex.corners[idx2].y;
                                if ("shorten" in marker && marker.shorten !== undefined && marker.shorten > 0) {
                                    [xFrom, yFrom, xTo, yTo] = shortenLine(xFrom, yFrom, xTo, yTo, marker.shorten);
                                }
                                targetGroup.line(xFrom, yFrom, xTo, yTo).addClass(`aprender-marker-${x2uid(cloned)}`).stroke(stroke).attr({ 'pointer-events': 'none' });
                            }
                        }
                    }
                } else if (marker.type === "glyph") {
                    const key = marker.glyph;
                    const piece = svgGroup.root().findOne("#" + key) as Svg;
                    if ( (piece === null) || (piece === undefined) ) {
                        throw new Error(`Could not find the requested piece (${key}). Each piece in the \`pieces\` property *must* exist in the \`legend\`.`);
                    }
                    for (const pt of marker.points as ITarget[]) {
                        const point = grid[pt.row][pt.col];
                        const use = usePieceAt({svg: targetGroup, piece, cellsize: this.cellsize, x: point.x, y: point.y, scalingFactor: 1});
                        use.attr({ 'pointer-events': 'none' });
                        // if (this.options.rotate && this.json.options && this.json.options.includes('rotate-pieces')) {
                        //     rotate(use, this.options.rotate, point.x, point.y);
                        // }
                    }
                }
            }
        }
    }

    /**
     * An internal helper function for generating `eject` annotations.
     * This is not generalized. It only assumes we are rotating in increments of 45 degrees.
     *
     * @param from - Starting point
     * @param to - Ending point
     * @param delta - The depth of the arc
     * @returns The midpoint of the arc
     */
    public getArcCentre(from: IPoint, to: IPoint, delta: number): IPoint {
        const m: IPoint = {x: (from.x + to.x) / 2, y: (from.y + to.y) / 2};
        let dir = "";
        if (to.y < from.y) {
            dir = "N";
        } else if (to.y > from.y) {
            dir = "S";
        }
        if (to.x < from.x) {
            dir += "W";
        } else if (to.x > from.x) {
            dir += "E";
        }
        switch (dir) {
            case "N":
            case "S":
            case "NE":
            case "SW":
                return {x: m.x + delta, y: m.y};
            case "E":
            case "W":
            case "NW":
            case "SE":
                return {x: m.x, y: m.y + delta};
            default:
                throw new Error(`Unrecognized direction ${dir}`);
        }
    }

    /**
     * Gets a list of column labels.
     *
     * @param arg1 - If alone, this is the number of labels you want starting from 0. Otherwise it's the starting point for the list.
     * @param arg2 - If provided, this is the number of labels you want, starting from nth label from the first argument.
     * @returns A list of labels
     */
    public getLabels(override: string[]|undefined, arg1: number, arg2?: number) : string[] {
        if (override !== undefined) {
            return [...override];
        }
        let start = 0;
        let count = 0;
        if (arg2 === undefined) {
            count = arg1;
        } else {
            start = arg1;
            count = arg2;
        }
        const it = generateColumnLabel(this.options.columnLabels);
        const labels: string[] = [];
        // prime the iterator to get to the start value
        for (let i = 0; i < start; i++) {
            it.next();
        }
        for (let i = 0; i < count; i++) {
            labels.push(it.next().value as string);
        }
        return labels;
    }

    public getRowLabels(override: unknown, height: number) : string[] {
        if (override !== undefined) {
            return ([...(override as string[])]).reverse();
        }
        const rowLabels: string[] = [];
        for (let row = 0; row < height; row++) {
            rowLabels.push((height - row).toString());
        }
        return rowLabels;
    }

    /** Default row/col coordinate label size on most boards (legacy implicit SVG ~16px). */
    private static readonly COORD_LABEL_DEFAULT_FONT_SIZE = 16;

    private static readonly RECT_OF_HEX_BOARD_STYLES = new Set([
        "hex-odd-p",
        "hex-even-p",
        "hex-odd-f",
        "hex-even-f",
    ]);

    /** Legacy rect-of-hex boards used `cellsize / 5`; other row/col boards used ~16px. */
    private coordinateLabelUsesCellsizeFifthBase(): boolean {
        const board = this.json?.board;
        if (board === null || board === undefined || !("style" in board)) {
            return false;
        }
        const style = board.style;
        return (
            style !== undefined &&
            RendererBase.RECT_OF_HEX_BOARD_STYLES.has(style)
        );
    }

    public coordinateLabelBaseFontSize(): number {
        if (this.coordinateLabelUsesCellsizeFifthBase()) {
            return this.cellsize / 5;
        }
        return RendererBase.COORD_LABEL_DEFAULT_FONT_SIZE;
    }

    public boardLabelFontSize(): number {
        let scale = 1;
        const board = this.json?.board;
        if (
            board !== null &&
            board !== undefined &&
            "labelScale" in board &&
            typeof board.labelScale === "number"
        ) {
            scale = board.labelScale;
        }
        return this.coordinateLabelBaseFontSize() * scale;
    }

    /** Multiplier for outward coordinate-label gap (`board.labelOutset`, default 1). */
    public labelOutsetMultiplier(): number {
        const board = this.json?.board;
        if (
            board !== null &&
            board !== undefined &&
            "labelOutset" in board &&
            typeof board.labelOutset === "number" &&
            board.labelOutset >= 0
        ) {
            return board.labelOutset;
        }
        return 1;
    }

    /**
     * Scale a board style's base outward label gap (in px) by {@link labelOutsetMultiplier}.
     * The first half of the default gap reaches the playfield edge from the cell centre; only the
     * remainder is scaled so `labelOutset: 0` keeps labels on the margin, not on the cells.
     */
    public scaleCoordinateLabelGap(gap: number): number {
        const m = this.labelOutsetMultiplier();
        if (m === 1) {
            return gap;
        }
        const minGap = gap * 0.5 + this.boardLabelFontSize() * 0.5;
        if (m <= 1) {
            return minGap + (gap - minGap) * m;
        }
        return gap * m;
    }

    protected haloStyleSupported(style: string): boolean {
        return style.startsWith("circular")
            || style.startsWith("conical-hex")
            || style.startsWith("hex-of")
            || style === "bricka";
    }

    public applyCoordinateLabelStyle(
        el: SVGText,
        labelColour: string,
        labelOpacity: number,
    ): SVGText {
        return el.font({
            anchor: "middle",
            size: this.boardLabelFontSize(),
            fill: labelColour,
            opacity: labelOpacity,
        });
    }

    /**
     * An internal helper function for producing labels for hex fields.
     *
     * @param x - The column number
     * @param y - The row number
     * @param height - The total height of the field
     * @returns A string label for the hex
     */
    protected coords2algebraicHex(columnLabels: string[]|undefined, x: number, y: number, height: number): string {
        const [label] = this.getLabels(columnLabels, height - y - 1, 1);
        return label + (x + 1).toString();
    }

    /**
     * Dispatch board style rendering for the current `json.board` into `rootSvg` (or a nested SVG when embedding).
     */
    protected dispatchBoardRender(targetSvg?: Svg): BoardReturn {
        if (this.json === undefined || this.rootSvg === undefined) {
            throw new Error("Invalid object state.");
        }
        if (this.json.board === null || !("style" in this.json.board) || this.json.board.style === undefined) {
            throw new Error("Board style required for dispatchBoardRender.");
        }
        const savedRoot = this.rootSvg;
        if (targetSvg !== undefined) {
            this.rootSvg = targetSvg;
        }
        const savedCellsize = this.cellsize;
        let gridPoints: GridPoints;
        let polys: Poly[][] | undefined;
        let boardFill: Poly | undefined;
        try {
            switch (this.json.board.style) {
                case "squares-beveled":
                case "squares-checkered":
                case "squares":
                case "pegboard":
                    ({ grid: gridPoints, polys, boardFill } = squares(this));
                    break;
                case "squares-diamonds":
                case "squares-diamonds-checkered":
                    ({ grid: gridPoints, polys, boardFill } = squaresDiamonds(this));
                    break;
                case "squares-stacked":
                    ({ grid: gridPoints, boardFill } = squaresStacked(this));
                    break;
                case "vertex":
                case "vertex-cross":
                case "vertex-fanorona":
                    ({ grid: gridPoints, boardFill } = vertex(this));
                    break;
                case "snubsquare":
                    ({ grid: gridPoints, boardFill } = snubSquare(this));
                    break;
                case "onyx":
                    ({ grid: gridPoints, boardFill } = onyx(this));
                    break;
                case "bricka":
                    ({ grid: gridPoints, polys, boardFill } = bricka(this));
                    break;
                case "snubsquare-cells":
                    ({ grid: gridPoints, polys, boardFill } = snubSquareCells(this));
                    break;
                case "pentagonal":
                case "pentagonal-bluestone":
                    ({ grid: gridPoints, boardFill } = pentagonal(this));
                    break;
                case "bent-tri":
                    ({ grid: gridPoints, boardFill } = bentTri(this));
                    break;
                case "star":
                    ({ grid: gridPoints, boardFill } = star(this));
                    break;
                case "hex-of-hex":
                    ({ grid: gridPoints, polys, boardFill } = hexOfHex(this));
                    break;
                case "hex-of-tri":
                    ({ grid: gridPoints, boardFill } = hexOfTri(this));
                    break;
                case "hex-of-tri-f":
                    ({ grid: gridPoints, polys, boardFill } = hexOfTriF(this));
                    break;
                case "hex-of-cir":
                    ({ grid: gridPoints, polys, boardFill } = hexOfCir(this));
                    break;
                case "rect-of-tri":
                    ({ grid: gridPoints, boardFill } = rectOfTri(this));
                    break;
                case "hex-slanted":
                    ({ grid: gridPoints, polys, boardFill } = hexSlanted(this));
                    break;
                case "hex-odd-p":
                case "hex-even-p":
                case "hex-odd-f":
                case "hex-even-f":
                    ({ grid: gridPoints, polys, boardFill } = rectOfHex(this));
                    break;
                case "triangles-stacked": {
                    ({ grid: gridPoints, polys, boardFill } = stackingTriangles(this));
                    break;
                }
                case "circular-cobweb":
                    ({ grid: gridPoints, polys, boardFill } = cobweb(this));
                    break;
                case "circular-wheel":
                case "circular-wheel-spaces":
                case "circular-wheel-vertices":
                case "circular-wheel-spaces-checkered":
                    ({ grid: gridPoints, polys, boardFill } = wheel(this));
                    break;
                case "sowing":
                    ({ grid: gridPoints, boardFill } = sowing(this));
                    break;
                case "conhex-cells":
                    ({ grid: gridPoints, polys, boardFill } = conhex(this));
                    break;
                case "cairo-collinear":
                    ({ grid: gridPoints, polys, boardFill } = cairoCollinear(this));
                    break;
                case "cairo-catalan":
                    ({ grid: gridPoints, polys, boardFill } = cairoCatalan(this));
                    break;
                case "conical-hex":
                case "conical-hex-narrow":
                    ({ grid: gridPoints, polys, boardFill } = conicalHex(this));
                    break;
                case "pyramid-hex":
                    ({ grid: gridPoints, polys, boardFill } = pyramidHex(this));
                    break;
                case "dvgc":
                case "dvgc-checkered":
                    ({ grid: gridPoints, polys, boardFill } = dvgc(this));
                    break;
                case "circular-moon":
                    this.cellsize = 15;
                    ({ grid: gridPoints, polys, boardFill } = moon(this));
                    break;
                case "fractured-flat":
                    this.cellsize = 40;
                    ({ grid: gridPoints, polys, boardFill } = fracturedFlat(this));
                    break;
                case "eleven":
                    ({ grid: gridPoints, polys, boardFill } = eleven(this));
                    break;
                default:
                    throw new Error(`The requested board style (${ this.json.board.style }) is not yet supported by the default renderer.`);
            }
            if (targetSvg === undefined) {
                this.mainBoardPolys = polys;
            }
            return { grid: gridPoints, polys, boardFill };
        } finally {
            this.rootSvg = savedRoot;
            // Embedded boards (tracks) must restore cellsize; main-board styles like
            // circular-moon and fractured-flat adjust cellsize for the whole render.
            if (targetSvg !== undefined) {
                this.cellsize = savedCellsize;
            }
        }
    }

    protected parseBoardPieces(
        pieces: APRenderRep["pieces"] | NonNullable<AreaTrack["pieces"]>,
        boardWidth: number,
    ): string[][][] {
        if (pieces === null) {
            return [];
        }
        if (typeof pieces === "string") {
            const parsed: string[][][] = [];
            if (pieces.indexOf(",") >= 0) {
                for (const row of pieces.split("\n")) {
                    let node: string[][];
                    if (row === "_") {
                        node = new Array(boardWidth).fill([]) as string[][];
                    } else {
                        let cells = row.split(",");
                        cells = cells.map((x) => { if (x === "") {return "-"; } else {return x; } });
                        node = cells.map((x) => [x]);
                    }
                    parsed.push(node);
                }
            } else {
                for (const row of pieces.split("\n")) {
                    let node: string[][];
                    if (row === "_") {
                        node = new Array(boardWidth).fill([]) as string[][];
                    } else {
                        const cells = row.split("");
                        node = cells.map((x) => [x]);
                    }
                    parsed.push(node);
                }
            }
            return parsed;
        }
        if ( (pieces instanceof Array) && (pieces[0] instanceof Array) && (pieces[0][0] instanceof Array) ) {
            return pieces as string[][][];
        }
        throw new Error("Unrecognized `pieces` property.");
    }

    protected placeBoardGridPieces(
        gridPoints: GridPoints,
        pieces: string[][][],
        group: SVGG,
        boardStyle: string,
        polys?: Poly[][],
        opts?: { clickable?: boolean },
    ): void {
        if (this.rootSvg === undefined) {
            throw new Error("Invalid object state.");
        }
        const clickable = opts?.clickable ?? (
            this.options.boardClick !== undefined
            && (this.json?.options === undefined || !this.json.options.includes("no-piece-click"))
        );
        for (let row = 0; row < pieces.length; row++) {
            for (let col = 0; col < pieces[row].length; col++) {
                for (const key of pieces[row][col]) {
                    if ( (key !== null) && (key !== "-") ) {
                        let point: IPoint|undefined;
                        if (row >= gridPoints.length || col >= gridPoints[row].length) {
                            if (boardStyle === "squares-stacked") {
                                point = this.getStackedPoint(gridPoints, col, row);
                                if (point === undefined) {
                                    continue;
                                }
                            } else if (boardStyle === "triangles-stacked" && polys !== undefined) {
                                point = this.getTriStackedPoint(gridPoints, col, row, polys);
                                if (point === undefined) {
                                    continue;
                                }
                            } else {
                                continue;
                            }
                        } else {
                            point = gridPoints[row][col];
                        }
                        const piece = this.rootSvg.findOne("#" + key) as Svg;
                        if ( (piece === null) || (piece === undefined) ) {
                            throw new Error(`Could not find the requested piece (${key}). Each piece in the \`pieces\` property *must* exist in the \`legend\`.`);
                        }
                        const factor = 0.85;
                        const use = usePieceAt({svg: group, piece, cellsize: this.cellsize, x: point.x, y: point.y, scalingFactor: factor});
                        if (clickable && boardStyle !== "squares-stacked") {
                            use.click((e : Event) => {this.options.boardClick!(row, col, key); e.stopPropagation(); });
                        } else {
                            use.attr({"pointer-events": "none"});
                        }
                    }
                }
            }
        }
    }

    protected buildTrack(areaIndex: number, track: AreaTrack): Svg {
        if (this.json === undefined || this.rootSvg === undefined) {
            throw new Error("Invalid object state.");
        }
        const mainRoot = this.rootSvg;
        const nested = mainRoot.defs().nested().id(`_track_${areaIndex}`);
        const savedBoard = this.json.board;
        const savedOptions = this.json.options;
        const savedBoardClick = this.options.boardClick;
        const savedCellsize = this.cellsize;
        this.json.board = track.board;
        if (track.labels !== true) {
            const opts = savedOptions !== undefined ? [...savedOptions] : [];
            if (!opts.includes("hide-labels")) {
                opts.push("hide-labels");
            }
            this.json.options = opts;
        }
        this.options.boardClick = undefined;
        let gridPoints: GridPoints;
        let polys: Poly[][] | undefined;
        try {
            ({ grid: gridPoints, polys } = this.dispatchBoardRender(nested));
        } finally {
            this.json.board = savedBoard;
            this.json.options = savedOptions;
            this.options.boardClick = savedBoardClick;
            this.cellsize = savedCellsize;
        }
        const trackBoard = nested.findOne("#board") as SVGG|null;
        if (trackBoard === null) {
            throw new Error("Track board group was not created.");
        }
        const group = trackBoard.group().id("pieces");
        if (track.pieces !== undefined && track.pieces !== null) {
            const boardWidth = ("width" in track.board && track.board.width !== undefined) ? track.board.width : 0;
            const pieces = this.parseBoardPieces(track.pieces, boardWidth);
            this.placeBoardGridPieces(gridPoints, pieces, group, track.board.style, polys, { clickable: false });
        }
        if (track.annotations !== undefined && track.annotations.length > 0) {
            this.annotateBoard(gridPoints, polys, {
                annotations: track.annotations,
                notesParent: trackBoard,
            });
        }
        const viewbox = this.trackBoardViewbox(trackBoard, gridPoints, polys, savedCellsize);
        nested.viewbox(viewbox.x, viewbox.y, viewbox.width, viewbox.height);
        return nested;
    }

    /**
     * Track boards live in `defs`; Firefox often returns an empty `bbox()` there.
     * Compute the viewBox from geometry like `buildKey` / `buildButtonBar` do.
     */
    protected trackBoardViewbox(
        trackBoard: SVGG,
        gridPoints: GridPoints,
        polys: Poly[][] | undefined,
        cellsize: number,
    ): { x: number; y: number; width: number; height: number } {
        const polyExtent = polys !== undefined && polys.length > 0
            ? playfieldExtentFromPolys(polys)
            : null;
        if (polyExtent !== null && polyExtent.width > 0 && polyExtent.height > 0) {
            return polyExtent;
        }
        const boardBbox = trackBoard.bbox();
        if (boardBbox.width > 0 && boardBbox.height > 0) {
            return { x: boardBbox.x, y: boardBbox.y, width: boardBbox.width, height: boardBbox.height };
        }
        const flat = gridPoints.flat();
        const half = cellsize / 2;
        const minX = Math.min(...flat.map((p) => p.x)) - half;
        const maxX = Math.max(...flat.map((p) => p.x)) + half;
        const minY = Math.min(...flat.map((p) => p.y)) - half;
        const maxY = Math.max(...flat.map((p) => p.y)) + half;
        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }

    /**
     * Place `track` areas in declaration order. Tracks do not rotate with the main board.
     */
    protected placeTrackAreas(box: SVGBox): SVGBox {
        if (this.json === undefined || this.rootSvg === undefined) {
            throw new Error("Invalid object state.");
        }
        if (this.json.areas === undefined || this.json.areas.length === 0) {
            return box;
        }
        const tracks: { area: AreaTrack; index: number }[] = [];
        for (let i = 0; i < this.json.areas.length; i++) {
            const area = this.json.areas[i];
            if (area.type === "track") {
                tracks.push({ area, index: i });
            }
        }
        if (tracks.length === 0) {
            return box;
        }
        const playfield = computePlayfieldMetrics(this.rootSvg, this.cellsize, this.mainBoardPolys);
        const mainWidthCells = (this.json.board !== null && "width" in this.json.board && this.json.board.width !== undefined)
            ? this.json.board.width
            : Math.round(playfield.width / this.cellsize);
        const mainHeightCells = (this.json.board !== null && "height" in this.json.board && this.json.board.height !== undefined)
            ? this.json.board.height
            : Math.round(playfield.height / this.cellsize);
        const padding = this.cellsize;
        const offsets = { top: 0, bottom: 0, left: 0, right: 0 };
        let minX = box.x;
        let minY = box.y;
        let maxX = box.x2;
        let maxY = box.y2;
        const tableau = ensureTableau(this.rootSvg);
        for (const { area, index } of tracks) {
            const nested = this.buildTrack(index, area);
            const naturalW = nested.viewbox().w;
            const naturalH = nested.viewbox().h;
            const pos = area.position ?? "bottom";
            let displayW = naturalW;
            let displayH = naturalH;
            if (pos === "left" || pos === "right") {
                const targetHeightCells = area.width ?? mainHeightCells;
                const targetHeightPx = targetHeightCells * this.cellsize;
                displayH = targetHeightPx;
                displayW = naturalW * (targetHeightPx / naturalH);
            } else {
                const targetWidthCells = area.width ?? mainWidthCells;
                const targetWidthPx = targetWidthCells * this.cellsize;
                displayW = targetWidthPx;
                displayH = naturalH * (targetWidthPx / naturalW);
            }
            let x = 0;
            let y = 0;
            const alignTrackX = (targetWidthPx: number): number => {
                if (targetWidthPx === playfield.width) {
                    return playfield.x;
                }
                return playfield.x + (playfield.width - displayW) / 2;
            };
            const alignTrackY = (targetHeightPx: number): number => {
                if (targetHeightPx === playfield.height) {
                    return playfield.y;
                }
                return playfield.y + (playfield.height - displayH) / 2;
            };
            switch (pos) {
                case "bottom": {
                    const targetWidthPx = (area.width ?? mainWidthCells) * this.cellsize;
                    x = alignTrackX(targetWidthPx);
                    y = playfield.y + playfield.height + padding + offsets.bottom;
                    offsets.bottom += displayH + padding;
                    break;
                }
                case "top": {
                    const targetWidthPx = (area.width ?? mainWidthCells) * this.cellsize;
                    x = alignTrackX(targetWidthPx);
                    y = playfield.y - padding - displayH - offsets.top;
                    offsets.top += displayH + padding;
                    break;
                }
                case "left": {
                    const targetHeightPx = (area.width ?? mainHeightCells) * this.cellsize;
                    x = playfield.x - padding - displayW - offsets.left;
                    y = alignTrackY(targetHeightPx);
                    offsets.left += displayW + padding;
                    break;
                }
                case "right": {
                    const targetHeightPx = (area.width ?? mainHeightCells) * this.cellsize;
                    x = playfield.x + playfield.width + padding + offsets.right;
                    y = alignTrackY(targetHeightPx);
                    offsets.right += displayW + padding;
                    break;
                }
            }
            tableau.use(nested).size(displayW, displayH).move(x, y);
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x + displayW);
            maxY = Math.max(maxY, y + displayH);
        }
        return new SVGBox(minX, minY, maxX - minX, maxY - minY);
    }

    /**
     * Generates the button bar and then places it appropriately.
     *
     * @param grid - The grid of points; used for positioning.
     * @param position - If given, overrides the JSON setting.
     */
    protected placeButtonBar(box: SVGBox, position?: "left"|"right", opts?: {padding?: number}): void {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }
        let padding = this.cellsize;
        if (opts !== undefined && opts.padding !== undefined) {
            padding = opts.padding;
        }
        if ( ("areas" in this.json) && (this.json.areas !== undefined) && (Array.isArray(this.json.areas)) && (this.json.areas.length > 0) ) {
            const bars = this.json.areas.filter((b) => b.type === "buttonBar") as AreaButtonBar[];
            if (bars.length > 1) {
                throw new Error("Only one button bar may be defined.");
            }
            if (bars.length === 1) {
                const bar = bars[0];
                const barimg = this.buildButtonBar(bar);
                const y = box.y; // - this.cellsize;
                // Position defaults to "right"
                // If a position is passed by the renderer, it overrides everything
                // Otherwise, the JSON prevails
                let pos = "right";
                if (position !== undefined) {
                    pos = position;
                } else if (bar.position !== undefined) {
                    pos = bar.position;
                }
                let x = 0;
                if (pos === "left") {
                    x = box.x - barimg.viewbox().w - padding;
                } else {
                    x = box.x2 + padding;
                }
                const used = this.rootSvg.use(barimg).size(barimg.viewbox().w, barimg.viewbox().h).move(x, y);
                if (this.options.boardClick !== undefined) {
                    const top = used.y() as number;
                    const height = used.height() as number;
                    const numButtons = bar.buttons.length;
                    const btnHeight = height / numButtons;
                    used.click((e: MouseEvent) => {
                        const point = used.point(e.clientX, e.clientY);
                        const yRelative = point.y - top;
                        const row = Math.floor(yRelative / btnHeight);
                        if ( (row >= 0) && (row < numButtons) ) {
                            const value = buttonBarClickValue(bar.buttons[row]);
                            this.options.boardClick!(-1, -1, `_btn_${value}`);
                            e.stopPropagation();
                        }
                    });
                }
            }
        }
    }

    /**
     * Builds the button bar from JSON.
     *
     * @param bar - The parsed JSON representing the button bar
     * @returns The nested SVG, which is embedded in the root `defs()`
     */
    protected buildButtonBar(bar: AreaButtonBar): Svg {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }

        // initialize values
        let colour = this.options.colourContext.strokes;
        if (bar.colour !== undefined) {
            colour= this.resolveColour(bar.colour) as string;
        }
        let minWidth = 0;
        if (bar.minWidth !== undefined) {
            minWidth = bar.minWidth;
        }
        let height = this.cellsize;
        if (bar.height !== undefined) {
            height = this.cellsize * bar.height;
        }
        let buffer = 0;
        if (bar.buffer !== undefined) {
            buffer = height * bar.buffer;
        }
        const nested = this.rootSvg.defs().nested().id("_btnBar");
        const rotation = this.getRotation();

        // build symbols of each label
        const labels: (SVGSymbol | undefined)[] = [];
        let maxWidth = minWidth;
        let maxHeight = 0;
        const hasAnyGlyph = bar.buttons.some((btn) => btn.glyph !== undefined);
        for (let i = 0; i < bar.buttons.length; i++) {
            const b = bar.buttons[i];
            assertButtonBarButton(b, i);
            if (b.label === undefined) {
                labels.push(undefined);
                continue;
            }
            const cloned = {attributes: b.attributes, fill: b.fill};
            const tmptxt = this.rootSvg.text(labelDisplayText(b.label)).font({size: 17, fill: colour, anchor: "start"});
            if (b.attributes !== undefined) {
                for (const a of b.attributes) {
                    tmptxt.attr(a.name, a.value);
                }
            }
            const tmpBox = tmptxt.bbox();
            maxWidth = Math.max(maxWidth, tmpBox.width);
            maxHeight = Math.max(maxHeight, tmpBox.height);
            const symtxt = nested.symbol().addClass(`aprender-button-${x2uid(cloned)}`);
            const realtxt = symtxt.text(labelDisplayText(b.label)).font({size: 17, fill: colour, anchor: "start"});
            if (b.attributes !== undefined) {
                for (const a of b.attributes) {
                    realtxt.attr(a.name, a.value);
                }
            }
            symtxt.viewbox(tmpBox);
            tmptxt.remove();
            labels.push(symtxt);
        }
        if (bar.buttons.length !== labels.length) {
            throw new Error("Something terrible happened.");
        }

        // build the symbol for the rectangle
        const width = buttonBarTotalWidth(maxWidth, height, hasAnyGlyph);
        const rects: SVGSymbol[] = [];
        for (const b of bar.buttons) {
            const cloned = {attributes: b.attributes, fill: b.fill};
            const symrect = nested.symbol().addClass(`aprender-button-${x2uid(cloned)}`);
            let fill: FillData | SVGGradient | SVGElement = {color: this.options.colourContext.background, opacity: 0};
            if ( ("fill" in b) && (b.fill !== undefined) ) {
                fill = this.resolveMarkerFill(b.fill, 1);
            }
            // @ts-expect-error (poor SVGjs typing)
            symrect.rect(width, height).fill(fill).stroke({width: 1, color: colour, linecap: "round", linejoin: "round"});
            // Adding the viewbox triggers auto-filling, auto-centering behaviour that we don't want
            // symrect.viewbox(-1, -1, width + 2, height + 1);
            rects.push(symrect);
        }

        // Composite each into a group, all at 0,0
        const groups: Svg[] = [];
        for (let i = 0; i < labels.length; i++) {
            const b: ButtonBarButton = bar.buttons[i];
            const symlabel = labels[i];
            const symrect = rects[i];
            const value = buttonBarClickValue(b);
            const id = `_btn_${value}`;
            const g = nested.nested().id(id);
            g.use(symrect).size(width, height).move(0, 0);
            if (symlabel !== undefined) {
                const labelCx = buttonBarLabelCenterX(width, height, b.glyphPosition, b.glyph !== undefined);
                g.use(symlabel).size(maxWidth, maxHeight).center(labelCx, height / 2);
            }
            if (b.glyph !== undefined) {
                const piece = this.rootSvg.findOne(`#${b.glyph}`) as Svg;
                if ( (piece === undefined) || (piece === null) ) {
                    throw new Error(`Could not find the requested piece (${b.glyph}). Each glyph *must* exist in the \`legend\`.`);
                }
                const glyphPos = buttonBarGlyphPosition(b.glyphPosition);
                const glyphCenterX = buttonBarGlyphCenterX(glyphPos, width, height);
                const allowance = buttonBarGlyphAllowance(height);
                const usedGlyph = placeButtonBarGlyph({
                    svg: g,
                    piece,
                    allowance,
                    centerX: glyphCenterX,
                    centerY: height / 2,
                    glyphScale: b.glyphScale,
                });
                if (rotation !== 0) {
                    rotate(usedGlyph, rotation, glyphCenterX, height / 2);
                }
            }
            groups.push(g);
        }

        // move each successive one down
        let dy = 0;
        for (const g of groups) {
            g.dy(dy);
            dy += height + buffer;
        }

        // set the viewbox and return
        nested.viewbox(-1, -1, width + 2, (height * bar.buttons.length) + (buffer * (bar.buttons.length - 1)) + 2);
        return nested;
    }

    /**
     * Generates the key and then places it appropriately.
     *
     * @param grid - The grid of points; used for positioning.
     * @param position - If given, overrides the JSON setting.
     */
    protected placeKey(box: SVGBox, position?: "left"|"right", opts?: {padding?: number}): void {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }
        let padding = this.cellsize;
        if (opts !== undefined && opts.padding !== undefined) {
            padding = opts.padding;
        }
        if ( ("areas" in this.json) && (this.json.areas !== undefined) && (Array.isArray(this.json.areas)) && (this.json.areas.length > 0) ) {
            const keys = this.json.areas.filter((b) => b.type === "key") as AreaKey[];
            if (keys.length > 1) {
                throw new Error("Only one key may be defined.");
            }
            if (keys.length === 1) {
                const key = keys[0];
                const keyimg = this.buildKey(key);
                const y = box.y; // - this.cellsize;
                // Position defaults to "right"
                // If a position is passed by the renderer, it overrides everything
                // Otherwise, the JSON prevails
                let pos = "right";
                if (position !== undefined) {
                    pos = position;
                } else if (key.position !== undefined) {
                    pos = key.position;
                }
                let x = 0;
                if (pos === "left") {
                    x = box.x - keyimg.viewbox().w - padding;
                } else {
                    x = box.x2 + padding;
                }
                const used = this.rootSvg.use(keyimg).size(keyimg.viewbox().w, keyimg.viewbox().h).dmove(x, y);
                let clickable = true;
                if (key.clickable !== undefined) {
                    clickable = key.clickable
                }
                if ( (this.options.boardClick !== undefined) && (clickable) ) {
                    const top = used.y() as number;
                    const height = used.height() as number;
                    const numEntries = key.list.length;
                    const btnHeight = height / numEntries;
                    used.click((e: { clientX: number; clientY: number; }) => {
                        const point = used.point(e.clientX, e.clientY);
                        const yRelative = point.y - top;
                        const row = Math.floor(yRelative / btnHeight);
                        if ( (row >= 0) && (row < numEntries) ) {
                            let value = key.list[row].name;
                            if (key.list[row].value !== undefined) {
                                value = key.list[row].value!;
                            }
                            this.options.boardClick!(-1, -1, value);
                        }
                    });
                }

            }
        }
    }

    /**
     * Builds the key from JSON.
     *
     * @param key - The parsed JSON representing the button bar
     * @returns The nested SVG, which is embedded in the root `defs()`
     */
    protected buildKey(key: AreaKey): Svg {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }

        // initialize values
        let labelColour = this.options.colourContext.labels;
        if ( (this.json.board !== null) && ("labelColour" in this.json.board) && (this.json.board.labelColour !== undefined) ) {
            labelColour = this.resolveColour(this.json.board.labelColour) as string;
        }
        let height = this.cellsize * 0.333;
        if (key.height !== undefined) {
            height = this.cellsize * key.height;
        }
        let buffer = height * 0.1;
        if (key.buffer !== undefined) {
            buffer = height * key.buffer;
        }
        const nested = this.rootSvg.defs().nested().id("_key");

        // build symbols of each label
        const rotation = this.getRotation();
        const labels: SVGSymbol[] = [];
        let maxWidth = 0;
        let maxHeight = 0;
        for (const k of key.list) {
            const tmptxt = this.rootSvg.text(k.name).font({size: 17, fill: labelColour, anchor: "start"});
            const tmpBox = tmptxt.bbox();
            maxWidth = Math.max(maxWidth, tmpBox.width);
            maxHeight = Math.max(maxHeight, tmpBox.height);
            const symtxt = nested.symbol();
            symtxt.text(k.name).font({size: 17, fill: labelColour, anchor: "start"});
            symtxt.viewbox(tmpBox);
            tmptxt.remove();
            labels.push(symtxt);
        }
        if (key.list.length !== labels.length) {
            throw new Error("Something terrible happened.");
        }

        // Composite each into a group, all at 0,0, adding click handlers as we go
        const groups: Svg[] = [];
        let maxScaledWidth = 0;
        for (let i = 0; i < labels.length; i++) {
            const k = key.list[i];
            const symlabel = labels[i];
            const piece = this.rootSvg.findOne(`#${k.piece}`) as Svg;
            if ( (piece === undefined) || (piece === null) ) {
                throw new Error(`Could not find the requested piece (${k.piece}). Each piece *must* exist in the \`legend\`.`);

            }
            let id = `_key_${k.name}`;
            if (k.value !== undefined) {
                id = `_key_${k.value}`;
            }
            const g = nested.nested().id(id);
            const used = usePieceAt({svg: g, piece, cellsize: height, x: height / 2, y: height / 2, scalingFactor: 1});
            if (rotation !== 0) {
                rotate(used, rotation, height / 2, height / 2);
            }
            // Have to manually calculate the width so Firefox will render it properly.
            const factor = height / symlabel.viewbox().h;
            const usedLabel = g.use(symlabel).size(symlabel.viewbox().w * factor, height).dx(height * 1.1);
            maxScaledWidth = Math.max(maxScaledWidth, usedLabel.width() as number)
            groups.push(g);
        }

        // move each successive one down
        let dy = 0;
        for (const g of groups) {
            g.dy(dy);
            dy += height + buffer;
        }

        // set the viewbox and return
        nested.viewbox(0, 0, (height * 1.1) + maxScaledWidth, (height * key.list.length) + (buffer * (key.list.length - 1)));
        return nested;
    }

    /**
     * Generates the scroll bar and then places it appropriately.
     *
     * @param grid - The grid of points; used for positioning.
     * @param position - If given, overrides the JSON setting.
     */
    protected placeScroll(box: SVGBox, position?: "left"|"right"): void {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }
        if ( ("areas" in this.json) && (this.json.areas !== undefined) && (Array.isArray(this.json.areas)) && (this.json.areas.length > 0) ) {
            const scrolls = this.json.areas.filter((b) => b.type === "scrollBar") as AreaScrollBar[];
            if (scrolls.length > 1) {
                throw new Error("Only one scroll bar may be defined.");
            }
            if (scrolls.length === 1) {
                const scroll = scrolls[0];
                const scrollImg = this.buildScroll(scroll);
                const y = box.y; // - this.cellsize;
                // Position defaults to "right"
                // If a position is passed by the renderer, it overrides everything
                // Otherwise, the JSON prevails
                let pos = "right";
                if (position !== undefined) {
                    pos = position;
                } else if (scroll.position !== undefined) {
                    pos = scroll.position;
                }
                let x = 0;
                if (pos === "left") {
                    x = box.x - scrollImg.viewbox().w - this.cellsize;
                } else {
                    x = box.x2 + this.cellsize;
                }
                let min = 0;
                if (scroll.min !== undefined) {
                    min = scroll.min;
                }
                let current = scroll.max;
                if (scroll.current !== undefined) {
                    current = scroll.current;
                }
                const used = this.rootSvg.use(scrollImg).size(scrollImg.viewbox().w, scrollImg.viewbox().h).dmove(x, y);
                if (this.options.boardClick !== undefined) {
                    const top = used.y() as number;
                    const height = used.height() as number;
                    const numSegs = scroll.max - min;
                    const numEntries = 4 + numSegs;
                    const segHeight = height / numEntries;
                    used.click((e: { clientX: number; clientY: number; }) => {
                        const point = used.point(e.clientX, e.clientY);
                        const yRelative = point.y - top;
                        const row = Math.floor(yRelative / segHeight);
                        if ( (row >= 0) && (row < numEntries) ) {
                            let value = "";
                            if (row === 0) {
                                // upAll
                                value = `scroll_newval_${scroll.max}`;
                            } else if (row === 1) {
                                // upOne
                                value = `scroll_newval_${current < scroll.max ? current + 1 : scroll.max}`;
                            } else if (row === numEntries - 1) {
                                // downAll
                                value = `scroll_newval_${min}`;
                            } else if (row === numEntries - 2) {
                                // downOne
                                value = `scroll_newval_${current > min ? current - 1 : min}`;
                            } else {
                                // segment
                                const offset = row - 2;
                                value = `scroll_newval_${scroll.max - offset}`;
                            }
                            this.options.boardClick!(-1, -1, value);
                        }
                    });
                }
            }
        }
    }

    /**
     * Builds the scroll bar from JSON.
     *
     * @param json - The parsed JSON representing the button bar
     * @returns The nested SVG, which is embedded in the root `defs()`
     */
    protected buildScroll(json: AreaScrollBar): Svg {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }

        // initialize values
        let height = this.cellsize * 0.75;
        if (json.height !== undefined) {
            height = this.cellsize * json.height;
        }
        let width = this.cellsize * 0.25;
        if (json.width !== undefined) {
            width = this.cellsize * json.width;
        }
        let lineWidth = width * 1.5;
        if (json.lineWidth !== undefined) {
            lineWidth = width * (1 + json.lineWidth);
        }
        let min = 0;
        if (json.min !== undefined) {
            min = json.min;
        }
        let current = json.max;
        if (json.current !== undefined) {
            current = json.current;
        }
        let lblUpOne = "\u00A0+\u00A0";
        let lblUpAll = "++";
        let lblDownOne = "\u00A0\u2212\u00A0";
        let lblDownAll = "\u2212\u2212";
        // let lblBar = "Hide/show layers";
        if (json.labels !== undefined) {
            // if (json.labels.bar !== undefined) {
            //     lblBar = json.labels.bar;
            // }
            if (json.labels.upOne !== undefined) {
                lblUpOne = json.labels.upOne;
            }
            if (json.labels.upAll !== undefined) {
                lblUpAll = json.labels.upAll;
            }
            if (json.labels.downOne !== undefined) {
                lblDownOne = json.labels.downOne;
            }
            if (json.labels.downAll !== undefined) {
                lblDownAll = json.labels.downAll;
            }
        }
        let clrBack: string|undefined;
        let clrFill = this.options.colourContext.fill;
        let clrStrokes = this.options.colourContext.strokes;
        if (json.colours !== undefined) {
            if (json.colours.background !== undefined) {
                clrBack = json.colours.background;
            }
            if (json.colours.fill !== undefined) {
                clrFill = json.colours.fill;
            }
            if (json.colours.strokes !== undefined) {
                clrStrokes = json.colours.strokes;
            }
        }
        const nested = this.rootSvg.defs().nested().id("_scroll");

        // build symbols of each label
        const labels: SVGSymbol[] = [];
        let maxWidth = 0;
        let maxHeight = 0;
        for (const lbl of [lblUpOne, lblUpAll, lblDownOne, lblDownAll]) {
            const tmptxt = this.rootSvg.text(lbl).font({size: 17, fill: this.options.colourContext.strokes, anchor: "start"});
            const tmpBox = tmptxt.bbox();
            maxWidth = Math.max(maxWidth, tmpBox.width);
            maxHeight = Math.max(maxHeight, tmpBox.height);
            const symtxt = nested.symbol();
            symtxt.text(lbl).font({size: 17, fill: this.options.colourContext.strokes, anchor: "start"});
            symtxt.viewbox(tmpBox);
            tmptxt.remove();
            labels.push(symtxt);
        }
        const xOffset = (lineWidth - width) / 2;
        const segBlank = nested.symbol();
        segBlank.line(0,0,lineWidth,0).stroke({width: 1, color: clrStrokes});
        segBlank.line(0,height,lineWidth,height).stroke({width: 1, color: clrStrokes});
        if (clrBack !== undefined) {
            segBlank.rect(width, height).move(xOffset, 0).stroke({width: 1, color: clrStrokes}).fill({color: clrBack});
        } else {
            segBlank.rect(width, height).move(xOffset, 0).stroke({width: 1, color: clrStrokes}).fill({color: this.options.colourContext.background, opacity: 0});
        }
        segBlank.viewbox(0,0,lineWidth, height);
        const segFull = nested.symbol();
        segFull.line(0,0,lineWidth,0).stroke({width: 1, color: clrStrokes});
        segFull.line(0,height,lineWidth,height).stroke({width: 1, color: clrStrokes});
        segFull.rect(width, height).move(xOffset, 0).stroke({width: 1, color: clrStrokes}).fill({color: clrFill});
        segFull.viewbox(0,0,lineWidth, height);

        // Composite each into a group, all at 0,0
        // Click handlers don't work here
        const groups: Svg[] = [];
        let maxScaledWidth = -Infinity;
        // up buttons first
        for (const lbl of [labels[1], labels[0]]) {
            const g = nested.nested();
            const used = g.use(lbl).size(lineWidth, height);
            maxScaledWidth = Math.max(maxScaledWidth, used.width() as number)
            groups.push(g);
        }
        // segments
        for (let i = json.max; i > min; i--) {
            let sym: SVGSymbol;
            if (i > current) {
                sym = segBlank;
            } else {
                sym = segFull;
            }
            const g = nested.nested();
            const used = g.use(sym).size(lineWidth, height);
            maxScaledWidth = Math.max(maxScaledWidth, used.width() as number)
            groups.push(g);
        }
        // down buttons last
        for (const lbl of [labels[2], labels[3]]) {
            const g = nested.nested();
            const used = g.use(lbl).size(lineWidth, height);
            maxScaledWidth = Math.max(maxScaledWidth, used.width() as number)
            groups.push(g);
        }

        // move each successive one down
        let dy = 0;
        for (const g of groups) {
            g.dy(dy);
            dy += height;
        }

        // add background rect to capture all clicks
        nested.rect(maxScaledWidth, (height * (4 + (json.max - min))))
              .stroke({color: "none", width: 0})
              .fill({color: this.options.colourContext.background, opacity: 0});

        // set the viewbox and return
        nested.viewbox(0, 0, maxScaledWidth, (height * (4 + (json.max - min))));
        return nested;
    }

    /**
     * Generates the compass rose and then places it appropriately.
     *
     * @param grid - The grid of points; used for positioning.
     * @param position - If given, overrides the JSON setting.
     */
    protected placeCompass(box: SVGBox, position?: "left"|"right"): void {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }
        if ( ("areas" in this.json) && (this.json.areas !== undefined) && (Array.isArray(this.json.areas)) && (this.json.areas.length > 0) ) {
            const compasses = this.json.areas.filter((b) => b.type === "compassRose") as AreaCompassRose[];
            if (compasses.length > 1) {
                throw new Error("Only one compass rose may be defined.");
            }
            if (compasses.length === 1) {
                const compass = compasses[0];
                const compassImg = this.buildCompass(compass);
                const width = compassImg.viewbox().w;
                const height = compassImg.viewbox().h;
                const y = box.y; // - this.cellsize;
                // Position defaults to "right"
                // If a position is passed by the renderer, it overrides everything
                // Otherwise, the JSON prevails
                let pos = "right";
                if (position !== undefined) {
                    pos = position;
                } else if (compass.position !== undefined) {
                    pos = compass.position;
                }
                let x = 0;
                if (pos === "left") {
                    x = box.x - width - (this.cellsize / 2);
                } else {
                    x = box.x2 + (this.cellsize / 2);
                }
                const rotation = this.getRotation();
                const used = this.rootSvg.use(compassImg)
                    .size(width, height)
                    .dmove(x, y)
                    .rotate(rotation, x + compassImg.viewbox().w / 2, y + compassImg.viewbox().h / 2);
                if (this.options.boardClick !== undefined) {
                    const centre = {x: x + width / 2, y: y + height / 2};
                    const root = this.rootSvg;
                    const genericCatcher = ((e: { clientX: number; clientY: number; }) => {
                        const point = rotatePoint(root.point(e.clientX, e.clientY), rotation*-1, centre);
                        const bearing = calcBearing(centre.x, centre.y, point.x, point.y);
                        const delta = smallestDegreeDiff(bearing, 0);
                        if (delta >= -22.5 && delta <= 22.5) {
                            this.options.boardClick!(-1, -1, "N");
                        }
                        else if (delta >= 22.5 && delta <= 67.5) {
                            this.options.boardClick!(-1, -1, "NE");
                        }
                        else if (delta >= 67.5 && delta <= 112.5) {
                            this.options.boardClick!(-1, -1, "E");
                        }
                        else if (delta >= 112.5 && delta <= 157.5) {
                            this.options.boardClick!(-1, -1, "SE");
                        }
                        else if (delta >= -67.5 && delta <= -22.5) {
                            this.options.boardClick!(-1, -1, "NW");
                        }
                        else if (delta >= -112.5 && delta <= -67.5) {
                            this.options.boardClick!(-1, -1, "W");
                        }
                        else if (delta >= -157.5 && delta <= -112.5) {
                            this.options.boardClick!(-1, -1, "SW");
                        }
                        else  {
                            this.options.boardClick!(-1, -1, "S");
                        }
                    });
                    used.click(genericCatcher);
                }
            }
        }
    }

    /**
     * Builds the compass rose from JSON.
     *
     * @param json - The parsed JSON representing the button bar
     * @returns The nested SVG, which is embedded in the root `defs()`
     */
    protected buildCompass(json: AreaCompassRose): Svg {
        if ( (this.json === undefined) || (this.rootSvg === undefined) ) {
            throw new Error("Invalid object state.");
        }

        const roseSymbol = this.rootSvg.defs().symbol().id("_compassSymbol");
        roseSymbol.path("M49.956 10c-.591-.003-.916.4-1.007.872l-2.443 8.242a30.937 30.937 0 0 0-15.897 6.6c-2.538-1.378-5.075-2.763-7.615-4.133c-.47-.253-.882-.234-1.262.149c-.381.383-.273.947-.058 1.353l4.069 7.495a30.931 30.931 0 0 0-6.634 15.932c-2.774.823-5.552 1.639-8.323 2.467c-.51.153-.787.458-.786.998c.002.54.476.863.916.998l8.189 2.426a30.936 30.936 0 0 0 6.599 15.98c-1.385 2.552-2.778 5.104-4.156 7.658c-.253.47-.234.88.15 1.261c.382.38.946.274 1.352.06l7.537-4.091a30.933 30.933 0 0 0 15.922 6.622c.822 2.775 1.64 5.554 2.468 8.325c.153.51.458.787.998.786c.54-.002.863-.476.998-.916l2.426-8.189a30.89 30.89 0 0 0 16.006-6.632l7.568 4.11c.549.28.92.329 1.34-.087c.421-.416.366-.932.097-1.33l-4.12-7.593a30.962 30.962 0 0 0 6.591-15.958l8.233-2.44c.586-.19.883-.417.886-1.009c.003-.591-.4-.916-.872-1.007l-8.27-2.451a30.967 30.967 0 0 0-6.607-15.91l4.09-7.531c.279-.55.328-.92-.088-1.341a.957.957 0 0 0-.671-.305a1.15 1.15 0 0 0-.659.21l-7.546 4.097a30.883 30.883 0 0 0-15.978-6.62l-2.434-8.212c-.19-.586-.417-.883-1.009-.886zm-.001 4.176v31.04a4.783 4.783 0 0 0-2.306.619l-4.979-6.308l5.214-18.143l.7-2.441zM54.14 21.6a28.54 28.54 0 0 1 12.96 5.355l-9.797 5.318zm-8.377.025l-3.144 10.607c-3.241-1.762-6.484-3.517-9.726-5.278a28.553 28.553 0 0 1 12.87-5.329zM24.65 24.65l15.602 15.57l-.008.023c-1.987.589-3.972 1.18-5.96 1.768l-5.988-10.793l-1.223-2.205zm50.648.018l-3.343 3.344l-1.673 1.672L59.73 40.237l-.075-.022l-1.754-5.916l10.865-6.015l1.308-.722l.89-.494zm-2.278 8.188a28.554 28.554 0 0 1 5.355 12.907l-10.648-3.156zm-46.039.008l5.29 9.744c-3.554 1.05-7.104 2.108-10.657 3.16a28.538 28.538 0 0 1 5.367-12.904zm33.392 9.779l18.242 5.24l2.413.694l4.795 1.378h-31.04a4.783 4.783 0 0 0-.631-2.328zm-15.156 7.312l-.002.045c0 .798.2 1.583.581 2.284L39.5 57.241l-6.241-1.788l-11.88-3.4v-.002l-2.438-.698l-4.813-1.378zm33.184 4.184a28.538 28.538 0 0 1-5.348 12.95l-5.313-9.79zm-56.79.003l10.647 3.155c-1.775 3.265-3.542 6.532-5.316 9.797a28.549 28.549 0 0 1-5.331-12.952zm30.697.047l4.96 6.211l-1.803 6.296L52.05 78.62l-.698 2.439l-1.378 4.813l-.02-31.09c.842.002 1.716-.218 2.354-.593zm13.403 3.71l6.01 10.86l1.218 2.199l2.421 4.374l-3.374-3.375l-1.673-1.672l-10.607-10.607zm-25.475 1.832c.592 2 1.187 3.996 1.778 5.996l-10.786 5.984l-2.207 1.224l-4.405 2.445zm17.074 7.966l9.82 5.333a28.552 28.552 0 0 1-12.989 5.358zm-14.7.042c1.05 3.55 2.106 7.097 3.157 10.646a28.54 28.54 0 0 1-12.894-5.36z").fill(this.resolveColour("_context_strokes") as string);
        roseSymbol.path("M46 8V0h1.981l4.127 5.342V0H54v8h-2.043l-4.065-5.217V8z").fill(this.resolveColour("_context_strokes") as string);
        roseSymbol.viewbox(5, 0, 90, 90);

        // initialize values
        let width = this.cellsize * 2;
        if (json.width !== undefined) {
            width = this.cellsize * json.width;
        }
        const nested = this.rootSvg.defs().nested().id("_compass");
        nested.use(roseSymbol).width(width).height(width);

        // add background rect to capture all clicks
        // Click handlers don't work here
        nested.rect(width, width)
              .stroke({color: "none", width: 0})
              .fill({color: this.options.colourContext.background, opacity: 0});

        // set the viewbox and return
        nested.viewbox(0, 0, width, width);
        return nested;
    }

    /**
     * Semi-opaque veil over the piece grid (not the title band). Click-through.
     */
    protected addPiecesAreaVeil(
        nested: Svg,
        area: AreaPieces,
        areaWidth: number,
        areaHeight: number,
        titleBand: number,
    ): void {
        if (area.veiled !== true) {
            return;
        }
        const piecesRegionHeight = areaHeight - titleBand;
        if (piecesRegionHeight <= 0) {
            return;
        }
        nested.rect(areaWidth, piecesRegionHeight)
            .move(0, titleBand)
            .fill({ color: this.options.colourContext.background, opacity: 0.5 })
            .attr({ "pointer-events": "none" })
            .addClass("aprender-pieces-veil");
    }

    /**
     * For placing a generic `pieces` area at the bottom of the board.
     *
     * @param gridPoints -
     */
    protected piecesArea(box: SVGBox, opts?: {padding?: number, canvas?: Svg}): {newY: number|undefined; width: number|undefined} {
        if (this.rootSvg === undefined) {
            throw new Error("Can't place a `pieces` area until the root SVG is initialized!");
        }
        let padding = this.cellsize / 2;
        if (opts !== undefined && opts.padding !== undefined) {
            padding = opts.padding;
        }
        let placeY: number|undefined;
        let finalWidth: number|undefined;
        if ( (this.json !== undefined) && (this.json.areas !== undefined) && (Array.isArray(this.json.areas)) && (this.json.areas.length > 0) ) {
            const areas = this.json.areas.filter((x) => x.type === "pieces") as AreaPieces[];
            const boardBottom = box.y2; // + this.cellsize;
            // Width in number of cells, taking the maximum board width
            const boardWidth = Math.floor(box.width / this.cellsize);
            placeY = boardBottom + padding;
            for (let iArea = 0; iArea < areas.length; iArea++) {
                const area = areas[iArea];
                const rotation = shouldRotateAreaPieces(area) ? this.getRotation() : 0;
                let hpad = 0;
                if (area.spacing !== undefined) {
                    hpad = this.cellsize * area.spacing;
                }
                let desiredWidth = boardWidth;
                if (area.width !== undefined) {
                    desiredWidth = area.width;
                }
                const pieceRows = buildPiecesAreaRows(area.pieces, desiredWidth);
                const textHeight = this.cellsize / 3; // 10; // the allowance for the label
                const cellsize = this.cellsize * 0.75;
                const boardCellsize = this.cellsize;
                let labelColour = this.options.colourContext.labels;
                if ( (this.json.board !== null) && ("labelColour" in this.json.board) && (this.json.board.labelColour !== undefined) ) {
                    labelColour = this.resolveColour(this.json.board.labelColour) as string;
                }
                const areaLabelText = labelDisplayText(area.label);
                const areaLabelMeasure = measureAreaTitle(this.rootSvg, areaLabelText, textHeight, labelColour);
                const titleBand = areaTitleBand(areaLabelMeasure.height, textHeight);
                let areaWidth = 0;
                let areaHeight = titleBand;
                for (let i = 0; i < pieceRows.length; i++) {
                    const row = pieceRows[i];
                    let rowWidth = 0;
                    let rowHeight = cellsize;
                    for (let j = 0; j < row.length; j++) {
                        const entry = area.pieces[row[j]];
                        rowWidth += piecesAreaSlotWidth(entry, cellsize, boardCellsize);
                        rowHeight = Math.max(rowHeight, piecesAreaSlotHeight(entry, cellsize, boardCellsize));
                        if (j < row.length - 1) {
                            rowWidth += piecesAreaHorizontalGap(entry, hpad, boardCellsize);
                        }
                    }
                    areaWidth = Math.max(areaWidth, rowWidth);
                    areaHeight += rowHeight;
                    if (i < pieceRows.length - 1) {
                        areaHeight += piecesAreaVerticalGap(hpad, boardCellsize);
                    }
                }
                let markWidth = 0;
                let markColour: string|undefined;
                if ( ("ownerMark" in area) && (area.ownerMark !== undefined) ) {
                    markWidth = 15;
                    if (typeof area.ownerMark === "number") {
                        markColour = this.options.colours[area.ownerMark - 1];
                    } else {
                        markColour = this.resolveColour(area.ownerMark) as string;
                    }
                }
                let root = this.rootSvg;
                if (opts !== undefined && opts.canvas !== undefined) {
                    root = opts.canvas;
                }
                const nested = root.nested().id(`_pieces${iArea}`).size(areaWidth+2, areaHeight+2).viewbox(-1 - markWidth - 5, -1, areaWidth+2+markWidth+10, areaHeight+2).attr("overflow", "visible");
                const fullWidth = areaWidth + 2 + markWidth + 10;
                if (finalWidth === undefined) {
                    finalWidth = fullWidth;
                } else {
                    finalWidth = Math.max(finalWidth, fullWidth);
                }
                if ("background" in area && area.background !== undefined) {
                    // @ts-expect-error (poor SVGjs typing)
                    nested.rect(areaWidth,areaHeight).fill(this.resolveMarkerFill(area.background, 1));
                }
                const entryCaptionSize = boardCellsize / 4;
                for (let iRow = 0; iRow < pieceRows.length; iRow++) {
                    const row = pieceRows[iRow];
                    let slotX = 0;
                    let rowHeight = cellsize;
                    for (const iPiece of row) {
                        rowHeight = Math.max(rowHeight, piecesAreaSlotHeight(area.pieces[iPiece], cellsize, boardCellsize));
                    }
                    const slotTop = titleBand + pieceRows.slice(0, iRow).reduce((sum, prevRow) => {
                        let prevHeight = cellsize;
                        for (const iPiece of prevRow) {
                            prevHeight = Math.max(prevHeight, piecesAreaSlotHeight(area.pieces[iPiece], cellsize, boardCellsize));
                        }
                        return sum + prevHeight + piecesAreaVerticalGap(hpad, boardCellsize);
                    }, 0);
                    for (let j = 0; j < row.length; j++) {
                        const iPiece = row[j];
                        const entry = area.pieces[iPiece];
                        const slotWidth = piecesAreaSlotWidth(entry, cellsize, boardCellsize);
                        const pieceCenterX = slotX + (slotWidth / 2);
                        const pieceCenterY = piecesAreaPieceCenterYFromSlotTop(slotTop, entry, cellsize, boardCellsize);
                        if (isDominoTileRef(entry)) {
                            const [leftKey, rightKey] = entry.domino;
                            const tileLeft = slotX;
                            const tileTop = piecesAreaDominoTileTopFromSlotTop(slotTop, entry, boardCellsize);
                            const tileGroup = composeDominoTile(nested, this.rootSvg, leftKey, rightKey, boardCellsize);
                            tileGroup.move(tileLeft, tileTop);
                            if (rotation !== 0) {
                                rotate(tileGroup, rotation, pieceCenterX, pieceCenterY);
                            }
                            if (this.options.boardClick !== undefined) {
                                const handle = entry.id ?? iPiece;
                                const halfSize = boardCellsize;
                                const leftHit = nested.rect(halfSize, halfSize).fill({color: "#fff", opacity: 0}).addClass("aprender-domino-hit").move(tileLeft, tileTop);
                                const rightHit = nested.rect(halfSize, halfSize).fill({color: "#fff", opacity: 0}).addClass("aprender-domino-hit").move(tileLeft + halfSize, tileTop);
                                leftHit.click((e: Event) => {this.options.boardClick!(-1, -1, dominoClickPayload(handle, leftKey, rightKey, "L")); e.stopPropagation();});
                                rightHit.click((e: Event) => {this.options.boardClick!(-1, -1, dominoClickPayload(handle, leftKey, rightKey, "R")); e.stopPropagation();});
                            }
                        } else {
                            const legendKey = piecesAreaLegendKey(entry);
                            const piece = this.rootSvg.findOne("#" + legendKey) as Svg;
                            if ( (piece === null) || (piece === undefined) ) {
                                throw new Error(`Could not find the requested piece (${legendKey}). Each piece in the stack *must* exist in the \`legend\`.`);
                            }
                            const use = usePieceAt({svg: nested, piece, cellsize, x: pieceCenterX, y: pieceCenterY, scalingFactor: 1});
                            if (rotation !== 0) {
                                rotate(use, rotation, pieceCenterX, pieceCenterY);
                            }
                            if (this.options.boardClick !== undefined) {
                                use.click((e: Event) => {this.options.boardClick!(-1, -1, legendKey); e.stopPropagation();});
                            }
                        }
                        const caption = piecesAreaCaption(entry);
                        if (caption.text !== undefined) {
                            const captionY = piecesAreaCaptionTextYFromSlotTop(slotTop, entry, cellsize, boardCellsize)!;
                            const captionFont = {
                                size: entryCaptionSize,
                                anchor: "middle" as const,
                                fill: labelColour,
                            };
                            const captionEl = placeTextInkTop(
                                nested,
                                caption.text,
                                pieceCenterX,
                                captionY,
                                captionFont,
                                "aprender-pieces-entry-label",
                            );
                            if (rotation !== 0) {
                                rotate(captionEl, rotation, pieceCenterX, pieceCenterY);
                            }
                        }
                        slotX += slotWidth;
                        if (j < row.length - 1) {
                            slotX += piecesAreaHorizontalGap(entry, hpad, boardCellsize);
                        }
                    }
                }

                this.addPiecesAreaVeil(nested, area, areaWidth, areaHeight, titleBand);

                // add marker line if indicated
                if ( (markWidth > 0) && (markColour !== undefined) ) {
                    nested.rect(markWidth, nested.bbox().height).fill(markColour).stroke({width: 1, color: "black", linecap: "round", linejoin: "round"}).dmove((markWidth * -1) - 5, 0);
                    // nested.line(markWidth * -1, 0, markWidth * -1, nested.bbox().height).stroke({width: markWidth, color: markColour});
                }

                const { textWidth: areaLabelWidth } = placeAreaTitleInBand(this.rootSvg, nested, areaLabelText, {
                    fontSize: textHeight,
                    fill: labelColour,
                });
                const {x: vbx, y:vby, w: vbw, h: vbh} = nested.viewbox();
                const realWidth = Math.max(vbw, areaLabelWidth);
                nested.width(realWidth);
                nested.viewbox(vbx, vby, realWidth, vbh);

                nested.move(box.x, placeY);
                placeY += areaHeight + 2 + (this.cellsize * 0.5);
            }
        }
        return {newY: placeY, width: finalWidth};
    }

    /**
     * Pyramid stash columns (`localStash` areas), placed below the board like `pieces` areas.
     * Stack columns wrap at board width (or optional `width` on the area).
     */
    protected localStashArea(
        box: SVGBox,
        opts?: {
            padding?: number;
            startY?: number;
            canvas?: Svg;
            /** Layer offset as a fraction of the stash piece cell size (default {@link LOCAL_STASH_STACK_LAYER_OFFSET}). */
            stackLayerOffset?: number;
        },
    ): { newY: number | undefined; width: number | undefined } {
        if (this.rootSvg === undefined) {
            throw new Error("Can't place a `localStash` area until the root SVG is initialized!");
        }
        let padding = this.cellsize / 2;
        if (opts?.padding !== undefined) {
            padding = opts.padding;
        }
        let placeY: number | undefined = opts?.startY;
        let finalWidth: number | undefined;
        if (
            this.json === undefined
            || this.json.areas === undefined
            || !Array.isArray(this.json.areas)
            || this.json.areas.length === 0
        ) {
            return { newY: placeY, width: finalWidth };
        }
        const areas = this.json.areas.filter((x) => x.type === "localStash") as AreaVolcanoStash[];
        if (areas.length === 0) {
            return { newY: placeY, width: finalWidth };
        }
        const boardWidth = Math.floor(box.width / this.cellsize);
        if (placeY === undefined) {
            placeY = box.y2 + padding;
        }
        const layerOffsetFrac = opts?.stackLayerOffset ?? LOCAL_STASH_STACK_LAYER_OFFSET;
        const cellsize = this.cellsize * 0.75;
        const textHeight = this.cellsize / 3;
        let labelColour = this.options.colourContext.labels;
        if (
            this.json.board !== null
            && "labelColour" in this.json.board
            && this.json.board.labelColour !== undefined
        ) {
            labelColour = this.resolveColour(this.json.board.labelColour) as string;
        }
        let root = this.rootSvg;
        if (opts?.canvas !== undefined) {
            root = opts.canvas;
        }
        for (let iArea = 0; iArea < areas.length; iArea++) {
            const area = areas[iArea];
            let desiredWidth = boardWidth;
            if (area.width !== undefined) {
                desiredWidth = area.width;
            }
            let hpad = 0;
            if (area.spacing !== undefined) {
                hpad = this.cellsize * area.spacing;
            }
            const stackRows = buildLocalStashRows(area.stash.length, desiredWidth);
            const stashLabelText = labelDisplayText(area.label);
            const stashLabelMeasure = measureAreaTitle(this.rootSvg, stashLabelText, textHeight, labelColour);
            const titleBand = areaTitleBand(stashLabelMeasure.height, textHeight);
            const legend = this.json.legend;
            const rowBandHeights = stackRows.map((row) => {
                const rowSpan = localStashRowStackSpanPx(area.stash, row, legend, layerOffsetFrac, cellsize);
                return cellsize + rowSpan;
            });
            let areaWidth = 0;
            let areaHeight = titleBand + rowBandHeights.reduce((sum, h) => sum + h, 0);
            for (const row of stackRows) {
                let rowWidth = 0;
                for (let c = 0; c < row.length; c++) {
                    rowWidth += cellsize;
                    if (c < row.length - 1) {
                        rowWidth += hpad;
                    }
                }
                areaWidth = Math.max(areaWidth, rowWidth);
            }
            for (let r = 0; r < stackRows.length - 1; r++) {
                areaHeight += piecesAreaVerticalGap(hpad, this.cellsize);
            }
            const nested = root
                .nested()
                .id(`_localStash${iArea}`)
                .size(areaWidth + 2, areaHeight + 2)
                .viewbox(-1, -1, areaWidth + 2, areaHeight + 2)
                .attr("overflow", "visible");
            const fullWidth = areaWidth + 2;
            if (finalWidth === undefined) {
                finalWidth = fullWidth;
            } else {
                finalWidth = Math.max(finalWidth, fullWidth);
            }
            let contentY = titleBand;
            for (let iRow = 0; iRow < stackRows.length; iRow++) {
                const row = stackRows[iRow];
                const rowMaxSteps = localStashRowMaxSteps(area.stash, row);
                const rowBandHeight = rowBandHeights[iRow]!;
                const silhouetteRowBaseY = localStashSilhouetteRowSharedBaseY(contentY, cellsize);
                let slotX = 0;
                for (let c = 0; c < row.length; c++) {
                    const stackIdx = row[c]!;
                    const stack = area.stash[stackIdx]!;
                    const profile = localStashColumnProfile(stack, legend);
                    const stepPx = localStashVerticalStepPx(profile, layerOffsetFrac, cellsize);
                    for (let i = 0; i < stack.length; i++) {
                        const p = stack[i];
                        if (p === "-") {
                            continue;
                        }
                        const piece = this.rootSvg.findOne("#" + p) as Svg;
                        if (piece === null || piece === undefined) {
                            throw new Error(
                                `Could not find the requested piece (${p}). Each piece in the stack *must* exist in the \`legend\`.`,
                            );
                        }
                        let pieceY: number;
                        if (profile === "silhouette3D") {
                            const tier = localStashSilhouetteTier(legend, p);
                            if (tier === undefined) {
                                throw new Error(
                                    `Could not resolve silhouette tier for localStash piece (${p}).`,
                                );
                            }
                            pieceY = localStashSilhouettePieceCenterY(silhouetteRowBaseY, tier, cellsize);
                        } else {
                            const stepsAboveBottom = localStashStepsAboveBottom(stack, i);
                            pieceY =
                                contentY
                                + (rowMaxSteps - stepsAboveBottom) * stepPx
                                + LOCAL_STASH_PIECE_BASE_Y_FRAC * cellsize
                                + cellsize / 2;
                        }
                        const use = usePieceAt({
                            svg: nested,
                            piece,
                            cellsize,
                            x: slotX + cellsize / 2,
                            y: pieceY,
                            scalingFactor: 1,
                        });
                        if (this.options.boardClick !== undefined) {
                            use.click((e: Event) => {
                                this.options.boardClick!(-1, -1, p);
                                e.stopPropagation();
                            });
                        }
                    }
                    slotX += cellsize;
                    if (c < row.length - 1) {
                        slotX += hpad;
                    }
                }
                contentY += rowBandHeight;
                if (iRow < stackRows.length - 1) {
                    contentY += piecesAreaVerticalGap(hpad, this.cellsize);
                }
            }
            const { textWidth: stashLabelWidth } = placeAreaTitleInBand(this.rootSvg, nested, stashLabelText, {
                fontSize: textHeight,
                fill: labelColour,
            });
            const { x: vbx, y: vby, w: vbw, h: vbh } = nested.viewbox();
            const realWidth = Math.max(vbw, stashLabelWidth);
            nested.width(realWidth);
            nested.viewbox(vbx, vby, realWidth, vbh);
            nested.move(box.x, placeY);
            placeY += areaHeight + 2 + this.cellsize * 0.5;
        }
        return { newY: placeY, width: finalWidth };
    }

    /**
     * The reserves area is currently only used for the DVGC board.
     * This creates a label-less `pieces` area with the board itself.
     * It is designed for two-player games, 180 degree rotation only.
     * The area itself is clickable, as are the contained pieces.
     */
    protected reservesArea(opts: {bottomN: number, topS: number, xLeft: number, xRight: number}) {
        if (this.rootSvg === undefined) {
            throw new Error("Can't place a `reserves` area until the root SVG is initialized!");
        }
        const board = this.rootSvg.findOne("#board") as SVGG|null;
        if (board === null) {
            throw new Error("Can't place a `reserves` area unless a `board` group already exists.");
        }
        if ( (this.json !== undefined) && (this.json.areas !== undefined) && (Array.isArray(this.json.areas)) && (this.json.areas.length > 0) ) {
            const areas = this.json.areas.filter((x) => x.type === "reserves") as AreaReserves[];
            // Width in number of cells, taking the maximum board width
            const boardWidth = Math.floor(Math.abs(opts.xLeft - opts.xRight) / this.cellsize);
            for (let iArea = 0; iArea < areas.length; iArea++) {
                const area = areas[iArea];
                const numPieces = area.pieces.length;
                const numRows = Math.max(Math.ceil(numPieces / boardWidth), 1);
                const areaWidth = this.cellsize * boardWidth;
                const areaHeight = this.cellsize * numRows;
                let markWidth = 0;
                let markColour: string|undefined;
                if ( ("ownerMark" in area) && (area.ownerMark !== undefined) ) {
                    markWidth = 15;
                    markColour = this.resolveColour(area.ownerMark) as string;
                }
                const nested = board.nested().id(`_reserves${iArea}`).size(areaWidth+2, areaHeight+2).viewbox(-1 - markWidth - 5, -1, areaWidth+2+markWidth+10, areaHeight+2);
                let rect: SVGRect;
                if ("background" in area) {
                    // @ts-expect-error (poor SVGjs typing)
                    rect = nested.rect(areaWidth,areaHeight).fill(this.resolveMarkerFill(area.background, 0.25));
                } else {
                    rect = nested.rect(areaWidth,areaHeight).fill({opacity: 0});
                }
                if (this.options.boardClick !== undefined) {
                    rect.click((e: Event) => {this.options.boardClick!(-1, -1, `_reserves_${area.side}`); e.stopPropagation();});
                }
                for (let iPiece = 0; iPiece < area.pieces.length; iPiece++) {
                    const p = area.pieces[iPiece];
                    const row = Math.floor(iPiece / boardWidth);
                    const col = iPiece % boardWidth;
                    const piece = this.rootSvg.findOne("#" + p) as Svg;
                    if ( (piece === null) || (piece === undefined) ) {
                        throw new Error(`Could not find the requested piece (${p}). Each piece in the stack *must* exist in the \`legend\`.`);
                    }
                    const newx = (col * this.cellsize) + (this.cellsize / 2);
                    const newy = (row * this.cellsize) + (this.cellsize / 2);
                    const use = usePieceAt({svg: nested, piece, cellsize: this.cellsize * 0.75, x: newx, y: newy, scalingFactor: 1});
                    if (this.options.boardClick !== undefined) {
                        use.click((e: Event) => {this.options.boardClick!(-1, -1, p); e.stopPropagation();});
                    }
                }

                // add marker line if indicated
                if ( (markWidth > 0) && (markColour !== undefined) ) {
                    nested.rect(markWidth, nested.bbox().height).fill(markColour).stroke({width: 1, color: "black", linecap: "round", linejoin: "round"}).dmove((markWidth * -1) - 5, 0);
                    // nested.line(markWidth * -1, 0, markWidth * -1, nested.bbox().height).stroke({width: markWidth, color: markColour});
                }

                // Now place the whole group where it belongs
                if (area.side === "N") {
                    nested.move(opts.xLeft, opts.bottomN - areaHeight);
                } else {
                    nested.move(opts.xLeft, opts.topS);
                }
            }
        }
    }

    protected backFill(boardFill?: Poly, preRotation = false): boolean {
        // Because of how board fills are now implemented, a fill of type `full`
        // automatically implies `board` as well.
        type BackFill = {
            type?: "full"|"board";
            colour: string|number|Colourfuncs;
            opacity?: number;
        };

        if (this.rootSvg === undefined) {
            throw new Error("Can't add a back fill unless the root SVG is initialized!");
        }
        if ( (this.json === undefined) || (this.json.board === undefined) ) {
            throw new Error("Can't add a back fill unless the JSON is initialized!");
        }
        if (this.json.board !== null) {
            let backFillObj: BackFill|undefined;
            if ( ("backFill" in this.json.board) && (this.json.board.backFill !== undefined) && (this.json.board.backFill !== null) ) {
                backFillObj = this.json.board.backFill as BackFill;
            }
            const [fillColour, fillOpacity] = getBoardFill(this, undefined);

            // if there is no backFill object and no requested board colour, then don't try again
            if (backFillObj === undefined && fillColour === undefined) {
                return true;
            }
            // but if there's no backFill object but there's a fillColour,
            // populate a basic backFillObj
            else if (backFillObj === undefined && fillColour !== undefined) {
                backFillObj = {
                    type: "board",
                    colour: fillColour,
                    opacity: fillOpacity,
                };
            }
            // and if the object is still undefined, then error
            else if (backFillObj === undefined) {
                throw new Error("Could not determine the backFill options.");
            }

            let bgopacity = 1;
            if ( backFillObj.opacity !== undefined ) {
                bgopacity = backFillObj.opacity;
            }
            const bgfill = this.resolveMarkerFill(backFillObj.colour, bgopacity);
            let bgtype: "full"|"board" = "full";
            if (backFillObj.type !== undefined) {
                bgtype = backFillObj.type;
            }

            // if we're pre-rotation but we want to do a full fill, then try again later
            if (preRotation && bgtype === "full") {
                return false;
            }

            if (bgtype === "full") {
                const bbox = this.rootSvg.bbox();
                // @ts-expect-error (poor SVGjs typing)
                this.rootSvg.rect(bbox.width + 20, bbox.height + 20).id("aprender-backfill-full").move(bbox.x - 10, bbox.y - 10).fill(bgfill).back();
            } else {
                const board = this.rootSvg.findOne("#board") as SVGG|null;
                if (board === null) {
                    throw new Error(`Can't do a board fill if there's no board.`);
                }
                // ignore if boardFill is undefined
                if (boardFill !== undefined) {
                    if (boardFill.type === "circle") {
                        // @ts-expect-error (poor SVGjs typing)
                        const poly = this.rootSvg.circle(boardFill.r * 2).id("aprender-backfill-board").center(boardFill.cx, boardFill.cy).fill(bgfill);
                        // `board` backfill can't just be pushed to the back but must be inside the `board` group
                        board.add(poly, 0);

                    }
                    else if (boardFill.type === "poly") {
                        const path = roundPolygon(boardFill.points, 15);
                        // @ts-expect-error (poor SVGjs typing)
                        const poly = this.rootSvg.path(path).id("aprender-backfill").fill(bgfill).stroke("none");
                        // `board` backfill can't just be pushed to the back but must be inside the `board` group
                        board.add(poly, 0);
                    }
                    else {
                        throw new Error(`boardFill can only be a circle or a polygon.`);
                    }
                }
            }
            return true;
        }

        // if board is null, don't try again
        return true;
    }

    public getRotation(): number {
        if (!this.json) {
            throw new Error("Cannot rotate unless SVG is initialized and a board is present.");
        }
        let rotation = 0;
        if (this.options.rotate !== undefined) {
            rotation += this.options.rotate;
        }
        if (this.json.board !== undefined && this.json.board !== null &&("rotate" in this.json.board) && this.json.board.rotate !== undefined) {
            rotation += this.json.board.rotate;
        }
        rotation = rotation % 360;
        while (rotation < 0) { rotation += 360; }
        return rotation;
    }

    /**
     * Rotation baked into legend defs (upright text counter-rotate, flips). Excludes `opts.rotate`
     * so playground / viewer rotation only spins `#board` via {@link rotateBoard}.
     */
    protected getLegendGlyphBoardRotation(): number {
        if (!this.json) {
            return 0;
        }
        let rotation = 0;
        if (
            this.json.board !== undefined
            && this.json.board !== null
            && ("rotate" in this.json.board)
            && this.json.board.rotate !== undefined
        ) {
            rotation += this.json.board.rotate;
        }
        rotation = rotation % 360;
        while (rotation < 0) {
            rotation += 360;
        }
        return rotation;
    }

    public getBoardCentre(): IPoint {
        if (this.rootSvg === undefined) {
            throw new Error("Cannot calculate the board centre unless SVG is initialized and a board is present.");
        }
        const board = this.rootSvg.findOne("#board") as SVGG|null;
        if (board === null) {
            throw new Error("Could not find the core board group to calculate the centre.");
        }
        const bbox = board.bbox();
        return {x: bbox.cx, y: bbox.cy}
    }

  protected legendGlyphsForReferenceKey(key: string): [Glyph, ...Glyph[]] | null {
        if (this.json === undefined || this.json.legend === undefined) {
            return null;
        }
        const entry = this.json.legend[key];
        if (entry === undefined) {
            return null;
        }
        if (typeof entry === "string") {
            return [{ name: entry }];
        }
        if (!Array.isArray(entry)) {
            if (typeof entry === "object" && entry !== null && "piece" in entry) {
                return null;
            }
            return [entry as Glyph];
        }
        if (entry.length === 0) {
            return null;
        }
        if (Array.isArray(entry[0])) {
            return null;
        }
        return entry as [Glyph, ...Glyph[]];
    }

    protected resolveReferenceArt(ref: BoardReference, side: ReferenceSide, sourceId: string): {
        defId: string;
        svgContent?: string;
        meta: import("../references/types.js").ResolvedReferenceArt;
    } {
        const legendGlyphs = this.legendGlyphsForReferenceKey(sourceId);
        if (legendGlyphs !== null) {
            if (this.rootSvg === undefined) {
                throw new Error("Cannot resolve reference art before SVG is initialized.");
            }
            const defSvg = this.rootSvg.findOne("#" + sourceId) as Svg | null;
            if (defSvg === null) {
                throw new Error(`Legend reference "${sourceId}" was not loaded into defs.`);
            }
            const vb = defSvg.viewbox();
            const viewBox = { x: vb.x, y: vb.y, w: vb.width, h: vb.height };
            const anchor = ref.layout === "annulus"
                ? defaultAnnulusAnchor(viewBox)
                : defaultSidebarAnchor(side);
            return { defId: sourceId, meta: { viewBox, anchor, fromLegend: true } };
        }
        const { svgContent, meta } = getRegistryReferenceArt(sourceId);
        return { defId: registryReferenceDefId(sourceId), svgContent, meta };
    }

    protected ensureReferenceDef(resolved: {
        defId: string;
        svgContent?: string;
        meta: import("../references/types.js").ResolvedReferenceArt;
    }): Svg | SVGSymbol {
        if (this.rootSvg === undefined) {
            throw new Error("Cannot place board reference before SVG is initialized.");
        }
        const existing = this.rootSvg.findOne("#" + resolved.defId) as Svg | null;
        if (existing !== null) {
            return existing;
        }
        if (resolved.svgContent === undefined) {
            throw new Error(`Reference artwork "${resolved.defId}" is missing from defs.`);
        }
        const { x, y, w, h } = resolved.meta.viewBox;
        const symbol = this.rootSvg.defs().symbol().id(resolved.defId).viewbox(x, y, w, h);
        symbol.svg(referenceInnerMarkup(resolved.svgContent));
        return symbol;
    }

    protected applyReferenceStyles(root: Svg | SVGG | SVGSymbol, styles?: { [key: string]: ColourResolvable }): void {
        if (styles === undefined) {
            return;
        }
        this.applyReferenceContextStyles(root);
        for (const slotId of Object.keys(styles)) {
            this.applyReferenceSlotStyle(root, slotId, styles[slotId]);
        }
    }

    /**
     * Attaches optional board reference artwork (sidebar or annulus) around the playfield.
     * Sidebar bottom sides are deferred to a second call after rotateBoard (pass layoutBox).
     */
    protected placeBoardReference(
        polys?: Poly[][],
        opts?: { sides?: ReferenceSide[]; layoutBox?: SVGBox },
    ): SVGBox | undefined {
        if (this.json === undefined || this.rootSvg === undefined) {
            return undefined;
        }
        if (this.json.board === null || !("reference" in this.json.board) || this.json.board.reference === undefined) {
            return undefined;
        }
        const ref = this.json.board.reference;
        if (ref.layout !== "sidebar" && ref.layout !== "annulus") {
            throw new Error(`Unsupported board reference layout "${ref.layout as string}".`);
        }

        const allSides = resolveReferenceSides(ref);
        const requestedSides = opts?.sides ?? allSides;
        const sidesToPlace = requestedSides.filter((side) => allSides.includes(side));
        if (sidesToPlace.length === 0) {
            return undefined;
        }

        const rotateWithBoard = ref.rotateWithBoard ?? true;
        const opacity = ref.opacity ?? 1;

        let parent: Svg | SVGG;
        if (rotateWithBoard) {
            parent = this.rootSvg.findOne("#board") as SVGG;
        } else {
            parent = ensureTableau(this.rootSvg);
        }

        const isBottomPass = sidesToPlace.every((side) => side === "bottom");
        let refRoot = parent.findOne("#board-reference") as SVGG | null;
        if (!isBottomPass) {
            if (refRoot !== null) {
                refRoot.remove();
            }
            refRoot = parent.group().id("board-reference").attr("overflow", "visible");
            if (opacity !== 1) {
                refRoot.opacity(opacity);
            }
            if (rotateWithBoard) {
                refRoot.back();
            } else {
                const board = parent.findOne("#board") as SVGG | null;
                if (board !== null) {
                    refRoot.before(board);
                }
            }
        } else if (refRoot === null) {
            refRoot = parent.group().id("board-reference").attr("overflow", "visible");
            if (opacity !== 1) {
                refRoot.opacity(opacity);
            }
            if (rotateWithBoard) {
                refRoot.back();
            } else {
                const board = parent.findOne("#board") as SVGG | null;
                if (board !== null) {
                    refRoot.before(board);
                }
            }
        }

        if (ref.layout === "annulus") {
            const sourceId = referenceSourceString(ref);
            const metrics = computePlayfieldMetrics(this.rootSvg, this.cellsize, polys);
            const resolved = this.resolveReferenceArt(ref, "left", sourceId);
            const defSvg = this.ensureReferenceDef(resolved);
            this.applyReferenceStyles(defSvg, ref.styles);
            const placement = computeAnnulusPlacement(resolved.meta, metrics, ref, this.cellsize);
            refRoot!.use(defSvg).size(placement.width, placement.height).move(placement.x, placement.y);
            return undefined;
        }

        const playfieldMetrics = computePlayfieldMetrics(this.rootSvg, this.cellsize, polys);
        const board = this.rootSvg.findOne("#board") as SVGG;
        const bottomLayoutMetrics = isBottomPass && opts?.layoutBox !== undefined
            ? sidebarLayoutMetricsInParent(parent as SVGG, board, refRoot)
            : undefined;

        for (const side of sidesToPlace) {
            const sourceId = resolveSourceForSide(ref, side);
            const resolved = this.resolveReferenceArt(ref, side, sourceId);
            const defSvg = this.ensureReferenceDef(resolved);
            this.applyReferenceStyles(defSvg, ref.styles);
            const sideMetrics = side === "bottom" && bottomLayoutMetrics !== undefined
                ? bottomLayoutMetrics
                : playfieldMetrics;
            const placement = computeSidebarPlacement(
                resolved.meta,
                sideMetrics,
                ref,
                this.cellsize,
                side,
            );
            refRoot!.use(defSvg).size(placement.width, placement.height).move(placement.x, placement.y);
        }

        if (isBottomPass && opts?.layoutBox !== undefined) {
            return unionBoardReferenceBox(this.rootSvg, opts.layoutBox);
        }
        return undefined;
    }

    protected rotateBoard(opts?: {ignoreRotation?: boolean, ignoreLabels?: boolean}): SVGBox {
        let ignoreRotation = false;
        if (opts !== undefined && opts.ignoreRotation !== undefined) {
            ignoreRotation = opts.ignoreRotation;
        }
        let ignoreLabels = false;
        if (opts !== undefined && opts.ignoreLabels !== undefined) {
            ignoreLabels = opts.ignoreLabels;
        }
        if (this.rootSvg === undefined) {
            throw new Error("Cannot rotate unless SVG is initialized and a board is present.");
        }

        const tableau = this.rootSvg.findOne("#board-tableau") as SVGG|null;
        const board = this.rootSvg.findOne("#board") as SVGG|null;
        if (board === null) {
            throw new Error("Could not find the core board group to rotate.");
        }
        let rotation = this.getRotation();
        if (ignoreRotation) {
            rotation = 0;
        }

        const unionLayoutBox = (): SVGBox => {
            const boardBox = board.bbox();
            const ref = (tableau ?? board).findOne("#board-reference") as SVGG | null;
            if (ref === null) {
                return boardBox;
            }
            const refBox = ref.rbox(this.rootSvg);
            const x = Math.min(boardBox.x, refBox.x);
            const y = Math.min(boardBox.y, refBox.y);
            const x2 = Math.max(boardBox.x2, refBox.x2);
            const y2 = Math.max(boardBox.y2, refBox.y2);
            return new SVGBox(x, y, x2 - x, y2 - y);
        };

        const startingBox = unionLayoutBox();
        if (rotation === 0) {
            return startingBox;
        } else {
            const boardBox = board.bbox();
            rotate(board, rotation, boardBox.cx, boardBox.cy);
            // reorient all labels
            if (!ignoreLabels) {
                const labels = this.rootSvg.findOne("#labels") as SVGG|null;
                if (labels !== null) {
                    labels.find("text").each((e: SVGElement) => {
                        // const box = e.bbox();
                        const box = e.rbox(board);
                        rotate(e, rotation * -1, box.cx, box.cy);
                    });
                }
            }

            // reorientation of `vertical` glyphs has to happen in `loadLegend()`
            // because you can't otherwise access the bboxes of symbols

            // must be rbox relative to the root instead of bbox to pass the
            // correct coordinates to the different `areas`
            if (tableau !== null) {
                return tableau.rbox(this.rootSvg);
            }
            return board.rbox(this.rootSvg);
        }
    }

    // These functions let the base class build polyominoes
    protected buildPoly(svg: Svg, matrix: Polymatrix, {divided = false, tlmark = false} = {}): void {
        if (this.json === undefined) {
            throw new Error("Invalid JSON");
        }
        let baseColour = this.options.colourContext.strokes;
        const board = this.json.board;
        if (
            board !== null &&
            board !== undefined &&
            "strokeColour" in board &&
            board.strokeColour !== undefined
        ) {
            baseColour = this.resolveColour(board.strokeColour) as string;
        }

        const height = matrix.length;
        let width = 0;
        if (height > 0) {
            width = matrix[0].length;
        }
        for (let y = 0; y < matrix.length; y++) {
            for (let x = 0; x < matrix[y].length; x++) {
                const val = matrix[y][x];
                if (val === null || val === 0) {
                    continue;
                }
                const colour = this.resolveColour(val) as string;
                let borderColour = colour;
                if (divided) {
                    borderColour = baseColour;
                }
                // create rect
                svg.rect(this.cellsize, this.cellsize).stroke({color: borderColour, width: 1, linecap: "round", linejoin: "round"}).fill({color: colour}).move(x * this.cellsize, y * this.cellsize);

                // add borders as necessary
                // top
                if (y === 0 || matrix[y-1][x] === null || matrix[y-1][x] === 0) {
                    this.drawBorder(svg, x * this.cellsize, y * this.cellsize, (x+1) * this.cellsize, y * this.cellsize);
                }
                // bottom
                if (y === height - 1 || matrix[y+1][x] === null || matrix[y+1][x] === 0) {
                    this.drawBorder(svg, x * this.cellsize, (y+1) * this.cellsize, (x+1) * this.cellsize, (y+1) * this.cellsize);
                }
                // left
                if (x === 0 || matrix[y][x-1] === null || matrix[y][x-1] === 0) {
                    this.drawBorder(svg, x * this.cellsize, y * this.cellsize, x * this.cellsize, (y+1) * this.cellsize);
                }
                // right
                if (x === width - 1 || matrix[y][x+1] === null || matrix[y][x+1] === 0) {
                    this.drawBorder(svg, (x+1) * this.cellsize, y * this.cellsize, (x+1) * this.cellsize, (y+1) * this.cellsize);
                }
            }
        }

        // mark top-left cell if requested
        if (tlmark) {
            svg.circle(this.cellsize / 5).center(this.cellsize / 2, this.cellsize / 2).stroke({width: 1, color: this.options.colourContext.strokes}).fill(this.options.colourContext.strokes);
        }
    }

    protected drawBorder(svg: Svg, x1: number, y1: number, x2: number, y2: number): void {
        if (this.json === undefined) {
            throw new Error("No valid json found.");
        }
        let baseStroke = 1;
        let baseColour = this.options.colourContext.strokes;
        let baseOpacity = 1;
        const board = this.json.board;
        if (board !== null && board !== undefined) {
            if ( ("strokeWeight" in board) && (board.strokeWeight !== undefined) ) {
                baseStroke = board.strokeWeight;
            }
            if ( ("strokeColour" in board) && (board.strokeColour !== undefined) ) {
                baseColour = this.resolveColour(board.strokeColour) as string;
            }
            if ( ("strokeOpacity" in board) && (board.strokeOpacity !== undefined) ) {
                baseOpacity = board.strokeOpacity;
            }
        }
        svg.line(x1, y1, x2, y2)
           .stroke({color: baseColour, width: baseStroke, opacity: baseOpacity, linecap: "round", linejoin: "round"});
    }

    protected getStackedPoint (pts: GridPoints, col: number, row: number): IPoint|undefined {
        if ( (! ("json" in this)) || (this.json === undefined) || (this.json === null) || (! ("board" in this.json)) || (this.json.board === undefined) || (this.json.board === null) || (! ("style" in this.json.board)) || (this.json.board.style === undefined)) {
            throw new Error(`Cannot calculate the stacked point if the base object is not properly populated.`);
        }
        if (this.json.board.style !== "squares-stacked") {
            try {
                const [gx, gy] = this.interpolateFromGrid(pts, { row, col });
                return { x: gx, y: gy };
            } catch {
                return undefined;
            }
        }
        if ((! ("width" in this.json.board)) || (this.json.board.width === undefined) || (! ("height" in this.json.board)) || (this.json.board.height === undefined)) {
            throw new Error(`Cannot calculate the stacked point if the base object is not properly populated.`);
        }
        const result = calcPyramidOffset(this.json.board.width, this.json.board.height, col, row);
        if (result === undefined) {
            return undefined;
        }
        let point = pts[result.gridrow][result.gridcol];
        if (result.offset) {
            point = {x: point.x + (this.cellsize / 2), y: point.y + (this.cellsize / 2)}
        }
        return point;
    }

    protected getTriStackedPoint (pts: GridPoints, col: number, row: number, polys: Poly[][]): IPoint|undefined {
        if ( (! ("json" in this)) || (this.json === undefined) || (this.json === null) || (! ("board" in this.json)) || (this.json.board === undefined) || (this.json.board === null) || (! ("style" in this.json.board)) || (this.json.board.style === undefined)) {
            throw new Error(`Cannot calculate the stacked point if the base object is not properly populated.`);
        }
        if (this.json.board.style !== "triangles-stacked") {
            return pts[row][col];
        }
        if ((! ("width" in this.json.board)) || (this.json.board.width === undefined) || (! ("height" in this.json.board)) || (this.json.board.height === undefined)) {
            throw new Error(`Cannot calculate the stacked point if the base object is not properly populated.`);
        }
        const result = calcLazoOffset(this.json.board.width, this.json.board.height, col, row);
        if (result === undefined) {
            return undefined;
        }
        const anchor = polys[result.gridrow][result.gridcol] as IPolyPolygon;
        switch (result.layer % 3) {
            case 0:
                return pts[result.gridrow][result.gridcol];
            case 1:
                return anchor.points[2];
            case 2:
                return anchor.points[1];
            default:
                return undefined;
        }
    }

    /**
     * Resolves a colour or pattern value to an SVG fill: hex string, gradient, or pattern element.
     *
     * @remarks Colour functions degrade gracefully when a player slot is a pattern: `lighten` and
     * `flatten`-fg return the pattern unchanged; `flatten`-bg and `bestContrast`-bg use
     * `colourContext.background` as a stand-in; gradient stops use `colourContext.strokes`.
     *
     * @param val - the value in the JSON
     * @param def - the default value
     * @param opts - optional scale for pattern fills
     * @returns
     */
    public resolveFill(val: number | string | Gradient | Colourfuncs, def?: string, opts?: { scale?: number }): ResolvedFill {
        if (this.rootSvg === undefined || this.rootSvg === null) {
            throw new Error(`Cannot resolve colour values until the root SVG is initialized.`);
        }

        const patternScale = opts?.scale ?? 150;

        if (typeof val === "object") {
            if ("stops" in val) {
                const x1 = val.x1 !== undefined ? val.x1 : 0;
                const y1 = val.y1 !== undefined ? val.y1 : 0;
                const x2 = val.x2 !== undefined ? val.x2 : 1;
                const y2 = val.y2 !== undefined ? val.y2 : 0;
                const colour = this.rootSvg.defs().gradient("linear", add => {
                    for (const stop of (val as Gradient).stops) {
                        const stopFill = this.resolveFill(stop.colour, this.options.colourContext.strokes, opts);
                        add.stop({
                            offset: stop.offset,
                            color: this.coerceFillToHex(stopFill, this.options.colourContext.strokes),
                            opacity: stop.opacity !== undefined ? stop.opacity : 1,
                        });
                    }
                });
                colour.from(x1, y1).to(x2, y2);
                return colour;
            }
            if ("func" in val) {
                if (val.func === "flatten") {
                    const fgOp = this.resolveColourOperand(val.fg, def, opts);
                    if (fgOp.kind === "pattern") {
                        return fgOp.element;
                    }
                    const bgOp = this.resolveColourOperand(val.bg, this.options.colourContext.background, opts);
                    const bgHex = bgOp.kind === "pattern" ? this.options.colourContext.background : bgOp.value;
                    const fg = hex2rgb(fgOp.value);
                    const bg = hex2rgb(bgHex);
                    return rgb2hex(afterOpacity(fg, val.opacity, bg));
                }
                if (val.func === "lighten") {
                    const op = this.resolveColourOperand(val.colour, def, opts);
                    if (op.kind === "pattern") {
                        return op.element;
                    }
                    const base = hex2rgb(op.value);
                    return rgb2hex(lighten(base, val.ds, val.dl));
                }
                if (val.func === "bestContrast") {
                    const bgOp = this.resolveColourOperand(val.bg, def, opts);
                    const bgHex = bgOp.kind === "pattern" ? this.options.colourContext.background : bgOp.value;
                    const fgHexes: string[] = [];
                    for (const c of val.fg) {
                        const op = this.resolveColourOperand(c, def, opts);
                        if (op.kind === "colour") {
                            fgHexes.push(op.value);
                        }
                    }
                    if (fgHexes.length === 0) {
                        return this.options.colourContext.strokes;
                    }
                    return tinycolor.mostReadable(bgHex, fgHexes).toHexString();
                }
                if (val.func === "custom") {
                    let branch: number | string | Colourfuncs;
                    if (typeof val.palette === "number") {
                        branch = (this.options.coloursGlobal || this.options.useDefaults.includes(val.palette))
                            ? val.default
                            : val.palette;
                    } else if (typeof val.palette === "string" && val.palette.startsWith("_context_")) {
                        branch = this.options.contextGlobal ? val.default : val.palette;
                    } else if ("paletteType" in val && val.paletteType !== undefined) {
                        if (val.paletteType === "context") {
                            branch = this.options.contextGlobal ? val.default : val.palette;
                        } else {
                            branch = (this.options.coloursGlobal) ? val.default : val.palette;
                        }
                    } else {
                        throw new Error(`Could not resolve the custom function. If "palette" is neither a number nor a context, then "paletteType" is required.`);
                    }
                    return this.resolveFill(branch as number | string | Colourfuncs, def, opts);
                }
            }
        } else if (typeof val === "number") {
            if (val > this.options.playerPalette.length) {
                throw new Error("The list of colours provided is not long enough to support the number of players in this game.");
            }
            const slot = this.options.playerPalette[val - 1];
            if (slot === undefined) {
                throw new Error(`Unable to resolve colour:\n${JSON.stringify(val)}\nDefault: ${def}`);
            }
            if (slot.kind === "pattern") {
                return this.getScaledPattern(slot.value, patternScale);
            }
            return slot.value;
        } else if (isPatternName(val)) {
            return this.getScaledPattern(val, patternScale);
        } else {
            let colour: string = val;
            if (/^_context_/.test(colour)) {
                const [,,prop] = colour.split("_");
                if (prop in this.options.colourContext && this.options.colourContext[prop as "background"|"strokes"|"labels"|"annotations"|"fill"|"board"] !== undefined) {
                    colour = this.options.colourContext[prop as "background"|"strokes"|"labels"|"annotations"|"fill"|"board"] as string;
                } else if (prop === "board" && this.options.colourContext.board === undefined) {
                    colour = this.options.colourContext.background;
                }
            }
            return colour;
        }

        if (def === undefined) {
            throw new Error(`Unable to resolve colour:\n${JSON.stringify(val)}`);
        }
        return def;
    }

    protected resolveColourOperand(val: number | string | Gradient | Colourfuncs, def?: string, opts?: { scale?: number }): ColourOperand {
        const fill = this.resolveFill(val, def, opts);
        if (this.isPatternSVGElement(fill)) {
            return { kind: "pattern", element: fill };
        }
        return { kind: "colour", value: this.coerceFillToHex(fill, def) };
    }

    /**
     * This function takes a string, number, or gradient defintion and returns
     * either the corresponding hex colour or an SVGGradient.
     * Gradients cannot be recursive. Pattern fills fall back to `colourContext.strokes` for stroke use.
     * @param val - the value in the JSON
     * @param def - the default value
     * @returns
     */
    public resolveColour(val: number|string|Gradient|Colourfuncs, def?: string): string|SVGGradient {
        const fill = this.resolveFill(val, def);
        if (this.isPatternSVGElement(fill)) {
            return this.options.colourContext.strokes;
        }
        return fill as string | SVGGradient;
    }
}
